import React, { useState, useMemo, createContext, useContext } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Home, Server, Map, Database, BarChart3, Bell, HelpCircle, Search, ChevronRight, Activity, Wifi, Clock, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, Settings, User, Filter, Download, ArrowUpRight, ArrowDownRight, Zap, Globe, Signal, Router, Menu, X, ExternalLink, Play, Wrench, MessageSquare, TrendingUp, TrendingDown, MapPin, Calendar, FileText, CreditCard, DollarSign, Receipt, Building2, Printer, Eye, ChevronLeft, Layout, Palette, Users, Shield, Link, Copy, Trash2, Edit3, Plus, Power, Lock, Unlock, Mail, Phone, Image, Type, Hash, ToggleLeft, ToggleRight, RefreshCw, Share2, Layers, UserPlus, UserCheck, UserX, Key, Briefcase, Network, FolderTree, MoreVertical, Send, AlertCircle, Info, History, Target, CircleDot, Rocket, PanelLeft, ShieldCheck, UserCog, Sliders, Grid, List, Building, GitBranch, Landmark, Globe2, KeyRound, ShieldQuestion, LogIn, UserMinus, Fingerprint, Ship, Anchor } from 'lucide-react';

// ============================================================================
// MOCK DATA
// ============================================================================

const mockUsers = [
  { id: 'u1', name: 'Sarah Chen', email: 'sarah@acmecorp.com', role: 'LIBERTY_CUSTOMER', avatarUrl: null, team: 'Network Ops Team' },
  { id: 'u2', name: 'Marcus Johnson', email: 'marcus@liberty.net', role: 'LIBERTY_ADMIN', avatarUrl: null, team: 'NOC' },
  { id: 'u3', name: 'Emily Rodriguez', email: 'emily@techstart.io', role: 'LIBERTY_CUSTOMER', avatarUrl: null, team: 'IT Department' },
];

const mockServices = [
  { id: 's1', name: 'Branch SD-WAN + Starlink', ownerUserId: 'u1', ownerTeamName: 'Network Ops Team', status: 'active', type: 'SD-WAN Bundle', dataPoolSizeGb: 12000, dataUsedGb: 4200, antennas: ['a1', 'a2', 'a3', 'a4'], primarySite: 'Denver HQ', slaTarget: 99.5, slaActual: 99.8 },
  { id: 's2', name: 'Remote Site Backup', ownerUserId: 'u1', ownerTeamName: 'Network Ops Team', status: 'active', type: 'Backup Link', dataPoolSizeGb: 5000, dataUsedGb: 1800, antennas: ['a5', 'a6'], primarySite: 'Austin Branch', slaTarget: 99.0, slaActual: 98.5 },
  { id: 's3', name: 'Maritime Fleet Connect', ownerUserId: 'u3', ownerTeamName: 'IT Department', status: 'active', type: 'Maritime', dataPoolSizeGb: 8000, dataUsedGb: 6200, antennas: ['a7', 'a8', 'a9'], primarySite: 'Port Houston', slaTarget: 98.0, slaActual: 97.2 },
  { id: 's4', name: 'Construction Site Link', ownerUserId: 'u3', ownerTeamName: 'IT Department', status: 'attention', type: 'Temporary', dataPoolSizeGb: 2000, dataUsedGb: 1950, antennas: ['a10', 'a11'], primarySite: 'Phoenix Site 12', slaTarget: 95.0, slaActual: 94.8 },
];

const mockAntennas = [
  { id: 'a1', name: 'DEN-HQ-01', starlinkId: 'UT-2024-001234', serviceId: 's1', siteName: 'Denver HQ', status: 'healthy', signalQuality: 94, downMbps: 185, upMbps: 28, lastCheckIn: '2 min ago', latitude: 39.7392, longitude: -104.9903, firmwareVersion: 'v2.4.1' },
  { id: 'a2', name: 'DEN-HQ-02', starlinkId: 'UT-2024-001235', serviceId: 's1', siteName: 'Denver HQ', status: 'healthy', signalQuality: 91, downMbps: 172, upMbps: 25, lastCheckIn: '1 min ago', latitude: 39.7394, longitude: -104.9905, firmwareVersion: 'v2.4.1' },
  { id: 'a3', name: 'DEN-BACKUP', starlinkId: 'UT-2024-001236', serviceId: 's1', siteName: 'Denver Warehouse', status: 'attention', signalQuality: 72, downMbps: 98, upMbps: 15, lastCheckIn: '5 min ago', latitude: 39.7512, longitude: -104.9823, firmwareVersion: 'v2.4.0' },
  { id: 'a4', name: 'BOULDER-01', starlinkId: 'UT-2024-001237', serviceId: 's1', siteName: 'Boulder Office', status: 'healthy', signalQuality: 88, downMbps: 165, upMbps: 22, lastCheckIn: '3 min ago', latitude: 40.0150, longitude: -105.2705, firmwareVersion: 'v2.4.1' },
  { id: 'a5', name: 'AUS-BR-01', starlinkId: 'UT-2024-002341', serviceId: 's2', siteName: 'Austin Branch', status: 'healthy', signalQuality: 96, downMbps: 195, upMbps: 32, lastCheckIn: '1 min ago', latitude: 30.2672, longitude: -97.7431, firmwareVersion: 'v2.4.1' },
  { id: 'a6', name: 'AUS-BR-02', starlinkId: 'UT-2024-002342', serviceId: 's2', siteName: 'Austin Branch', status: 'critical', signalQuality: 45, downMbps: 42, upMbps: 8, lastCheckIn: '15 min ago', latitude: 30.2674, longitude: -97.7433, firmwareVersion: 'v2.3.8' },
  { id: 'a7', name: 'VESSEL-AURORA', starlinkId: 'UT-2024-003451', serviceId: 's3', siteName: 'MV Aurora', status: 'healthy', signalQuality: 82, downMbps: 145, upMbps: 18, lastCheckIn: '4 min ago', latitude: 29.7604, longitude: -95.3698, firmwareVersion: 'v2.4.1' },
  { id: 'a8', name: 'VESSEL-NEPTUNE', starlinkId: 'UT-2024-003452', serviceId: 's3', siteName: 'MV Neptune', status: 'attention', signalQuality: 68, downMbps: 95, upMbps: 12, lastCheckIn: '8 min ago', latitude: 28.9501, longitude: -95.3599, firmwareVersion: 'v2.4.0' },
  { id: 'a9', name: 'PORT-OFFICE', starlinkId: 'UT-2024-003453', serviceId: 's3', siteName: 'Port Houston', status: 'healthy', signalQuality: 97, downMbps: 210, upMbps: 35, lastCheckIn: '1 min ago', latitude: 29.7355, longitude: -95.0205, firmwareVersion: 'v2.4.1' },
  { id: 'a10', name: 'PHX-SITE12-01', starlinkId: 'UT-2024-004561', serviceId: 's4', siteName: 'Phoenix Site 12', status: 'attention', signalQuality: 65, downMbps: 88, upMbps: 14, lastCheckIn: '6 min ago', latitude: 33.4484, longitude: -112.0740, firmwareVersion: 'v2.4.1' },
  { id: 'a11', name: 'PHX-SITE12-02', starlinkId: 'UT-2024-004562', serviceId: 's4', siteName: 'Phoenix Site 12', status: 'critical', signalQuality: 38, downMbps: 25, upMbps: 5, lastCheckIn: '22 min ago', latitude: 33.4486, longitude: -112.0742, firmwareVersion: 'v2.3.5' },
];

const mockUsageSamples = Array.from({ length: 30 }, (_, i) => ({
  timestamp: `Day ${i + 1}`,
  s1: Math.floor(100 + Math.random() * 80),
  s2: Math.floor(40 + Math.random() * 30),
  s3: Math.floor(150 + Math.random() * 100),
  s4: Math.floor(50 + Math.random() * 40),
}));

const mockAlerts = [
  { id: 'al1', serviceId: 's1', antennaId: 'a3', severity: 'warning', category: 'Signal Degradation', title: 'Elevated obstruction detected', description: 'Antenna DEN-BACKUP experiencing 28% obstruction. Consider repositioning.', createdAt: '2 hours ago', status: 'active' },
  { id: 'al2', serviceId: 's2', antennaId: 'a6', severity: 'critical', category: 'Connectivity Loss', title: 'Terminal offline', description: 'AUS-BR-02 has not checked in for 15 minutes. Last known signal quality: 45%.', createdAt: '15 min ago', status: 'active' },
  { id: 'al3', serviceId: 's3', antennaId: 'a8', severity: 'warning', category: 'Performance', title: 'Below SLA threshold', description: 'VESSEL-NEPTUNE throughput below expected levels. Current: 95 Mbps, Expected: 150 Mbps.', createdAt: '1 hour ago', status: 'active' },
  { id: 'al4', serviceId: 's4', antennaId: 'a11', severity: 'critical', category: 'Hardware', title: 'Firmware update required', description: 'PHX-SITE12-02 running outdated firmware v2.3.5. Critical security update available.', createdAt: '3 hours ago', status: 'active' },
  { id: 'al5', serviceId: 's4', antennaId: 'a10', severity: 'info', category: 'Data Usage', title: 'Approaching data cap', description: 'Construction Site Link service at 97.5% of monthly data pool allocation.', createdAt: '30 min ago', status: 'active' },
  { id: 'al6', serviceId: 's1', antennaId: null, severity: 'info', category: 'Billing', title: 'Invoice generated', description: 'Monthly invoice for Branch SD-WAN + Starlink service is ready for review.', createdAt: '1 day ago', status: 'resolved' },
];

const mockPerformanceData = Array.from({ length: 24 }, (_, i) => ({
  time: `${i}:00`,
  downlink: 150 + Math.random() * 60,
  uplink: 20 + Math.random() * 15,
  latency: 25 + Math.random() * 20,
  obstruction: Math.random() * 5,
}));

// Billing Mock Data
const mockAccounts = [
  { id: 'acc1', name: 'ACME Corporation', type: 'Enterprise', contactEmail: 'billing@acmecorp.com', billingAddress: '123 Tech Park, Denver, CO 80202', paymentMethod: 'NET-30', creditLimit: 50000, currentBalance: 12450.00 },
  { id: 'acc2', name: 'TechStart Industries', type: 'Business', contactEmail: 'finance@techstart.io', billingAddress: '456 Innovation Blvd, Austin, TX 78701', paymentMethod: 'Credit Card', creditLimit: 25000, currentBalance: 8920.00 },
];

const mockInvoices = [
  { id: 'inv-2024-001', accountId: 'acc1', invoiceNumber: 'INV-2024-001', date: '2024-12-01', dueDate: '2024-12-31', status: 'paid', subtotal: 8450.00, tax: 676.00, total: 9126.00, paidDate: '2024-12-15' },
  { id: 'inv-2024-002', accountId: 'acc1', invoiceNumber: 'INV-2024-002', date: '2024-11-01', dueDate: '2024-11-30', status: 'paid', subtotal: 8200.00, tax: 656.00, total: 8856.00, paidDate: '2024-11-20' },
  { id: 'inv-2024-003', accountId: 'acc1', invoiceNumber: 'INV-2024-003', date: '2024-10-01', dueDate: '2024-10-31', status: 'paid', subtotal: 7950.00, tax: 636.00, total: 8586.00, paidDate: '2024-10-25' },
  { id: 'inv-2024-004', accountId: 'acc2', invoiceNumber: 'INV-2024-004', date: '2024-12-01', dueDate: '2024-12-31', status: 'pending', subtotal: 4250.00, tax: 340.00, total: 4590.00, paidDate: null },
  { id: 'inv-2024-005', accountId: 'acc2', invoiceNumber: 'INV-2024-005', date: '2024-11-01', dueDate: '2024-11-30', status: 'overdue', subtotal: 4100.00, tax: 328.00, total: 4428.00, paidDate: null },
  { id: 'inv-2024-006', accountId: 'acc1', invoiceNumber: 'INV-2024-006', date: '2024-12-15', dueDate: '2025-01-14', status: 'pending', subtotal: 3200.00, tax: 256.00, total: 3456.00, paidDate: null },
];

const mockTransactions = [
  { id: 'txn-001', accountId: 'acc1', date: '2024-12-15', type: 'payment', description: 'Payment received - INV-2024-001', amount: -9126.00, balance: 3456.00, reference: 'CHK-78234', method: 'Bank Transfer' },
  { id: 'txn-002', accountId: 'acc1', date: '2024-12-15', type: 'invoice', description: 'Invoice INV-2024-006 generated', amount: 3456.00, balance: 12582.00, reference: 'INV-2024-006', method: null },
  { id: 'txn-003', accountId: 'acc1', date: '2024-12-01', type: 'invoice', description: 'Invoice INV-2024-001 generated', amount: 9126.00, balance: 9126.00, reference: 'INV-2024-001', method: null },
  { id: 'txn-004', accountId: 'acc1', date: '2024-11-20', type: 'payment', description: 'Payment received - INV-2024-002', amount: -8856.00, balance: 0.00, reference: 'CHK-77156', method: 'Bank Transfer' },
  { id: 'txn-005', accountId: 'acc2', date: '2024-12-01', type: 'invoice', description: 'Invoice INV-2024-004 generated', amount: 4590.00, balance: 9018.00, reference: 'INV-2024-004', method: null },
  { id: 'txn-006', accountId: 'acc2', date: '2024-11-15', type: 'credit', description: 'Service credit - SLA compensation', amount: -150.00, balance: 4428.00, reference: 'CR-2024-012', method: null },
  { id: 'txn-007', accountId: 'acc2', date: '2024-11-01', type: 'invoice', description: 'Invoice INV-2024-005 generated', amount: 4428.00, balance: 4578.00, reference: 'INV-2024-005', method: null },
];

const mockBillingByService = [
  { serviceId: 's1', serviceName: 'Branch SD-WAN + Starlink', accountId: 'acc1', monthlyBase: 2450.00, dataCharges: 1200.00, overageCharges: 0, supportCharges: 150.00, total: 3800.00 },
  { serviceId: 's2', serviceName: 'Remote Site Backup', accountId: 'acc1', monthlyBase: 1200.00, dataCharges: 600.00, overageCharges: 85.00, supportCharges: 0, total: 1885.00 },
  { serviceId: 's3', serviceName: 'Maritime Fleet Connect', accountId: 'acc2', monthlyBase: 2800.00, dataCharges: 950.00, overageCharges: 320.00, supportCharges: 180.00, total: 4250.00 },
  { serviceId: 's4', serviceName: 'Construction Site Link', accountId: 'acc2', monthlyBase: 800.00, dataCharges: 400.00, overageCharges: 450.00, supportCharges: 0, total: 1650.00 },
];

const mockBillingByLocation = [
  { location: 'Denver HQ', serviceId: 's1', antennaCount: 2, monthlyCharges: 1800.00, dataUsageGb: 1850, dataCharges: 550.00 },
  { location: 'Denver Warehouse', serviceId: 's1', antennaCount: 1, monthlyCharges: 650.00, dataUsageGb: 420, dataCharges: 125.00 },
  { location: 'Boulder Office', serviceId: 's1', antennaCount: 1, monthlyCharges: 650.00, dataUsageGb: 380, dataCharges: 115.00 },
  { location: 'Austin Branch', serviceId: 's2', antennaCount: 2, monthlyCharges: 1200.00, dataUsageGb: 1800, dataCharges: 685.00 },
  { location: 'MV Aurora', serviceId: 's3', antennaCount: 1, monthlyCharges: 950.00, dataUsageGb: 2100, dataCharges: 420.00 },
  { location: 'MV Neptune', serviceId: 's3', antennaCount: 1, monthlyCharges: 950.00, dataUsageGb: 1950, dataCharges: 390.00 },
  { location: 'Port Houston', serviceId: 's3', antennaCount: 1, monthlyCharges: 900.00, dataUsageGb: 2150, dataCharges: 140.00 },
  { location: 'Phoenix Site 12', serviceId: 's4', antennaCount: 2, monthlyCharges: 800.00, dataUsageGb: 1950, dataCharges: 850.00 },
];

const mockBillingHistory = Array.from({ length: 12 }, (_, i) => {
  const month = new Date(2024, 11 - i, 1);
  return {
    month: month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    services: 7500 + Math.random() * 1500,
    data: 2000 + Math.random() * 800,
    overage: Math.random() > 0.6 ? Math.random() * 500 : 0,
    support: 150 + Math.random() * 100,
  };
}).reverse();

// ============================================================================
// MICROSITE / WHITE-LABEL MOCK DATA
// ============================================================================

const mockMicrosites = [
  {
    id: 'site-001',
    name: 'ACME Corporation Portal',
    slug: 'acme-corp',
    domain: 'starlink.acmecorp.com',
    customDomain: true,
    accountId: 'acc1',
    status: 'active',
    createdAt: '2024-01-15',
    lastUpdated: '2024-12-01',
    branding: {
      logoUrl: '/logos/acme-logo.png',
      logoText: 'ACME Starlink',
      primaryColor: '#0066CC',
      secondaryColor: '#004499',
      accentColor: '#00AAFF',
      headerBg: '#0066CC',
      sidebarBg: '#FFFFFF',
      fontFamily: 'Inter',
      favicon: '/favicons/acme.ico',
      loginBgImage: '/images/acme-login-bg.jpg',
      supportEmail: 'starlink-support@acmecorp.com',
      supportPhone: '+1 (800) 555-ACME',
      footerText: '© 2024 ACME Corporation. Powered by Liberty Connectivity.',
      customCss: '',
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: false,
      whiteLabel: true,
      customReports: true,
    },
    limits: {
      maxUsers: 50,
      maxServices: 20,
      maxAntennas: 100,
      dataRetentionDays: 365,
    },
    analytics: {
      totalLogins: 1247,
      activeUsers: 18,
      avgSessionMinutes: 12,
      lastMonthPageViews: 8542,
    },
  },
  {
    id: 'site-002',
    name: 'TechStart Portal',
    slug: 'techstart',
    domain: 'portal.techstart.io',
    customDomain: true,
    accountId: 'acc2',
    status: 'active',
    createdAt: '2024-03-20',
    lastUpdated: '2024-11-28',
    branding: {
      logoUrl: '/logos/techstart-logo.png',
      logoText: 'TechStart Connect',
      primaryColor: '#7C3AED',
      secondaryColor: '#5B21B6',
      accentColor: '#A78BFA',
      headerBg: '#7C3AED',
      sidebarBg: '#FAFAFA',
      fontFamily: 'Poppins',
      favicon: '/favicons/techstart.ico',
      loginBgImage: null,
      supportEmail: 'connectivity@techstart.io',
      supportPhone: '+1 (888) TECHSTART',
      footerText: '© 2024 TechStart Industries',
      customCss: '',
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: true,
      whiteLabel: true,
      customReports: false,
    },
    limits: {
      maxUsers: 25,
      maxServices: 10,
      maxAntennas: 50,
      dataRetentionDays: 180,
    },
    analytics: {
      totalLogins: 523,
      activeUsers: 8,
      avgSessionMinutes: 9,
      lastMonthPageViews: 3128,
    },
  },
  {
    id: 'site-003',
    name: 'Maritime Solutions Hub',
    slug: 'maritime-hub',
    domain: 'liberty-demo.maritime-solutions.net',
    customDomain: false,
    accountId: null,
    status: 'pending',
    createdAt: '2024-11-01',
    lastUpdated: '2024-11-15',
    branding: {
      logoUrl: null,
      logoText: 'Maritime Solutions',
      primaryColor: '#0891B2',
      secondaryColor: '#0E7490',
      accentColor: '#22D3EE',
      headerBg: '#0891B2',
      sidebarBg: '#FFFFFF',
      fontFamily: 'Inter',
      favicon: null,
      loginBgImage: null,
      supportEmail: 'support@maritime-solutions.net',
      supportPhone: null,
      footerText: 'Maritime Solutions - Satellite Connectivity',
      customCss: '',
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: false,
      dataPools: true,
      apiAccess: false,
      whiteLabel: false,
      customReports: false,
    },
    limits: {
      maxUsers: 10,
      maxServices: 5,
      maxAntennas: 25,
      dataRetentionDays: 90,
    },
    analytics: {
      totalLogins: 0,
      activeUsers: 0,
      avgSessionMinutes: 0,
      lastMonthPageViews: 0,
    },
  },
  {
    id: 'site-004',
    name: 'Global Energy Connect',
    slug: 'global-energy',
    domain: null,
    customDomain: false,
    accountId: null,
    status: 'suspended',
    createdAt: '2024-06-10',
    lastUpdated: '2024-10-05',
    branding: {
      logoUrl: '/logos/global-energy.png',
      logoText: 'Global Energy',
      primaryColor: '#DC2626',
      secondaryColor: '#B91C1C',
      accentColor: '#F87171',
      headerBg: '#DC2626',
      sidebarBg: '#FFFFFF',
      fontFamily: 'Roboto',
      favicon: null,
      loginBgImage: null,
      supportEmail: 'it@globalenergy.com',
      supportPhone: '+1 (555) 123-4567',
      footerText: '© Global Energy Corporation',
      customCss: '',
    },
    features: {
      billing: false,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: false,
      apiAccess: false,
      whiteLabel: true,
      customReports: false,
    },
    limits: {
      maxUsers: 15,
      maxServices: 8,
      maxAntennas: 40,
      dataRetentionDays: 90,
    },
    analytics: {
      totalLogins: 892,
      activeUsers: 0,
      avgSessionMinutes: 8,
      lastMonthPageViews: 0,
    },
  },
];

const mockSiteUsers = [
  // ACME Corporation Users
  { id: 'su-001', siteId: 'site-001', name: 'Sarah Chen', email: 'sarah.chen@acmecorp.com', role: 'site_admin', status: 'active', lastLogin: '2024-12-09 14:32', createdAt: '2024-01-15', mfaEnabled: true, department: 'IT Operations' },
  { id: 'su-002', siteId: 'site-001', name: 'Robert Martinez', email: 'r.martinez@acmecorp.com', role: 'site_admin', status: 'active', lastLogin: '2024-12-08 09:15', createdAt: '2024-01-20', mfaEnabled: true, department: 'Network Engineering' },
  { id: 'su-003', siteId: 'site-001', name: 'Jennifer Walsh', email: 'j.walsh@acmecorp.com', role: 'billing_admin', status: 'active', lastLogin: '2024-12-07 16:45', createdAt: '2024-02-05', mfaEnabled: false, department: 'Finance' },
  { id: 'su-004', siteId: 'site-001', name: 'Michael Thompson', email: 'm.thompson@acmecorp.com', role: 'network_viewer', status: 'active', lastLogin: '2024-12-09 11:20', createdAt: '2024-03-12', mfaEnabled: false, department: 'Field Operations' },
  { id: 'su-005', siteId: 'site-001', name: 'Amanda Foster', email: 'a.foster@acmecorp.com', role: 'network_viewer', status: 'active', lastLogin: '2024-12-05 08:30', createdAt: '2024-04-18', mfaEnabled: false, department: 'Field Operations' },
  { id: 'su-006', siteId: 'site-001', name: 'David Park', email: 'd.park@acmecorp.com', role: 'support_agent', status: 'active', lastLogin: '2024-12-09 15:00', createdAt: '2024-05-22', mfaEnabled: true, department: 'Customer Support' },
  { id: 'su-007', siteId: 'site-001', name: 'Lisa Brown', email: 'l.brown@acmecorp.com', role: 'readonly', status: 'inactive', lastLogin: '2024-10-15 12:00', createdAt: '2024-06-10', mfaEnabled: false, department: 'Management' },
  
  // TechStart Users
  { id: 'su-008', siteId: 'site-002', name: 'Emily Rodriguez', email: 'emily@techstart.io', role: 'site_admin', status: 'active', lastLogin: '2024-12-09 10:45', createdAt: '2024-03-20', mfaEnabled: true, department: 'IT' },
  { id: 'su-009', siteId: 'site-002', name: 'Kevin Liu', email: 'kevin.liu@techstart.io', role: 'network_manager', status: 'active', lastLogin: '2024-12-08 14:20', createdAt: '2024-04-01', mfaEnabled: true, department: 'Engineering' },
  { id: 'su-010', siteId: 'site-002', name: 'Rachel Green', email: 'r.green@techstart.io', role: 'billing_admin', status: 'active', lastLogin: '2024-12-06 11:30', createdAt: '2024-04-15', mfaEnabled: false, department: 'Finance' },
  { id: 'su-011', siteId: 'site-002', name: 'Chris Anderson', email: 'c.anderson@techstart.io', role: 'readonly', status: 'pending', lastLogin: null, createdAt: '2024-12-01', mfaEnabled: false, department: 'Operations' },
];

const mockSiteRoles = [
  { id: 'site_admin', name: 'Site Administrator', description: 'Full access to manage the portal, users, and all settings', permissions: ['all'], color: 'rose' },
  { id: 'billing_admin', name: 'Billing Administrator', description: 'Manage billing, invoices, and payment settings', permissions: ['billing.view', 'billing.edit', 'invoices.view', 'invoices.download', 'reports.billing'], color: 'amber' },
  { id: 'network_manager', name: 'Network Manager', description: 'Manage services, antennas, and network configuration', permissions: ['services.view', 'services.edit', 'antennas.view', 'antennas.edit', 'alerts.view', 'alerts.acknowledge'], color: 'indigo' },
  { id: 'network_viewer', name: 'Network Viewer', description: 'View network status, services, and performance data', permissions: ['services.view', 'antennas.view', 'alerts.view', 'datapools.view'], color: 'teal' },
  { id: 'support_agent', name: 'Support Agent', description: 'Handle support tickets and view service status', permissions: ['support.view', 'support.manage', 'services.view', 'alerts.view'], color: 'sky' },
  { id: 'readonly', name: 'Read-Only Viewer', description: 'View-only access to dashboards and reports', permissions: ['dashboard.view', 'reports.view'], color: 'slate' },
];

const mockSiteActivityLog = [
  { id: 'log-001', siteId: 'site-001', userId: 'su-001', action: 'user.login', details: 'Logged in from 192.168.1.100', timestamp: '2024-12-09 14:32:15', ipAddress: '192.168.1.100' },
  { id: 'log-002', siteId: 'site-001', userId: 'su-001', action: 'branding.update', details: 'Updated primary color from #0055BB to #0066CC', timestamp: '2024-12-09 14:45:30', ipAddress: '192.168.1.100' },
  { id: 'log-003', siteId: 'site-001', userId: 'su-002', action: 'user.create', details: 'Created new user: d.park@acmecorp.com', timestamp: '2024-12-08 10:20:00', ipAddress: '10.0.0.55' },
  { id: 'log-004', siteId: 'site-001', userId: 'su-003', action: 'invoice.download', details: 'Downloaded invoice INV-2024-001', timestamp: '2024-12-07 16:50:22', ipAddress: '172.16.0.10' },
  { id: 'log-005', siteId: 'site-001', userId: 'su-001', action: 'feature.toggle', details: 'Enabled API Access feature', timestamp: '2024-12-05 09:15:00', ipAddress: '192.168.1.100' },
  { id: 'log-006', siteId: 'site-002', userId: 'su-008', action: 'user.login', details: 'Logged in from 10.10.10.25', timestamp: '2024-12-09 10:45:00', ipAddress: '10.10.10.25' },
  { id: 'log-007', siteId: 'site-002', userId: 'su-009', action: 'service.view', details: 'Viewed Maritime Fleet Connect service details', timestamp: '2024-12-08 14:25:00', ipAddress: '10.10.10.30' },
  { id: 'log-008', siteId: 'site-001', userId: 'su-006', action: 'support.ticket', details: 'Created support ticket #TKT-2024-0892', timestamp: '2024-12-09 15:10:00', ipAddress: '192.168.1.150' },
];

const micrositeStats = {
  totalSites: 4,
  activeSites: 2,
  pendingSites: 1,
  suspendedSites: 1,
  totalUsers: 11,
  activeUsersToday: 6,
  totalPageViews: 11670,
  avgSessionTime: 10,
};

// ============================================================================
// DRUPAL-STYLE MULTI-SITE PLATFORM DATA
// ============================================================================

const platformAdmins = [
  { id: 'pa-001', name: 'Marcus Johnson', email: 'marcus.johnson@liberty.net', role: 'platform_admin', status: 'active', lastLogin: '2024-12-09 14:32', sitesManaged: 'all', permissions: ['sites.create', 'sites.delete', 'sites.configure', 'users.all', 'branding.all', 'billing.all'] },
  { id: 'pa-002', name: 'Jennifer Williams', email: 'j.williams@liberty.net', role: 'platform_admin', status: 'active', lastLogin: '2024-12-09 11:20', sitesManaged: 'all', permissions: ['sites.create', 'sites.delete', 'sites.configure', 'users.all', 'branding.all', 'billing.all'] },
];

const siteTemplates = [
  { id: 'tpl-001', name: 'Enterprise Standard', description: 'Full-featured portal for enterprise customers with all modules enabled', features: ['billing', 'fleetMap', 'alerts', 'support', 'dataPools', 'apiAccess', 'customReports'], defaultLimits: { maxUsers: 50, maxServices: 20, maxAntennas: 100, dataRetentionDays: 365 }, thumbnail: 'enterprise' },
  { id: 'tpl-002', name: 'SMB Essentials', description: 'Streamlined portal for small-medium businesses with core features', features: ['billing', 'fleetMap', 'alerts', 'support', 'dataPools'], defaultLimits: { maxUsers: 15, maxServices: 5, maxAntennas: 25, dataRetentionDays: 180 }, thumbnail: 'smb' },
  { id: 'tpl-003', name: 'Maritime Operations', description: 'Specialized for fleet and vessel management with real-time tracking', features: ['fleetMap', 'alerts', 'support', 'dataPools', 'apiAccess'], defaultLimits: { maxUsers: 30, maxServices: 15, maxAntennas: 75, dataRetentionDays: 365 }, thumbnail: 'maritime' },
  { id: 'tpl-004', name: 'Basic View-Only', description: 'Minimal portal with view-only access for monitoring purposes', features: ['fleetMap', 'alerts'], defaultLimits: { maxUsers: 5, maxServices: 3, maxAntennas: 10, dataRetentionDays: 90 }, thumbnail: 'basic' },
];

const acmeSiteAdminData = {
  siteId: 'site-001',
  siteName: 'ACME Corporation Portal',
  siteAdmins: ['su-001', 'su-002'],
  pendingInvites: [
    { id: 'inv-001', email: 'new.user@acmecorp.com', role: 'network_viewer', invitedBy: 'Sarah Chen', invitedAt: '2024-12-08', expiresAt: '2024-12-15', status: 'pending' },
    { id: 'inv-002', email: 'contractor@partner.com', role: 'readonly', invitedBy: 'Robert Martinez', invitedAt: '2024-12-09', expiresAt: '2024-12-16', status: 'pending' },
  ],
  recentActivity: [
    { id: 'act-001', user: 'Sarah Chen', action: 'Updated branding colors', timestamp: '2024-12-09 14:45', type: 'branding' },
    { id: 'act-002', user: 'Robert Martinez', action: 'Added user David Park', timestamp: '2024-12-08 10:20', type: 'user' },
    { id: 'act-003', user: 'Jennifer Walsh', action: 'Downloaded invoice INV-2024-001', timestamp: '2024-12-07 16:50', type: 'billing' },
    { id: 'act-004', user: 'Sarah Chen', action: 'Enabled API Access feature', timestamp: '2024-12-05 09:15', type: 'settings' },
    { id: 'act-005', user: 'David Park', action: 'Created support ticket #TKT-2024-0892', timestamp: '2024-12-09 15:10', type: 'support' },
  ],
  usersByRole: { site_admin: 2, billing_admin: 1, network_manager: 0, network_viewer: 2, support_agent: 1, readonly: 1 },
  usageQuotas: { users: { used: 7, limit: 50 }, services: { used: 2, limit: 20 }, antennas: { used: 6, limit: 100 }, storage: { used: 45, limit: 100 } },
};

const sitePermissionCategories = [
  { category: 'User Management', permissions: [
    { id: 'users.view', name: 'View Users', description: 'View user list and profiles' },
    { id: 'users.create', name: 'Create Users', description: 'Invite new users to the portal' },
    { id: 'users.edit', name: 'Edit Users', description: 'Modify user roles and settings' },
    { id: 'users.delete', name: 'Delete Users', description: 'Remove users from the portal' },
  ]},
  { category: 'Services & Network', permissions: [
    { id: 'services.view', name: 'View Services', description: 'View service configurations and status' },
    { id: 'services.edit', name: 'Edit Services', description: 'Modify service settings' },
    { id: 'antennas.view', name: 'View Antennas', description: 'View antenna status and metrics' },
    { id: 'antennas.edit', name: 'Edit Antennas', description: 'Configure antenna settings' },
  ]},
  { category: 'Billing & Finance', permissions: [
    { id: 'billing.view', name: 'View Billing', description: 'View invoices and billing history' },
    { id: 'billing.edit', name: 'Edit Billing', description: 'Modify billing settings' },
    { id: 'invoices.download', name: 'Download Invoices', description: 'Download invoice PDFs' },
  ]},
  { category: 'Alerts & Support', permissions: [
    { id: 'alerts.view', name: 'View Alerts', description: 'View system alerts' },
    { id: 'alerts.acknowledge', name: 'Acknowledge Alerts', description: 'Mark alerts as acknowledged' },
    { id: 'support.manage', name: 'Manage Tickets', description: 'Create and manage support tickets' },
  ]},
];

// ============================================================================
// USER MANAGEMENT MODULE DATA
// ============================================================================

const userManagementRoles = [
  { id: 'customer_admin', name: 'Customer Admin', description: 'Full administrative access to customer portal, users, and billing', color: 'rose', permissions: ['users.*', 'billing.*', 'services.*', 'reports.*'] },
  { id: 'sales_manager', name: 'Sales Manager', description: 'View accounts, usage reports, and billing summaries', color: 'amber', permissions: ['accounts.view', 'billing.view', 'reports.sales'] },
  { id: 'network_manager', name: 'Network Manager', description: 'Manage network configurations, services, and antennas', color: 'indigo', permissions: ['services.*', 'antennas.*', 'alerts.*'] },
  { id: 'ops_engineer', name: 'Ops Engineer', description: 'Monitor and troubleshoot network operations', color: 'teal', permissions: ['services.view', 'antennas.*', 'alerts.*', 'diagnostics.*'] },
  { id: 'billing_admin', name: 'Billing Admin', description: 'Manage invoices, payments, and billing settings', color: 'emerald', permissions: ['billing.*', 'invoices.*', 'payments.*'] },
  { id: 'service_manager', name: 'Service Manager', description: 'Oversee service delivery and customer satisfaction', color: 'sky', permissions: ['services.*', 'support.*', 'reports.service'] },
  { id: 'readonly_viewer', name: 'Read-only Viewer', description: 'View-only access to dashboards and reports', color: 'slate', permissions: ['*.view'] },
  { id: 'topup_approver', name: 'Top-up Approver', description: 'Approve data top-up requests and adjustments', color: 'purple', permissions: ['topup.approve', 'billing.view'] },
  { id: 'liberty_noc', name: 'Liberty NOC', description: 'Liberty Network Operations Center with full monitoring', color: 'orange', permissions: ['*.view', 'alerts.*', 'diagnostics.*'] },
  { id: 'support_desk', name: 'Support Desk', description: 'Handle support tickets and basic troubleshooting', color: 'cyan', permissions: ['support.*', 'services.view', 'alerts.view'] },
];

const allManagedUsers = [
  // Liberty Internal Users
  { id: 'lu-001', name: 'Marcus Johnson', email: 'marcus.johnson@liberty.net', phone: '+1 (555) 100-0001', organization: 'Liberty', orgType: 'liberty', type: 'Liberty Admin', roles: ['liberty_noc', 'customer_admin'], status: 'active', lastLogin: '2024-12-09 14:32', mfaEnabled: true, createdAt: '2023-01-15', department: 'Network Operations' },
  { id: 'lu-002', name: 'Jennifer Williams', email: 'j.williams@liberty.net', phone: '+1 (555) 100-0002', organization: 'Liberty', orgType: 'liberty', type: 'Liberty Admin', roles: ['customer_admin'], status: 'active', lastLogin: '2024-12-09 11:20', mfaEnabled: true, createdAt: '2023-02-20', department: 'Customer Success' },
  { id: 'lu-003', name: 'Thomas Wright', email: 't.wright@liberty.net', phone: '+1 (555) 100-0003', organization: 'Liberty', orgType: 'liberty', type: 'Liberty Support', roles: ['support_desk', 'liberty_noc'], status: 'active', lastLogin: '2024-12-09 09:45', mfaEnabled: true, createdAt: '2023-06-10', department: 'Support' },
  { id: 'lu-004', name: 'Patricia Adams', email: 'p.adams@liberty.net', phone: '+1 (555) 100-0004', organization: 'Liberty', orgType: 'liberty', type: 'Liberty Support', roles: ['support_desk'], status: 'active', lastLogin: '2024-12-08 16:30', mfaEnabled: false, createdAt: '2024-03-15', department: 'Support' },
  // ACME Corporation Users
  { id: 'cu-001', name: 'Sarah Chen', email: 'sarah.chen@acmecorp.com', phone: '+1 (555) 200-0001', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['customer_admin'], status: 'active', lastLogin: '2024-12-09 14:32', mfaEnabled: true, createdAt: '2024-01-15', department: 'IT Operations' },
  { id: 'cu-002', name: 'Robert Martinez', email: 'r.martinez@acmecorp.com', phone: '+1 (555) 200-0002', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['network_manager', 'ops_engineer'], status: 'active', lastLogin: '2024-12-08 09:15', mfaEnabled: true, createdAt: '2024-01-20', department: 'Network Engineering' },
  { id: 'cu-003', name: 'Jennifer Walsh', email: 'j.walsh@acmecorp.com', phone: '+1 (555) 200-0003', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['billing_admin'], status: 'active', lastLogin: '2024-12-07 16:45', mfaEnabled: false, createdAt: '2024-02-05', department: 'Finance' },
  { id: 'cu-004', name: 'Michael Thompson', email: 'm.thompson@acmecorp.com', phone: '+1 (555) 200-0004', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['readonly_viewer'], status: 'active', lastLogin: '2024-12-09 11:20', mfaEnabled: false, createdAt: '2024-03-12', department: 'Field Operations' },
  { id: 'cu-005', name: 'Amanda Foster', email: 'a.foster@acmecorp.com', phone: '+1 (555) 200-0005', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['ops_engineer'], status: 'locked', lastLogin: '2024-11-20 08:30', mfaEnabled: false, createdAt: '2024-04-18', department: 'Field Operations' },
  { id: 'cu-006', name: 'David Park', email: 'd.park@acmecorp.com', phone: '+1 (555) 200-0006', organization: 'ACME Corporation', orgType: 'customer', type: 'Customer User', roles: ['support_desk'], status: 'active', lastLogin: '2024-12-09 15:00', mfaEnabled: true, createdAt: '2024-05-22', department: 'Customer Support' },
  // TechStart Users
  { id: 'cu-007', name: 'Emily Rodriguez', email: 'emily@techstart.io', phone: '+1 (555) 300-0001', organization: 'TechStart Industries', orgType: 'customer', type: 'Customer User', roles: ['customer_admin', 'network_manager'], status: 'active', lastLogin: '2024-12-09 10:45', mfaEnabled: true, createdAt: '2024-03-20', department: 'IT' },
  { id: 'cu-008', name: 'Kevin Liu', email: 'kevin.liu@techstart.io', phone: '+1 (555) 300-0002', organization: 'TechStart Industries', orgType: 'customer', type: 'Customer User', roles: ['ops_engineer'], status: 'active', lastLogin: '2024-12-08 14:20', mfaEnabled: true, createdAt: '2024-04-01', department: 'Engineering' },
  { id: 'cu-009', name: 'Rachel Green', email: 'r.green@techstart.io', phone: '+1 (555) 300-0003', organization: 'TechStart Industries', orgType: 'customer', type: 'Customer User', roles: ['billing_admin', 'topup_approver'], status: 'active', lastLogin: '2024-12-06 11:30', mfaEnabled: false, createdAt: '2024-04-15', department: 'Finance' },
  { id: 'cu-010', name: 'Chris Anderson', email: 'c.anderson@techstart.io', phone: '+1 (555) 300-0004', organization: 'TechStart Industries', orgType: 'customer', type: 'Customer User', roles: ['readonly_viewer'], status: 'invited', lastLogin: null, mfaEnabled: false, createdAt: '2024-12-01', department: 'Operations' },
  // Maritime Solutions Users
  { id: 'cu-011', name: 'Captain James Morrison', email: 'j.morrison@maritimesol.com', phone: '+1 (555) 400-0001', organization: 'Maritime Solutions', orgType: 'customer', type: 'Customer User', roles: ['customer_admin', 'service_manager'], status: 'active', lastLogin: '2024-12-05 08:00', mfaEnabled: true, createdAt: '2024-06-10', department: 'Fleet Operations' },
];

const userAuditLog = [
  { id: 'ual-001', userId: 'cu-001', action: 'login', details: 'Logged in from 192.168.1.100', timestamp: '2024-12-09 14:32:15', ipAddress: '192.168.1.100' },
  { id: 'ual-002', userId: 'cu-001', action: 'role.change', details: 'Added role: Network Manager', timestamp: '2024-12-08 10:15:00', performedBy: 'Marcus Johnson' },
  { id: 'ual-003', userId: 'cu-002', action: 'config.update', details: 'Updated antenna ANT-003 power settings', timestamp: '2024-12-08 09:30:00', ipAddress: '10.0.0.55' },
  { id: 'ual-004', userId: 'cu-003', action: 'invoice.download', details: 'Downloaded invoice INV-2024-001', timestamp: '2024-12-07 16:50:22', ipAddress: '172.16.0.10' },
  { id: 'ual-005', userId: 'cu-006', action: 'topup.request', details: 'Requested 500GB data top-up for Branch SD-WAN', timestamp: '2024-12-09 15:05:00', ipAddress: '192.168.1.150' },
  { id: 'ual-006', userId: 'lu-001', action: 'user.create', details: 'Created user: c.anderson@techstart.io', timestamp: '2024-12-01 09:00:00', ipAddress: '10.10.10.1' },
  { id: 'ual-007', userId: 'cu-005', action: 'login.failed', details: 'Failed login attempt (3rd attempt)', timestamp: '2024-11-20 08:35:00', ipAddress: '203.0.113.50' },
  { id: 'ual-008', userId: 'lu-002', action: 'user.lock', details: 'Locked user: a.foster@acmecorp.com', timestamp: '2024-11-20 09:00:00', ipAddress: '10.10.10.2' },
];

// ============================================================================
// ACCOUNT MANAGEMENT MODULE DATA
// ============================================================================

const managedAccounts = [
  {
    id: 'acc-001',
    name: 'ACME Corporation',
    accountId: 'ACME-2024-001',
    status: 'active',
    segment: 'Enterprise',
    region: 'North America',
    industry: 'Manufacturing & Logistics',
    contractStart: '2024-01-01',
    contractEnd: '2026-12-31',
    planBundle: 'Starlink Business Pro',
    dataPool: { allocated: 15000, used: 9600, unit: 'GB' },
    primaryContact: { name: 'Sarah Chen', email: 'sarah.chen@acmecorp.com', phone: '+1 (555) 200-0001' },
    billingContact: { name: 'Jennifer Walsh', email: 'j.walsh@acmecorp.com', phone: '+1 (555) 200-0003' },
    hierarchy: {
      sites: [
        { id: 'site-hq', name: 'Headquarters', location: 'Chicago, IL', services: 2, terminals: 4, alerts: 0 },
        { id: 'site-west', name: 'West Coast DC', location: 'Los Angeles, CA', services: 1, terminals: 2, alerts: 1 },
        { id: 'site-east', name: 'East Coast DC', location: 'New York, NY', services: 1, terminals: 3, alerts: 0 },
      ],
      totalServices: 4,
      totalTerminals: 9,
      totalAlerts: 1,
    },
    stats: { users: 7, activeUsers: 6, services: 4, terminals: 9, monthlySpend: 12500 },
  },
  {
    id: 'acc-002',
    name: 'TechStart Industries',
    accountId: 'TECH-2024-002',
    status: 'active',
    segment: 'Mid-Market',
    region: 'North America',
    industry: 'Technology',
    contractStart: '2024-03-15',
    contractEnd: '2025-03-14',
    planBundle: 'Starlink Business Standard',
    dataPool: { allocated: 5000, used: 2800, unit: 'GB' },
    primaryContact: { name: 'Emily Rodriguez', email: 'emily@techstart.io', phone: '+1 (555) 300-0001' },
    billingContact: { name: 'Rachel Green', email: 'r.green@techstart.io', phone: '+1 (555) 300-0003' },
    hierarchy: {
      sites: [
        { id: 'site-main', name: 'Main Office', location: 'Austin, TX', services: 2, terminals: 3, alerts: 0 },
      ],
      totalServices: 2,
      totalTerminals: 3,
      totalAlerts: 0,
    },
    stats: { users: 4, activeUsers: 3, services: 2, terminals: 3, monthlySpend: 4500 },
  },
  {
    id: 'acc-003',
    name: 'Maritime Solutions',
    accountId: 'MARI-2024-003',
    status: 'active',
    segment: 'Enterprise',
    region: 'Global',
    industry: 'Shipping & Maritime',
    contractStart: '2024-06-01',
    contractEnd: '2027-05-31',
    planBundle: 'Starlink Maritime Enterprise',
    dataPool: { allocated: 25000, used: 18500, unit: 'GB' },
    primaryContact: { name: 'Captain James Morrison', email: 'j.morrison@maritimesol.com', phone: '+1 (555) 400-0001' },
    billingContact: { name: 'Captain James Morrison', email: 'j.morrison@maritimesol.com', phone: '+1 (555) 400-0001' },
    hierarchy: {
      sites: [
        { id: 'fleet-atlantic', name: 'Atlantic Fleet', location: 'At Sea', services: 3, terminals: 8, alerts: 2 },
        { id: 'fleet-pacific', name: 'Pacific Fleet', location: 'At Sea', services: 2, terminals: 5, alerts: 0 },
        { id: 'port-ops', name: 'Port Operations', location: 'Houston, TX', services: 1, terminals: 2, alerts: 0 },
      ],
      totalServices: 6,
      totalTerminals: 15,
      totalAlerts: 2,
    },
    stats: { users: 1, activeUsers: 1, services: 6, terminals: 15, monthlySpend: 35000 },
  },
  {
    id: 'acc-004',
    name: 'Global Energy Corp',
    accountId: 'GLEC-2024-004',
    status: 'suspended',
    segment: 'Enterprise',
    region: 'Global',
    industry: 'Energy & Utilities',
    contractStart: '2024-02-01',
    contractEnd: '2025-01-31',
    planBundle: 'Starlink Business Pro',
    dataPool: { allocated: 10000, used: 0, unit: 'GB' },
    primaryContact: { name: 'John Smith', email: 'j.smith@globalenergy.com', phone: '+1 (555) 500-0001' },
    billingContact: { name: 'Mary Johnson', email: 'm.johnson@globalenergy.com', phone: '+1 (555) 500-0002' },
    hierarchy: {
      sites: [
        { id: 'rig-north', name: 'Northern Rig', location: 'North Sea', services: 1, terminals: 2, alerts: 3 },
        { id: 'rig-gulf', name: 'Gulf Platform', location: 'Gulf of Mexico', services: 1, terminals: 2, alerts: 1 },
      ],
      totalServices: 2,
      totalTerminals: 4,
      totalAlerts: 4,
    },
    stats: { users: 0, activeUsers: 0, services: 2, terminals: 4, monthlySpend: 0 },
  },
];

const accountStats = {
  totalAccounts: 4,
  activeAccounts: 3,
  suspendedAccounts: 1,
  totalUsers: 15,
  totalServices: 14,
  totalTerminals: 31,
  monthlyRevenue: 52000,
};

// ============================================================================
// V4 ENHANCED DRUPAL 2.0-STYLE MULTI-SITE MANAGEMENT DATA
// ============================================================================

const v4RetailerMicrosites = [
  {
    id: 'retail-001',
    name: 'ACME Corporation',
    slug: 'acme',
    domain: 'acme.libertystarlink.com',
    customDomain: 'portal.acmecorp.com',
    status: 'active',
    templateId: 'tpl-001',
    createdAt: '2024-01-15',
    launchedAt: '2024-01-20',
    accountManager: 'Marcus Johnson',
    industry: 'Enterprise Technology',
    region: 'North America',
    tier: 'Enterprise',
    branding: {
      primaryColor: '#0066CC',
      secondaryColor: '#00A651',
      accentColor: '#FF6B35',
      logo: 'acme-logo.svg',
      favicon: 'acme-favicon.ico',
      headerStyle: 'gradient',
      fontFamily: 'Inter',
      borderRadius: 'rounded',
      darkMode: false,
      customCSS: '',
      emailLogo: 'acme-email-logo.png',
      loginBackground: 'acme-login-bg.jpg',
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: true,
      customReports: true,
      whiteLabel: true,
      sso: true,
      mfa: true,
    },
    limits: { maxUsers: 50, maxServices: 20, maxAntennas: 100, dataRetentionDays: 365, storageGb: 100 },
    usage: { users: 7, services: 2, antennas: 6, storageGb: 45, apiCallsMonth: 12500 },
    analytics: { pageViews30d: 4520, activeUsers: 5, avgSessionMin: 12, bounceRate: 28 },
    contacts: {
      primary: { name: 'Sarah Chen', email: 'sarah@acmecorp.com', phone: '+1-555-0101' },
      billing: { name: 'Jennifer Walsh', email: 'j.walsh@acmecorp.com', phone: '+1-555-0102' },
      technical: { name: 'Robert Martinez', email: 'r.martinez@acmecorp.com', phone: '+1-555-0103' },
    },
    billingInfo: { plan: 'Enterprise Annual', monthlyValue: 8500, contractEnd: '2025-01-15', paymentMethod: 'NET-30' },
    integrations: { crm: 'Salesforce', ticketing: 'Zendesk', monitoring: 'DataDog' },
  },
  {
    id: 'retail-002',
    name: 'TechStart Industries',
    slug: 'techstart',
    domain: 'techstart.libertystarlink.com',
    customDomain: null,
    status: 'active',
    templateId: 'tpl-002',
    createdAt: '2024-03-10',
    launchedAt: '2024-03-15',
    accountManager: 'Jennifer Williams',
    industry: 'Manufacturing',
    region: 'North America',
    tier: 'Business',
    branding: {
      primaryColor: '#7C3AED',
      secondaryColor: '#10B981',
      accentColor: '#F59E0B',
      logo: 'techstart-logo.svg',
      favicon: 'techstart-favicon.ico',
      headerStyle: 'solid',
      fontFamily: 'Roboto',
      borderRadius: 'rounded-lg',
      darkMode: false,
      customCSS: '',
      emailLogo: null,
      loginBackground: null,
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: false,
      customReports: false,
      whiteLabel: false,
      sso: false,
      mfa: true,
    },
    limits: { maxUsers: 15, maxServices: 5, maxAntennas: 25, dataRetentionDays: 180, storageGb: 25 },
    usage: { users: 4, services: 2, antennas: 4, storageGb: 12, apiCallsMonth: 0 },
    analytics: { pageViews30d: 2340, activeUsers: 3, avgSessionMin: 8, bounceRate: 35 },
    contacts: {
      primary: { name: 'Mike Torres', email: 'mike@techstart.io', phone: '+1-555-0201' },
      billing: { name: 'Lisa Park', email: 'lisa@techstart.io', phone: '+1-555-0202' },
      technical: { name: 'Mike Torres', email: 'mike@techstart.io', phone: '+1-555-0201' },
    },
    billingInfo: { plan: 'Business Monthly', monthlyValue: 2400, contractEnd: null, paymentMethod: 'Credit Card' },
    integrations: { crm: null, ticketing: null, monitoring: null },
  },
  {
    id: 'retail-003',
    name: 'Maritime Solutions LLC',
    slug: 'maritime',
    domain: 'maritime.libertystarlink.com',
    customDomain: 'fleet.maritimesolutions.com',
    status: 'active',
    templateId: 'tpl-003',
    createdAt: '2024-02-20',
    launchedAt: '2024-02-28',
    accountManager: 'Marcus Johnson',
    industry: 'Maritime & Shipping',
    region: 'Gulf Coast',
    tier: 'Enterprise',
    branding: {
      primaryColor: '#0EA5E9',
      secondaryColor: '#0369A1',
      accentColor: '#F97316',
      logo: 'maritime-logo.svg',
      favicon: 'maritime-favicon.ico',
      headerStyle: 'gradient',
      fontFamily: 'Open Sans',
      borderRadius: 'rounded-xl',
      darkMode: true,
      customCSS: '',
      emailLogo: 'maritime-email-logo.png',
      loginBackground: 'maritime-ocean-bg.jpg',
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: true,
      customReports: true,
      whiteLabel: true,
      sso: true,
      mfa: true,
    },
    limits: { maxUsers: 30, maxServices: 15, maxAntennas: 75, dataRetentionDays: 365, storageGb: 75 },
    usage: { users: 3, services: 1, antennas: 3, storageGb: 28, apiCallsMonth: 8200 },
    analytics: { pageViews30d: 3210, activeUsers: 2, avgSessionMin: 15, bounceRate: 22 },
    contacts: {
      primary: { name: 'Captain James Rivera', email: 'j.rivera@maritimesolutions.com', phone: '+1-555-0301' },
      billing: { name: 'Anna Hughes', email: 'a.hughes@maritimesolutions.com', phone: '+1-555-0302' },
      technical: { name: 'Thomas Lee', email: 't.lee@maritimesolutions.com', phone: '+1-555-0303' },
    },
    billingInfo: { plan: 'Maritime Premium', monthlyValue: 6200, contractEnd: '2025-02-28', paymentMethod: 'NET-30' },
    integrations: { crm: 'HubSpot', ticketing: 'Freshdesk', monitoring: 'Prometheus' },
  },
  {
    id: 'retail-004',
    name: 'Global Energy Corp',
    slug: 'globalenergy',
    domain: 'globalenergy.libertystarlink.com',
    customDomain: null,
    status: 'pending',
    templateId: 'tpl-001',
    createdAt: '2024-12-01',
    launchedAt: null,
    accountManager: 'Jennifer Williams',
    industry: 'Energy & Utilities',
    region: 'North America',
    tier: 'Enterprise',
    branding: {
      primaryColor: '#059669',
      secondaryColor: '#0D9488',
      accentColor: '#FBBF24',
      logo: null,
      favicon: null,
      headerStyle: 'solid',
      fontFamily: 'Inter',
      borderRadius: 'rounded-lg',
      darkMode: false,
      customCSS: '',
      emailLogo: null,
      loginBackground: null,
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: true,
      customReports: true,
      whiteLabel: true,
      sso: true,
      mfa: true,
    },
    limits: { maxUsers: 50, maxServices: 20, maxAntennas: 100, dataRetentionDays: 365, storageGb: 100 },
    usage: { users: 0, services: 0, antennas: 0, storageGb: 0, apiCallsMonth: 0 },
    analytics: { pageViews30d: 0, activeUsers: 0, avgSessionMin: 0, bounceRate: 0 },
    contacts: {
      primary: { name: 'David Chen', email: 'd.chen@globalenergy.com', phone: '+1-555-0401' },
      billing: { name: 'Pending', email: '', phone: '' },
      technical: { name: 'Pending', email: '', phone: '' },
    },
    billingInfo: { plan: 'Enterprise Annual', monthlyValue: 9500, contractEnd: null, paymentMethod: 'Pending' },
    integrations: { crm: null, ticketing: null, monitoring: null },
  },
  {
    id: 'retail-005',
    name: 'Construction Dynamics',
    slug: 'constdyn',
    domain: 'constdyn.libertystarlink.com',
    customDomain: null,
    status: 'suspended',
    templateId: 'tpl-002',
    createdAt: '2024-04-05',
    launchedAt: '2024-04-10',
    accountManager: 'Marcus Johnson',
    industry: 'Construction',
    region: 'Southwest',
    tier: 'Business',
    branding: {
      primaryColor: '#D97706',
      secondaryColor: '#92400E',
      accentColor: '#DC2626',
      logo: 'constdyn-logo.svg',
      favicon: 'constdyn-favicon.ico',
      headerStyle: 'solid',
      fontFamily: 'Poppins',
      borderRadius: 'rounded',
      darkMode: false,
      customCSS: '',
      emailLogo: null,
      loginBackground: null,
    },
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: false,
      customReports: false,
      whiteLabel: false,
      sso: false,
      mfa: false,
    },
    limits: { maxUsers: 15, maxServices: 5, maxAntennas: 25, dataRetentionDays: 180, storageGb: 25 },
    usage: { users: 2, services: 1, antennas: 2, storageGb: 8, apiCallsMonth: 0 },
    analytics: { pageViews30d: 120, activeUsers: 0, avgSessionMin: 0, bounceRate: 0 },
    contacts: {
      primary: { name: 'Steve Murphy', email: 's.murphy@constdyn.com', phone: '+1-555-0501' },
      billing: { name: 'Steve Murphy', email: 's.murphy@constdyn.com', phone: '+1-555-0501' },
      technical: { name: 'Steve Murphy', email: 's.murphy@constdyn.com', phone: '+1-555-0501' },
    },
    billingInfo: { plan: 'Business Monthly', monthlyValue: 1800, contractEnd: null, paymentMethod: 'Credit Card - Failed' },
    integrations: { crm: null, ticketing: null, monitoring: null },
  },
];

const v4RetailerUsers = [
  // ACME Corporation Users
  { id: 'ru-001', micrositeId: 'retail-001', name: 'Sarah Chen', email: 'sarah@acmecorp.com', role: 'site_admin', status: 'active', lastLogin: '2024-12-09 14:32', mfaEnabled: true, createdAt: '2024-01-20', invitedBy: 'Platform', department: 'IT Operations' },
  { id: 'ru-002', micrositeId: 'retail-001', name: 'Robert Martinez', email: 'r.martinez@acmecorp.com', role: 'site_admin', status: 'active', lastLogin: '2024-12-08 16:45', mfaEnabled: true, createdAt: '2024-01-22', invitedBy: 'Sarah Chen', department: 'Network Engineering' },
  { id: 'ru-003', micrositeId: 'retail-001', name: 'Jennifer Walsh', email: 'j.walsh@acmecorp.com', role: 'billing_admin', status: 'active', lastLogin: '2024-12-07 10:20', mfaEnabled: false, createdAt: '2024-02-01', invitedBy: 'Sarah Chen', department: 'Finance' },
  { id: 'ru-004', micrositeId: 'retail-001', name: 'Michael Torres', email: 'm.torres@acmecorp.com', role: 'network_viewer', status: 'active', lastLogin: '2024-12-09 09:15', mfaEnabled: true, createdAt: '2024-03-15', invitedBy: 'Robert Martinez', department: 'IT Operations' },
  { id: 'ru-005', micrositeId: 'retail-001', name: 'Emily Davis', email: 'e.davis@acmecorp.com', role: 'network_viewer', status: 'active', lastLogin: '2024-12-05 14:30', mfaEnabled: false, createdAt: '2024-04-10', invitedBy: 'Sarah Chen', department: 'IT Operations' },
  { id: 'ru-006', micrositeId: 'retail-001', name: 'David Park', email: 'd.park@acmecorp.com', role: 'support_agent', status: 'active', lastLogin: '2024-12-09 15:10', mfaEnabled: true, createdAt: '2024-12-08', invitedBy: 'Robert Martinez', department: 'Support' },
  { id: 'ru-007', micrositeId: 'retail-001', name: 'Lisa Anderson', email: 'l.anderson@acmecorp.com', role: 'readonly', status: 'invited', lastLogin: null, mfaEnabled: false, createdAt: '2024-12-09', invitedBy: 'Sarah Chen', department: 'Management' },
  // TechStart Users
  { id: 'ru-008', micrositeId: 'retail-002', name: 'Mike Torres', email: 'mike@techstart.io', role: 'site_admin', status: 'active', lastLogin: '2024-12-09 11:20', mfaEnabled: true, createdAt: '2024-03-15', invitedBy: 'Platform', department: 'IT' },
  { id: 'ru-009', micrositeId: 'retail-002', name: 'Lisa Park', email: 'lisa@techstart.io', role: 'billing_admin', status: 'active', lastLogin: '2024-12-08 09:45', mfaEnabled: false, createdAt: '2024-03-20', invitedBy: 'Mike Torres', department: 'Finance' },
  { id: 'ru-010', micrositeId: 'retail-002', name: 'James Wilson', email: 'james@techstart.io', role: 'network_viewer', status: 'active', lastLogin: '2024-12-06 16:30', mfaEnabled: true, createdAt: '2024-05-01', invitedBy: 'Mike Torres', department: 'Operations' },
  { id: 'ru-011', micrositeId: 'retail-002', name: 'Anna Kim', email: 'anna@techstart.io', role: 'readonly', status: 'active', lastLogin: '2024-12-04 08:15', mfaEnabled: false, createdAt: '2024-06-15', invitedBy: 'Mike Torres', department: 'Management' },
  // Maritime Solutions Users
  { id: 'ru-012', micrositeId: 'retail-003', name: 'Captain James Rivera', email: 'j.rivera@maritimesolutions.com', role: 'site_admin', status: 'active', lastLogin: '2024-12-09 06:30', mfaEnabled: true, createdAt: '2024-02-28', invitedBy: 'Platform', department: 'Operations' },
  { id: 'ru-013', micrositeId: 'retail-003', name: 'Anna Hughes', email: 'a.hughes@maritimesolutions.com', role: 'billing_admin', status: 'active', lastLogin: '2024-12-07 14:20', mfaEnabled: true, createdAt: '2024-03-05', invitedBy: 'Captain James Rivera', department: 'Finance' },
  { id: 'ru-014', micrositeId: 'retail-003', name: 'Thomas Lee', email: 't.lee@maritimesolutions.com', role: 'network_manager', status: 'active', lastLogin: '2024-12-09 08:45', mfaEnabled: true, createdAt: '2024-03-10', invitedBy: 'Captain James Rivera', department: 'Technical' },
  // Construction Dynamics Users (suspended site)
  { id: 'ru-015', micrositeId: 'retail-005', name: 'Steve Murphy', email: 's.murphy@constdyn.com', role: 'site_admin', status: 'suspended', lastLogin: '2024-11-15 10:20', mfaEnabled: false, createdAt: '2024-04-10', invitedBy: 'Platform', department: 'Management' },
  { id: 'ru-016', micrositeId: 'retail-005', name: 'Tom Baker', email: 't.baker@constdyn.com', role: 'network_viewer', status: 'suspended', lastLogin: '2024-11-10 14:30', mfaEnabled: false, createdAt: '2024-04-15', invitedBy: 'Steve Murphy', department: 'Field Ops' },
];

const v4PlatformActivity = [
  { id: 'pa-001', type: 'site_created', siteId: 'retail-004', siteName: 'Global Energy Corp', userId: 'pa-002', userName: 'Jennifer Williams', details: 'New microsite created from Enterprise Standard template', timestamp: '2024-12-01 09:30', ipAddress: '10.0.0.100' },
  { id: 'pa-002', type: 'user_login', siteId: 'retail-001', siteName: 'ACME Corporation', userId: 'ru-001', userName: 'Sarah Chen', details: 'User logged in', timestamp: '2024-12-09 14:32', ipAddress: '192.168.1.100' },
  { id: 'pa-003', type: 'branding_updated', siteId: 'retail-001', siteName: 'ACME Corporation', userId: 'ru-001', userName: 'Sarah Chen', details: 'Updated primary color and header style', timestamp: '2024-12-09 14:45', ipAddress: '192.168.1.100' },
  { id: 'pa-004', type: 'user_invited', siteId: 'retail-001', siteName: 'ACME Corporation', userId: 'ru-002', userName: 'Robert Martinez', details: 'Invited new user: d.park@acmecorp.com', timestamp: '2024-12-08 10:20', ipAddress: '10.0.0.55' },
  { id: 'pa-005', type: 'site_suspended', siteId: 'retail-005', siteName: 'Construction Dynamics', userId: 'pa-001', userName: 'Marcus Johnson', details: 'Site suspended due to payment failure', timestamp: '2024-12-01 16:00', ipAddress: '10.0.0.100' },
  { id: 'pa-006', type: 'feature_enabled', siteId: 'retail-002', siteName: 'TechStart Industries', userId: 'ru-008', userName: 'Mike Torres', details: 'Enabled MFA requirement for all users', timestamp: '2024-11-28 11:15', ipAddress: '172.16.0.50' },
  { id: 'pa-007', type: 'domain_verified', siteId: 'retail-003', siteName: 'Maritime Solutions LLC', userId: 'pa-001', userName: 'Marcus Johnson', details: 'Custom domain fleet.maritimesolutions.com verified', timestamp: '2024-11-25 14:30', ipAddress: '10.0.0.100' },
  { id: 'pa-008', type: 'integration_connected', siteId: 'retail-001', siteName: 'ACME Corporation', userId: 'ru-001', userName: 'Sarah Chen', details: 'Connected Salesforce CRM integration', timestamp: '2024-11-20 09:45', ipAddress: '192.168.1.100' },
];

const v4PlatformStats = {
  totalMicrosites: 5,
  activeMicrosites: 3,
  pendingMicrosites: 1,
  suspendedMicrosites: 1,
  totalRetailerUsers: 16,
  activeUsersToday: 8,
  totalPageViews30d: 10190,
  totalApiCalls30d: 20700,
  totalMonthlyRevenue: 28400,
  avgSiteHealthScore: 87,
  sitesWithAlerts: 2,
  pendingInvitations: 3,
};

const v4SiteRolesDetailed = [
  { 
    id: 'site_admin', 
    name: 'Site Administrator', 
    description: 'Full control over site configuration, users, and all features',
    color: 'rose',
    icon: 'ShieldCheck',
    permissions: ['users.*', 'branding.*', 'features.*', 'billing.view', 'billing.edit', 'services.*', 'antennas.*', 'alerts.*', 'support.*', 'reports.*', 'settings.*'],
    canInviteRoles: ['site_admin', 'billing_admin', 'network_manager', 'network_viewer', 'support_agent', 'readonly'],
    maxPerSite: 5,
  },
  { 
    id: 'billing_admin', 
    name: 'Billing Administrator', 
    description: 'Manage billing, invoices, payments, and financial reports',
    color: 'amber',
    icon: 'CreditCard',
    permissions: ['billing.*', 'invoices.*', 'reports.billing', 'services.view'],
    canInviteRoles: [],
    maxPerSite: 3,
  },
  { 
    id: 'network_manager', 
    name: 'Network Manager', 
    description: 'Configure services, antennas, and network settings',
    color: 'indigo',
    icon: 'Network',
    permissions: ['services.*', 'antennas.*', 'alerts.*', 'reports.network', 'dataPools.*'],
    canInviteRoles: ['network_viewer'],
    maxPerSite: 5,
  },
  { 
    id: 'network_viewer', 
    name: 'Network Viewer', 
    description: 'View network status, services, and performance metrics',
    color: 'teal',
    icon: 'Eye',
    permissions: ['services.view', 'antennas.view', 'alerts.view', 'dataPools.view', 'reports.view'],
    canInviteRoles: [],
    maxPerSite: 20,
  },
  { 
    id: 'support_agent', 
    name: 'Support Agent', 
    description: 'Create and manage support tickets, view service status',
    color: 'sky',
    icon: 'MessageSquare',
    permissions: ['support.*', 'services.view', 'alerts.view'],
    canInviteRoles: [],
    maxPerSite: 10,
  },
  { 
    id: 'readonly', 
    name: 'Read-Only Viewer', 
    description: 'View-only access to dashboards and basic information',
    color: 'slate',
    icon: 'Eye',
    permissions: ['dashboard.view', 'services.view'],
    canInviteRoles: [],
    maxPerSite: 50,
  },
];

const v4BrandingPresets = [
  { id: 'preset-corporate', name: 'Corporate Blue', colors: { primary: '#0066CC', secondary: '#004499', accent: '#00A651' } },
  { id: 'preset-modern', name: 'Modern Purple', colors: { primary: '#7C3AED', secondary: '#5B21B6', accent: '#10B981' } },
  { id: 'preset-ocean', name: 'Ocean Blue', colors: { primary: '#0EA5E9', secondary: '#0369A1', accent: '#F97316' } },
  { id: 'preset-forest', name: 'Forest Green', colors: { primary: '#059669', secondary: '#047857', accent: '#FBBF24' } },
  { id: 'preset-sunset', name: 'Sunset Orange', colors: { primary: '#EA580C', secondary: '#C2410C', accent: '#0891B2' } },
  { id: 'preset-midnight', name: 'Midnight Dark', colors: { primary: '#1E293B', secondary: '#0F172A', accent: '#38BDF8' } },
];

const v4EmailTemplates = [
  { id: 'email-welcome', name: 'Welcome Email', subject: 'Welcome to {{site_name}}', description: 'Sent when a new user is invited' },
  { id: 'email-password', name: 'Password Reset', subject: 'Reset your {{site_name}} password', description: 'Sent when user requests password reset' },
  { id: 'email-invoice', name: 'Invoice Ready', subject: 'Your {{site_name}} invoice is ready', description: 'Sent when new invoice is generated' },
  { id: 'email-alert', name: 'Alert Notification', subject: '[{{severity}}] Alert: {{alert_title}}', description: 'Sent for critical alerts' },
  { id: 'email-mfa', name: 'MFA Setup', subject: 'Enable two-factor authentication', description: 'Sent when MFA is required' },
];

// ============================================================================
// CONTEXT
// ============================================================================

const AppContext = createContext();

const useApp = () => useContext(AppContext);

// ============================================================================
// UI COMPONENTS
// ============================================================================

const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border border-amber-200',
    critical: 'bg-rose-50 text-rose-700 border border-rose-200',
    info: 'bg-sky-50 text-sky-700 border border-sky-200',
    teal: 'bg-teal-50 text-teal-700 border border-teal-200',
    indigo: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  };
  
  const sizes = {
    xs: 'px-1.5 py-0.5 text-[10px]',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };
  
  return (
    <span className={`inline-flex items-center font-medium rounded-full ${variants[variant]} ${sizes[size]}`}>
      {children}
    </span>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', icon: Icon, onClick, className = '' }) => {
  const variants = {
    primary: 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-sm shadow-teal-200',
    secondary: 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300',
    ghost: 'text-slate-600 hover:bg-slate-100',
    danger: 'bg-rose-500 text-white hover:bg-rose-600',
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 font-medium rounded-xl transition-all duration-200 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

const Card = ({ children, className = '', padding = 'p-5', hover = false }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm ${padding} ${hover ? 'hover:shadow-md hover:border-slate-200 transition-all duration-200 cursor-pointer' : ''} ${className}`}>
    {children}
  </div>
);

const StatTile = ({ label, value, subValue, icon: Icon, trend, trendValue, color = 'teal' }) => {
  const colors = {
    teal: 'bg-teal-50 text-teal-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };
  
  return (
    <Card className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-slate-500 mb-0.5">{label}</p>
        <p className="text-2xl font-semibold text-slate-800">{value}</p>
        {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${trend === 'up' ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
          activeTab === tab.id
            ? 'bg-white text-slate-800 shadow-sm'
            : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const TimeRangeChips = ({ selected, onChange }) => {
  const ranges = ['15m', '1h', '24h', '7d', '30d'];
  return (
    <div className="flex gap-2">
      {ranges.map((range) => (
        <button
          key={range}
          onClick={() => onChange(range)}
          className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${
            selected === range
              ? 'bg-teal-500 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {range}
        </button>
      ))}
    </div>
  );
};

const SearchInput = ({ placeholder = 'Search...', value, onChange }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all"
    />
  </div>
);

const Toast = ({ message, type = 'success', onClose }) => {
  const types = {
    success: 'bg-emerald-500',
    error: 'bg-rose-500',
    warning: 'bg-amber-500',
    info: 'bg-sky-500',
  };
  
  return (
    <div className={`fixed bottom-4 right-4 ${types[type]} text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-slide-up z-50`}>
      <span>{message}</span>
      <button onClick={onClose} className="hover:opacity-80">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============================================================================
// CHART COMPONENTS
// ============================================================================

const CHART_COLORS = {
  teal: '#14b8a6',
  indigo: '#6366f1',
  amber: '#f59e0b',
  rose: '#f43f5e',
  emerald: '#10b981',
  sky: '#0ea5e9',
};

const ServiceHealthDonut = ({ antennas }) => {
  const healthy = antennas.filter(a => a.status === 'healthy').length;
  const attention = antennas.filter(a => a.status === 'attention').length;
  const critical = antennas.filter(a => a.status === 'critical').length;
  const total = antennas.length;
  
  const data = [
    { name: 'Healthy', value: healthy, color: CHART_COLORS.emerald },
    { name: 'Attention', value: attention, color: CHART_COLORS.amber },
    { name: 'Critical', value: critical, color: CHART_COLORS.rose },
  ];
  
  const healthPercent = Math.round((healthy / total) * 100);
  
  return (
    <Card className="h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Service Health & SLA</h3>
        <Badge variant="success">99.5% SLA</Badge>
      </div>
      <div className="flex items-center gap-6">
        <div className="relative w-32 h-32">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={50}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800">{healthPercent}%</p>
              <p className="text-[10px] text-slate-500">Healthy</p>
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-sm text-slate-600">{healthy} Healthy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-sm text-slate-600">{attention} Attention</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-rose-500"></div>
            <span className="text-sm text-slate-600">{critical} Critical</span>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Activity className="w-4 h-4" />
          <span>Last 24h: 2 incidents resolved</span>
        </div>
      </div>
    </Card>
  );
};

const UsageAreaChart = ({ data, serviceId }) => {
  return (
    <Card className="h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Monthly Data Usage</h3>
        <TimeRangeChips selected="30d" onChange={() => {}} />
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.teal} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={CHART_COLORS.teal} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="timestamp" tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0', 
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }} 
            />
            <Area type="monotone" dataKey={serviceId} stroke={CHART_COLORS.teal} fillOpacity={1} fill="url(#colorUsage)" strokeWidth={2} />
            <Line type="monotone" dataKey={serviceId} stroke={CHART_COLORS.indigo} strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center justify-between text-sm">
        <span className="text-slate-500">Committed: 400 GB/month</span>
        <span className="text-teal-600 font-medium">Used: 342 GB</span>
      </div>
    </Card>
  );
};

const PerformanceGauge = ({ value, max, label, unit, color = 'teal' }) => {
  const percent = (value / max) * 100;
  const colors = {
    teal: { bg: 'bg-teal-100', fill: 'bg-teal-500', text: 'text-teal-600' },
    indigo: { bg: 'bg-indigo-100', fill: 'bg-indigo-500', text: 'text-indigo-600' },
    amber: { bg: 'bg-amber-100', fill: 'bg-amber-500', text: 'text-amber-600' },
  };
  
  return (
    <Card className="text-center">
      <div className="relative w-24 h-24 mx-auto mb-3">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="48" cy="48" r="40" stroke="#e2e8f0" strokeWidth="8" fill="none" />
          <circle 
            cx="48" 
            cy="48" 
            r="40" 
            stroke={CHART_COLORS[color]} 
            strokeWidth="8" 
            fill="none"
            strokeDasharray={`${percent * 2.51} 251`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl font-bold ${colors[color].text}`}>{value}</span>
        </div>
      </div>
      <p className="text-sm font-medium text-slate-800">{label}</p>
      <p className="text-xs text-slate-500">{unit}</p>
    </Card>
  );
};

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

const Sidebar = ({ currentPage, onNavigate, isOpen, onClose, role }) => {
  const navItems = [
    { id: 'connectivity', label: 'Connectivity', icon: Home },
    { id: 'services', label: 'Services', icon: Server },
    { id: 'fleet-map', label: 'Fleet Map', icon: Map },
    { id: 'data-pools', label: 'Data Pools', icon: Database },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'usage', label: 'Usage', icon: BarChart3 },
    { id: 'alerts', label: 'Alerts', icon: Bell },
    { id: 'support', label: 'Support', icon: HelpCircle },
  ];
  
  const adminItems = [
    { id: 'microsites', label: 'Platform Admin', icon: Layers, badge: '4' },
    { id: 'site-admin-portal', label: 'Site Admin', icon: UserCog, badge: 'ACME' },
    { id: 'user-management', label: 'User Management', icon: Users, badge: '15' },
    { id: 'account-management', label: 'Account Mgmt', icon: Building, badge: '4' },
    { id: 'site-users', label: 'Microsite Users', icon: Users },
  ];
  
  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:transform-none ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-indigo-600 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800">Liberty</h1>
              <p className="text-xs text-slate-500">Starlink Connectivity</p>
            </div>
          </div>
        </div>
        
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => { onNavigate(item.id); onClose(); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? 'bg-gradient-to-r from-teal-50 to-indigo-50 text-teal-700 border border-teal-100' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                    {item.label}
                    {item.id === 'alerts' && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-rose-100 text-rose-600 rounded-full">4</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          
          {/* Admin Section - Only visible for Admin role */}
          {role === 'LIBERTY_ADMIN' && (
            <>
              <div className="mt-6 mb-2 px-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Administration</p>
              </div>
              <ul className="space-y-1">
                {adminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => { onNavigate(item.id); onClose(); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                          isActive 
                            ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-100' 
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                        {item.label}
                        {item.badge && (
                          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-600 rounded-full">{item.badge}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100">
          <Card padding="p-3" className="bg-gradient-to-r from-slate-50 to-teal-50/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-teal-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-700">System Status</p>
                <p className="text-[10px] text-emerald-600">All systems operational</p>
              </div>
            </div>
          </Card>
        </div>
      </aside>
    </>
  );
};

const Header = ({ role, onRoleChange, searchQuery, onSearchChange }) => {
  const { sidebarOpen, setSidebarOpen } = useApp();
  
  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="flex items-center justify-between px-4 lg:px-6 py-3">
        <div className="flex items-center gap-4">
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="hidden md:block w-72">
            <SearchInput 
              placeholder="Search services, antennas, users..." 
              value={searchQuery}
              onChange={onSearchChange}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={role}
            onChange={(e) => onRoleChange(e.target.value)}
            className="text-xs font-medium px-3 py-1.5 bg-slate-100 border-0 rounded-lg text-slate-600 focus:ring-2 focus:ring-teal-500/20"
          >
            <option value="LIBERTY_CUSTOMER">Customer View</option>
            <option value="LIBERTY_ADMIN">Admin View</option>
          </select>
          
          <button className="relative p-2 hover:bg-slate-100 rounded-lg">
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full"></span>
          </button>
          
          <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-indigo-500 flex items-center justify-center text-white text-sm font-medium">
              {role === 'LIBERTY_ADMIN' ? 'MJ' : 'SC'}
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-slate-800">
                {role === 'LIBERTY_ADMIN' ? 'Marcus Johnson' : 'Sarah Chen'}
              </p>
              <p className="text-xs text-slate-500">
                {role === 'LIBERTY_ADMIN' ? 'NOC Admin' : 'Network Ops'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// ============================================================================
// PAGE COMPONENTS
// ============================================================================

const ServiceCard = ({ service, antennas, isSelected, onClick }) => {
  const serviceAntennas = antennas.filter(a => service.antennas.includes(a.id));
  const healthyCount = serviceAntennas.filter(a => a.status === 'healthy').length;
  const avgDown = Math.round(serviceAntennas.reduce((acc, a) => acc + a.downMbps, 0) / serviceAntennas.length);
  const usagePercent = Math.round((service.dataUsedGb / service.dataPoolSizeGb) * 100);
  
  const statusColors = {
    active: 'success',
    attention: 'warning',
    suspended: 'critical',
  };
  
  return (
    <Card 
      hover 
      onClick={onClick}
      className={`min-w-[280px] transition-all duration-200 ${isSelected ? 'ring-2 ring-teal-500 border-teal-200' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-800">{service.name}</h3>
          <p className="text-xs text-slate-500">{service.ownerTeamName}</p>
        </div>
        <Badge variant={statusColors[service.status]}>{service.status}</Badge>
      </div>
      
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-lg font-semibold text-slate-800">{serviceAntennas.length}</p>
          <p className="text-[10px] text-slate-500">Antennas</p>
        </div>
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-lg font-semibold text-teal-600">{avgDown}</p>
          <p className="text-[10px] text-slate-500">Mbps \u2193</p>
        </div>
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-lg font-semibold text-indigo-600">{healthyCount}/{serviceAntennas.length}</p>
          <p className="text-[10px] text-slate-500">Healthy</p>
        </div>
      </div>
      
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">Data Pool</span>
          <span className="font-medium text-slate-700">{usagePercent}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${usagePercent > 80 ? 'bg-amber-500' : 'bg-teal-500'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      </div>
    </Card>
  );
};

const AntennasTable = ({ antennas, onRowClick, expandedRow, onExpand }) => {
  return (
    <Card padding="p-0">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Antennas</h3>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" icon={Filter}>Filter</Button>
          <Button variant="ghost" size="sm" icon={Download}>Export</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Site</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Antenna</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Starlink ID</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Status</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Signal</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Down</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Up</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Last Check-in</th>
              <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {antennas.map((antenna) => (
              <React.Fragment key={antenna.id}>
                <tr 
                  className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                  onClick={() => onExpand(expandedRow === antenna.id ? null : antenna.id)}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700">{antenna.siteName}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm font-medium text-slate-800">{antenna.name}</span>
                  </td>
                  <td className="px-5 py-3">
                    <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">{antenna.starlinkId}</code>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={antenna.status === 'healthy' ? 'success' : antenna.status === 'attention' ? 'warning' : 'critical'}>
                      {antenna.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${antenna.signalQuality > 80 ? 'bg-emerald-500' : antenna.signalQuality > 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                          style={{ width: `${antenna.signalQuality}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500">{antenna.signalQuality}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-teal-600 font-medium">{antenna.downMbps} Mbps</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-indigo-600 font-medium">{antenna.upMbps} Mbps</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-sm text-slate-500">{antenna.lastCheckIn}</span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="Run Diagnostics">
                        <Play className="w-4 h-4 text-slate-400" />
                      </button>
                      <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="Details">
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </button>
                      <button className="p-1.5 hover:bg-slate-100 rounded-lg">
                        {expandedRow === antenna.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRow === antenna.id && (
                  <tr>
                    <td colSpan="9" className="bg-slate-50/50 px-5 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 grid grid-cols-4 gap-4">
                          <div className="p-3 bg-white rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-500 mb-1">Firmware</p>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-slate-800">{antenna.firmwareVersion}</p>
                              <button className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors">
                                Upgrade
                              </button>
                            </div>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-500 mb-1">Coordinates</p>
                            <p className="text-sm font-medium text-slate-800">{antenna.latitude.toFixed(4)}, {antenna.longitude.toFixed(4)}</p>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-500 mb-1">Obstruction</p>
                            <p className="text-sm font-medium text-slate-800">{(100 - antenna.signalQuality) / 3}%</p>
                          </div>
                          <div className="p-3 bg-white rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-500 mb-1">Uptime (24h)</p>
                            <p className="text-sm font-medium text-emerald-600">99.8%</p>
                          </div>
                        </div>
                        {/* Reboot Button - Extreme Right */}
                        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl transition-colors">
                          <RefreshCw className="w-4 h-4" />
                          Reboot
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const AlertsList = ({ alerts, compact = false }) => {
  const severityIcons = {
    critical: <XCircle className="w-4 h-4 text-rose-500" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    info: <CheckCircle className="w-4 h-4 text-sky-500" />,
  };
  
  return (
    <Card className="h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Active Alerts</h3>
        <Badge variant="critical">{alerts.filter(a => a.status === 'active').length} Active</Badge>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {alerts.filter(a => a.status === 'active').slice(0, compact ? 4 : 10).map((alert) => (
          <div 
            key={alert.id}
            className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
          >
            {severityIcons[alert.severity]}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{alert.title}</p>
              <p className="text-xs text-slate-500 truncate">{alert.description}</p>
              <p className="text-[10px] text-slate-400 mt-1">{alert.createdAt}</p>
            </div>
            <Badge variant={alert.severity === 'critical' ? 'critical' : alert.severity === 'warning' ? 'warning' : 'info'} size="xs">
              {alert.category}
            </Badge>
          </div>
        ))}
      </div>
    </Card>
  );
};

const TopSitesPanel = ({ antennas }) => {
  const siteData = Object.entries(
    antennas.reduce((acc, a) => {
      acc[a.siteName] = (acc[a.siteName] || 0) + a.downMbps + a.upMbps;
      return acc;
    }, {})
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  return (
    <Card className="h-full">
      <h3 className="font-semibold text-slate-800 mb-4">Top Sites by Traffic</h3>
      <div className="space-y-3">
        {siteData.map(([site, traffic], index) => (
          <div key={site} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-100 to-indigo-100 flex items-center justify-center text-xs font-medium text-teal-700">
              {index + 1}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">{site}</p>
              <div className="w-full h-1 bg-slate-100 rounded-full mt-1">
                <div 
                  className="h-full bg-gradient-to-r from-teal-500 to-indigo-500 rounded-full"
                  style={{ width: `${(traffic / siteData[0][1]) * 100}%` }}
                />
              </div>
            </div>
            <span className="text-xs font-medium text-slate-600">{Math.round(traffic)} Mbps</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ============================================================================
// PAGES
// ============================================================================

const ConnectivityOverviewPage = ({ services, antennas, alerts, usageData }) => {
  const [selectedServiceId, setSelectedServiceId] = useState(services[0]?.id);
  const [expandedRow, setExpandedRow] = useState(null);
  
  const selectedService = services.find(s => s.id === selectedServiceId);
  const filteredAntennas = selectedService 
    ? antennas.filter(a => selectedService.antennas.includes(a.id))
    : antennas;
  const filteredAlerts = selectedService
    ? alerts.filter(a => a.serviceId === selectedServiceId)
    : alerts;
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Connectivity & Services</h1>
        <p className="text-slate-500">Overview of Liberty services and associated Starlink antennas</p>
      </div>
      
      {/* Service Cards Scroll */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 lg:-mx-6 lg:px-6">
        <div className="flex gap-4 min-w-max">
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              antennas={antennas}
              isSelected={selectedServiceId === service.id}
              onClick={() => setSelectedServiceId(service.id)}
            />
          ))}
        </div>
      </div>
      
      {/* Health & Usage Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ServiceHealthDonut antennas={filteredAntennas} />
        <UsageAreaChart data={usageData} serviceId={selectedServiceId} />
      </div>
      
      {/* Alerts & Top Sites Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AlertsList alerts={filteredAlerts} compact />
        <TopSitesPanel antennas={filteredAntennas} />
      </div>
      
      {/* Antennas Table */}
      <AntennasTable 
        antennas={filteredAntennas}
        expandedRow={expandedRow}
        onExpand={setExpandedRow}
      />
    </div>
  );
};

const ServiceDetailPage = ({ service, antennas, alerts, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('24h');
  const [expandedRow, setExpandedRow] = useState(null);
  
  const serviceAntennas = antennas.filter(a => service.antennas.includes(a.id));
  const avgDown = Math.round(serviceAntennas.reduce((acc, a) => acc + a.downMbps, 0) / serviceAntennas.length);
  const avgUp = Math.round(serviceAntennas.reduce((acc, a) => acc + a.upMbps, 0) / serviceAntennas.length);
  const serviceAlerts = alerts.filter(a => a.serviceId === service.id);
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'antennas', label: 'Antennas' },
    { id: 'performance', label: 'Performance' },
    { id: 'monitoring', label: 'Monitoring & Alerts' },
    { id: 'billing', label: 'Billing Summary' },
  ];
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button onClick={onBack} className="text-slate-500 hover:text-teal-600 transition-colors">Connectivity</button>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="text-slate-500">Services</span>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="text-slate-800 font-medium">{service.name}</span>
      </div>
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800">{service.name}</h1>
            <Badge variant="success">{service.status}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
            <span className="flex items-center gap-1"><User className="w-4 h-4" /> {service.ownerTeamName}</span>
            <span className="flex items-center gap-1"><Server className="w-4 h-4" /> {service.type}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Play}>Run Diagnostics</Button>
          <Button variant="secondary" icon={Settings}>Change Plan</Button>
          <Button variant="primary" icon={MessageSquare}>Open Ticket</Button>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile label="Avg Downlink" value={`${avgDown} Mbps`} icon={ArrowDownRight} color="teal" trend="up" trendValue="+5.2%" />
            <StatTile label="Avg Uplink" value={`${avgUp} Mbps`} icon={ArrowUpRight} color="indigo" trend="up" trendValue="+3.1%" />
            <StatTile label="Avg Latency" value="32 ms" icon={Activity} color="amber" />
            <StatTile label="Data Used" value={`${(service.dataUsedGb / 1000).toFixed(1)} TB`} subValue={`of ${(service.dataPoolSizeGb / 1000).toFixed(0)} TB`} icon={Database} color="emerald" />
            <StatTile label="Antennas" value={serviceAntennas.length} icon={Router} color="rose" />
          </div>
          
          {/* Service Topology */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Service Topology</h3>
            <div className="flex items-center justify-center gap-4 py-6 overflow-x-auto">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center">
                  <User className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-xs text-slate-600 mt-2 font-medium">{service.ownerTeamName}</p>
                <p className="text-[10px] text-slate-400">Owner</p>
              </div>
              <div className="w-12 h-0.5 bg-gradient-to-r from-indigo-200 to-teal-200 rounded-full" />
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center">
                  <Server className="w-8 h-8 text-teal-600" />
                </div>
                <p className="text-xs text-slate-600 mt-2 font-medium">{service.name}</p>
                <p className="text-[10px] text-slate-400">Service</p>
              </div>
              <div className="w-12 h-0.5 bg-gradient-to-r from-teal-200 to-emerald-200 rounded-full" />
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center">
                  <Wifi className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-xs text-slate-600 mt-2 font-medium">{serviceAntennas.length} Antennas</p>
                <p className="text-[10px] text-slate-400">Terminals</p>
              </div>
              <div className="w-12 h-0.5 bg-gradient-to-r from-emerald-200 to-amber-200 rounded-full" />
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-amber-600" />
                </div>
                <p className="text-xs text-slate-600 mt-2 font-medium">{[...new Set(serviceAntennas.map(a => a.siteName))].length} Sites</p>
                <p className="text-[10px] text-slate-400">Locations</p>
              </div>
            </div>
          </Card>
          
          {/* Insights */}
          <Card className="bg-gradient-to-r from-teal-50 to-indigo-50 border-teal-100">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Zap className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Insights</h4>
                <p className="text-sm text-slate-600 mt-1">
                  {serviceAntennas.filter(a => a.status !== 'healthy').length > 0 
                    ? `${serviceAntennas.filter(a => a.status !== 'healthy').length} antenna(s) require attention. Consider running diagnostics or repositioning for better signal.`
                    : 'All antennas are performing optimally. SLA compliance is above target.'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {activeTab === 'antennas' && (
        <AntennasTable 
          antennas={serviceAntennas}
          expandedRow={expandedRow}
          onExpand={setExpandedRow}
        />
      )}
      
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {/* Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PerformanceGauge value={avgDown} max={250} label="Downlink" unit="Mbps" color="teal" />
            <PerformanceGauge value={avgUp} max={50} label="Uplink" unit="Mbps" color="indigo" />
            <PerformanceGauge value={32} max={100} label="Latency" unit="ms" color="amber" />
          </div>
          
          {/* Time Range */}
          <div className="flex justify-end">
            <TimeRangeChips selected={timeRange} onChange={setTimeRange} />
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4">Throughput Over Time</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mockPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <Tooltip />
                    <Line type="monotone" dataKey="downlink" stroke={CHART_COLORS.teal} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="uplink" stroke={CHART_COLORS.indigo} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
            
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4">Latency & Obstruction</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={mockPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <Tooltip />
                    <Area type="monotone" dataKey="latency" stroke={CHART_COLORS.amber} fill={CHART_COLORS.amber} fillOpacity={0.2} strokeWidth={2} />
                    <Area type="monotone" dataKey="obstruction" stroke={CHART_COLORS.rose} fill={CHART_COLORS.rose} fillOpacity={0.2} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {activeTab === 'monitoring' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4">Alert Timeline</h3>
              <div className="space-y-3">
                {['Signal Degradation', 'Connectivity Loss', 'Performance', 'Data Usage'].map((category) => (
                  <div key={category} className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-32 truncate">{category}</span>
                    <div className="flex-1 h-6 bg-slate-50 rounded-lg relative overflow-hidden">
                      {serviceAlerts.filter(a => a.category === category).map((alert, i) => (
                        <div 
                          key={alert.id}
                          className={`absolute top-0.5 bottom-0.5 w-8 rounded ${
                            alert.severity === 'critical' ? 'bg-rose-400' : alert.severity === 'warning' ? 'bg-amber-400' : 'bg-sky-400'
                          }`}
                          style={{ left: `${10 + i * 15}%` }}
                          title={alert.title}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-rose-400" /> Critical</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-400" /> Warning</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-400" /> Info</span>
              </div>
            </Card>
          </div>
          <AlertsList alerts={serviceAlerts} />
        </div>
      )}
      
      {activeTab === 'billing' && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-4">Billing Summary</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-slate-800">Monthly Service Fee</p>
                <p className="text-xs text-slate-500">{service.type} - {serviceAntennas.length} terminals</p>
              </div>
              <p className="text-lg font-semibold text-slate-800">$2,450.00</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="text-sm font-medium text-slate-800">Data Pool</p>
                <p className="text-xs text-slate-500">{(service.dataPoolSizeGb / 1000).toFixed(0)} TB committed</p>
              </div>
              <p className="text-lg font-semibold text-slate-800">$1,200.00</p>
            </div>
            <div className="flex items-center justify-between p-4 bg-teal-50 rounded-xl border border-teal-100">
              <p className="text-sm font-medium text-teal-800">Current Cycle Total</p>
              <p className="text-xl font-bold text-teal-700">$3,650.00</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const FleetMapPage = ({ services, antennas }) => {
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedAntenna, setSelectedAntenna] = useState(null);
  
  const filteredAntennas = selectedServiceId 
    ? antennas.filter(a => services.find(s => s.id === selectedServiceId)?.antennas.includes(a.id))
    : antennas;
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'bg-emerald-500';
      case 'attention': return 'bg-amber-500';
      case 'critical': return 'bg-rose-500';
      default: return 'bg-slate-400';
    }
  };
  
  return (
    <div className="h-[calc(100vh-80px)] flex">
      {/* Left Drawer */}
      <div className="w-72 bg-white border-r border-slate-100 overflow-y-auto">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">Services</h2>
          <p className="text-xs text-slate-500">Select to filter map</p>
        </div>
        <div className="p-3 space-y-2">
          <button
            onClick={() => setSelectedServiceId(null)}
            className={`w-full p-3 rounded-xl text-left transition-all ${!selectedServiceId ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50'}`}
          >
            <p className="text-sm font-medium text-slate-800">All Services</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="success" size="xs">{antennas.filter(a => a.status === 'healthy').length}</Badge>
              <Badge variant="warning" size="xs">{antennas.filter(a => a.status === 'attention').length}</Badge>
              <Badge variant="critical" size="xs">{antennas.filter(a => a.status === 'critical').length}</Badge>
            </div>
          </button>
          {services.map((service) => {
            const sAntennas = antennas.filter(a => service.antennas.includes(a.id));
            return (
              <button
                key={service.id}
                onClick={() => setSelectedServiceId(service.id)}
                className={`w-full p-3 rounded-xl text-left transition-all ${selectedServiceId === service.id ? 'bg-teal-50 border border-teal-200' : 'hover:bg-slate-50'}`}
              >
                <p className="text-sm font-medium text-slate-800">{service.name}</p>
                <p className="text-xs text-slate-500">{service.ownerTeamName}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="success" size="xs">{sAntennas.filter(a => a.status === 'healthy').length}</Badge>
                  <Badge variant="warning" size="xs">{sAntennas.filter(a => a.status === 'attention').length}</Badge>
                  <Badge variant="critical" size="xs">{sAntennas.filter(a => a.status === 'critical').length}</Badge>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Map Canvas */}
      <div className="flex-1 relative bg-gradient-to-br from-slate-100 via-teal-50/30 to-indigo-50/30">
        {/* Map Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#64748b" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        
        {/* Antenna Markers */}
        {filteredAntennas.map((antenna, index) => {
          const x = 15 + ((antenna.longitude + 115) * 3);
          const y = 80 - ((antenna.latitude - 25) * 8);
          return (
            <button
              key={antenna.id}
              className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ${
                selectedAntenna?.id === antenna.id ? 'z-20 scale-125' : 'z-10 hover:scale-110'
              }`}
              style={{ left: `${x}%`, top: `${y}%` }}
              onClick={() => setSelectedAntenna(antenna)}
            >
              <div className="relative">
                <div className={`w-4 h-4 rounded-full ${getStatusColor(antenna.status)} shadow-lg`}>
                  <div className={`absolute inset-0 rounded-full ${getStatusColor(antenna.status)} animate-ping opacity-30`} />
                </div>
              </div>
            </button>
          );
        })}
        
        {/* Selected Antenna Card */}
        {selectedAntenna && (
          <div className="absolute top-4 right-4 w-80 z-30">
            <Card className="shadow-xl">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800">{selectedAntenna.name}</h3>
                  <p className="text-xs text-slate-500">{selectedAntenna.siteName}</p>
                </div>
                <button onClick={() => setSelectedAntenna(null)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <Badge variant={selectedAntenna.status === 'healthy' ? 'success' : selectedAntenna.status === 'attention' ? 'warning' : 'critical'}>
                  {selectedAntenna.status}
                </Badge>
                <span className="text-xs text-slate-500">Last: {selectedAntenna.lastCheckIn}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-2 bg-slate-50 rounded-lg text-center">
                  <p className="text-lg font-semibold text-teal-600">{selectedAntenna.downMbps}</p>
                  <p className="text-[10px] text-slate-500">Mbps Down</p>
                </div>
                <div className="p-2 bg-slate-50 rounded-lg text-center">
                  <p className="text-lg font-semibold text-indigo-600">{selectedAntenna.upMbps}</p>
                  <p className="text-[10px] text-slate-500">Mbps Up</p>
                </div>
              </div>
              
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={mockPerformanceData.slice(0, 12)}>
                    <Area type="monotone" dataKey="downlink" stroke={CHART_COLORS.teal} fill={CHART_COLORS.teal} fillOpacity={0.2} strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              <Button variant="secondary" size="sm" className="w-full mt-3" icon={ExternalLink}>
                Open Service Detail
              </Button>
            </Card>
          </div>
        )}
        
        {/* Map Legend */}
        <div className="absolute bottom-4 left-4">
          <Card padding="p-3" className="shadow-lg">
            <p className="text-xs font-medium text-slate-600 mb-2">Antenna Status</p>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-xs text-slate-600"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Healthy</span>
              <span className="flex items-center gap-1 text-xs text-slate-600"><div className="w-2 h-2 rounded-full bg-amber-500" /> Attention</span>
              <span className="flex items-center gap-1 text-xs text-slate-600"><div className="w-2 h-2 rounded-full bg-rose-500" /> Critical</span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const DataPoolPage = ({ services, antennas }) => {
  const [selectedServiceId, setSelectedServiceId] = useState(services[0]?.id);
  
  const service = services.find(s => s.id === selectedServiceId);
  const serviceAntennas = antennas.filter(a => service?.antennas.includes(a.id));
  const usagePercent = service ? Math.round((service.dataUsedGb / service.dataPoolSizeGb) * 100) : 0;
  
  const dailyData = Array.from({ length: 14 }, (_, i) => ({
    day: `Day ${i + 1}`,
    usage: Math.floor(80 + Math.random() * 60),
  }));
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fixed Data Pool</h1>
          <p className="text-slate-500">Monitor and manage service data allocations</p>
        </div>
        <select
          value={selectedServiceId}
          onChange={(e) => setSelectedServiceId(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
        >
          {services.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      
      {service && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Usage Card */}
          <div className="lg:col-span-2">
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4">Data Pool Usage \u2013 {service.name}</h3>
              
              <div className="flex items-center gap-6 mb-6">
                <div className="text-center">
                  <p className="text-4xl font-bold text-slate-800">{(service.dataUsedGb / 1000).toFixed(1)}</p>
                  <p className="text-sm text-slate-500">TB Used</p>
                </div>
                <div className="text-2xl text-slate-300">of</div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-teal-600">{(service.dataPoolSizeGb / 1000).toFixed(0)}</p>
                  <p className="text-sm text-slate-500">TB Pool</p>
                </div>
              </div>
              
              {/* Threshold Bar */}
              <div className="relative">
                <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      usagePercent > 80 ? 'bg-gradient-to-r from-amber-400 to-rose-500' : 'bg-gradient-to-r from-teal-400 to-teal-500'
                    }`}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
                {/* Current Usage Indicator Dot */}
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
                  style={{ left: '65%', transform: 'translate(-50%, -50%)' }}
                >
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                {/* Threshold Markers */}
                <div className="absolute top-0 left-[60%] w-px h-4 bg-amber-400" />
                <div className="absolute top-0 left-[80%] w-px h-4 bg-rose-400" />
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>0%</span>
                  <span className="text-amber-600">60%</span>
                  <span className="absolute text-emerald-600 font-medium" style={{ left: '65%', transform: 'translateX(-50%)' }}>9.6 TB</span>
                  <span className="text-rose-600">80%</span>
                  <span>100%</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-sky-50 to-teal-50 rounded-xl border border-sky-100">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-sky-600" />
                  <p className="text-sm text-slate-700">
                    You've increased usage by <span className="font-medium text-sky-600">18%</span> compared to your 14-day average.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button variant="secondary" icon={Download}>Download CSV</Button>
                <Button variant="secondary" icon={Settings}>Adjust Pool</Button>
                <Button variant="primary" icon={TrendingUp}>Recommend Upgrade</Button>
              </div>
            </Card>
          </div>
          
          {/* Daily Usage Chart */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Daily Usage</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="usage" fill={CHART_COLORS.teal} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
      
      {/* Site Breakdown Table */}
      <Card padding="p-0">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Usage by Site</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Site</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Antennas</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Used (24h)</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Total This Cycle</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">% of Pool</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...new Set(serviceAntennas.map(a => a.siteName))].map((site) => {
                const siteAntennas = serviceAntennas.filter(a => a.siteName === site);
                const sitePercent = Math.floor(Math.random() * 40) + 10;
                return (
                  <tr key={site} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">{site}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-slate-600">{siteAntennas.length}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-teal-600 font-medium">{(Math.random() * 50 + 20).toFixed(1)} GB</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-slate-600">{(Math.random() * 800 + 200).toFixed(0)} GB</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full" style={{ width: `${sitePercent}%` }} />
                        </div>
                        <span className="text-xs text-slate-500">{sitePercent}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const AlertsPage = ({ alerts, services, antennas }) => {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  
  const filteredAlerts = alerts.filter(a => 
    (severityFilter === 'all' || a.severity === severityFilter) &&
    (serviceFilter === 'all' || a.serviceId === serviceFilter)
  );
  
  const severityIcons = {
    critical: <XCircle className="w-5 h-5 text-rose-500" />,
    warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
    info: <CheckCircle className="w-5 h-5 text-sky-500" />,
  };
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Alerts & Monitoring</h1>
          <p className="text-slate-500">System-wide alert management and monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          >
            <option value="all">All Services</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile 
          label="Critical Alerts" 
          value={alerts.filter(a => a.severity === 'critical' && a.status === 'active').length} 
          icon={XCircle} 
          color="rose" 
        />
        <StatTile 
          label="Warning Alerts" 
          value={alerts.filter(a => a.severity === 'warning' && a.status === 'active').length} 
          icon={AlertTriangle} 
          color="amber" 
        />
        <StatTile 
          label="Info Notices" 
          value={alerts.filter(a => a.severity === 'info' && a.status === 'active').length} 
          icon={CheckCircle} 
          color="teal" 
        />
        <StatTile 
          label="Resolved Today" 
          value={alerts.filter(a => a.status === 'resolved').length} 
          icon={CheckCircle} 
          color="emerald" 
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts List */}
        <div className="lg:col-span-2">
          <Card padding="p-0">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">All Alerts</h3>
              <Badge variant="info">{filteredAlerts.length} Total</Badge>
            </div>
            <div className="divide-y divide-slate-100">
              {filteredAlerts.map((alert) => {
                const service = services.find(s => s.id === alert.serviceId);
                const antenna = antennas.find(a => a.id === alert.antennaId);
                return (
                  <div 
                    key={alert.id}
                    onClick={() => setSelectedAlert(alert)}
                    className={`flex items-start gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                      selectedAlert?.id === alert.id ? 'bg-teal-50/50' : ''
                    }`}
                  >
                    {severityIcons[alert.severity]}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-slate-800">{alert.title}</p>
                        <Badge variant={alert.status === 'active' ? 'warning' : 'success'} size="xs">
                          {alert.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{alert.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1"><Server className="w-3 h-3" /> {service?.name}</span>
                        {antenna && <span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> {antenna.name}</span>}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {alert.createdAt}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
        
        {/* Alert Detail Panel */}
        <Card className={`transition-all ${selectedAlert ? 'opacity-100' : 'opacity-50'}`}>
          {selectedAlert ? (
            <>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {severityIcons[selectedAlert.severity]}
                  <div>
                    <h3 className="font-semibold text-slate-800">{selectedAlert.title}</h3>
                    <Badge variant={selectedAlert.severity === 'critical' ? 'critical' : selectedAlert.severity === 'warning' ? 'warning' : 'info'}>
                      {selectedAlert.category}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-slate-600 mb-4">{selectedAlert.description}</p>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-xs text-slate-500">Service</span>
                  <span className="text-sm font-medium text-slate-700">
                    {services.find(s => s.id === selectedAlert.serviceId)?.name}
                  </span>
                </div>
                {selectedAlert.antennaId && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">Antenna</span>
                    <span className="text-sm font-medium text-slate-700">
                      {antennas.find(a => a.id === selectedAlert.antennaId)?.name}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-xs text-slate-500">Created</span>
                  <span className="text-sm font-medium text-slate-700">{selectedAlert.createdAt}</span>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-100 mb-4">
                <p className="text-xs font-medium text-amber-800 mb-1">Recommended Action</p>
                <p className="text-xs text-amber-700">
                  {selectedAlert.severity === 'critical' 
                    ? 'Immediate attention required. Consider running diagnostics or escalating to NOC.'
                    : 'Monitor the situation. Schedule maintenance if issue persists.'}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" icon={Wrench} className="flex-1">Diagnose</Button>
                <Button variant="primary" size="sm" icon={CheckCircle} className="flex-1">Resolve</Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Select an alert to view details</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ============================================================================
// BILLING PAGE
// ============================================================================

const BillingPage = ({ services, accounts, invoices, transactions, billingByService, billingByLocation, billingHistory }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'transactions', label: 'Transaction History' },
    { id: 'by-service', label: 'By Service' },
    { id: 'by-location', label: 'By Location' },
    { id: 'by-account', label: 'By Account' },
  ];
  
  // Calculate totals
  const totalOutstanding = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.total, 0);
  const totalPaidThisMonth = invoices.filter(i => i.status === 'paid' && i.date.startsWith('2024-12')).reduce((sum, i) => sum + i.total, 0);
  const overdueAmount = invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.total, 0);
  const pendingAmount = invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.total, 0);
  
  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    if (selectedAccount !== 'all' && inv.accountId !== selectedAccount) return false;
    if (invoiceFilter !== 'all' && inv.status !== invoiceFilter) return false;
    return true;
  });
  
  // Filter transactions
  const filteredTransactions = selectedAccount === 'all' 
    ? transactions 
    : transactions.filter(t => t.accountId === selectedAccount);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <Badge variant="success">Paid</Badge>;
      case 'pending': return <Badge variant="warning">Pending</Badge>;
      case 'overdue': return <Badge variant="critical">Overdue</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Billing & Invoices</h1>
          <p className="text-slate-500">Manage billing, view invoices, and track payments</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
          >
            <option value="all">All Accounts</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
          <Button variant="secondary" icon={Download}>Export</Button>
          <Button variant="primary" icon={CreditCard}>Make Payment</Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile 
          label="Total Outstanding" 
          value={formatCurrency(totalOutstanding)} 
          icon={DollarSign} 
          color="teal"
        />
        <StatTile 
          label="Paid This Month" 
          value={formatCurrency(totalPaidThisMonth)} 
          icon={CheckCircle} 
          color="emerald"
        />
        <StatTile 
          label="Pending" 
          value={formatCurrency(pendingAmount)} 
          icon={Clock} 
          color="amber"
        />
        <StatTile 
          label="Overdue" 
          value={formatCurrency(overdueAmount)} 
          icon={AlertTriangle} 
          color="rose"
        />
      </div>
      
      {/* Tabs */}
      <div className="overflow-x-auto">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Billing Trend Chart */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Billing Trend (12 Months)</h3>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-teal-500" /> Services</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-indigo-500" /> Data</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-amber-500" /> Overage</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={billingHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="services" stackId="a" fill="#14b8a6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="data" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="overage" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          
          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Invoices */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Recent Invoices</h3>
                <button onClick={() => setActiveTab('invoices')} className="text-sm text-teal-600 hover:text-teal-700">View All</button>
              </div>
              <div className="space-y-3">
                {invoices.slice(0, 4).map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{invoice.invoiceNumber}</p>
                        <p className="text-xs text-slate-500">{invoice.date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">{formatCurrency(invoice.total)}</p>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Recent Transactions */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Recent Transactions</h3>
                <button onClick={() => setActiveTab('transactions')} className="text-sm text-teal-600 hover:text-teal-700">View All</button>
              </div>
              <div className="space-y-3">
                {transactions.slice(0, 4).map((txn) => (
                  <div key={txn.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        txn.type === 'payment' ? 'bg-emerald-50' : txn.type === 'credit' ? 'bg-sky-50' : 'bg-amber-50'
                      }`}>
                        {txn.type === 'payment' ? <ArrowDownRight className="w-5 h-5 text-emerald-600" /> :
                         txn.type === 'credit' ? <ArrowDownRight className="w-5 h-5 text-sky-600" /> :
                         <ArrowUpRight className="w-5 h-5 text-amber-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800 truncate max-w-[180px]">{txn.description}</p>
                        <p className="text-xs text-slate-500">{txn.date}</p>
                      </div>
                    </div>
                    <p className={`text-sm font-semibold ${txn.amount < 0 ? 'text-emerald-600' : 'text-slate-800'}`}>
                      {txn.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(txn.amount))}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          
          {/* Account Summary */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Account Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Account</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Type</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Payment Terms</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Credit Limit</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Current Balance</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accounts.map((account) => (
                    <tr key={account.id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">{account.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="info" size="xs">{account.type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{account.paymentMethod}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatCurrency(account.creditLimit)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">{formatCurrency(account.currentBalance)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={account.currentBalance < account.creditLimit * 0.8 ? 'success' : 'warning'}>
                          {account.currentBalance < account.creditLimit * 0.8 ? 'Good Standing' : 'Near Limit'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
      
      {activeTab === 'invoices' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <select
              value={invoiceFilter}
              onChange={(e) => setInvoiceFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" icon={Filter}>More Filters</Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Invoice List */}
            <div className="lg:col-span-2">
              <Card padding="p-0">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Invoices</h3>
                  <Badge variant="info">{filteredInvoices.length} Total</Badge>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredInvoices.map((invoice) => {
                    const account = accounts.find(a => a.id === invoice.accountId);
                    return (
                      <div 
                        key={invoice.id}
                        onClick={() => setSelectedInvoice(invoice)}
                        className={`flex items-center gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                          selectedInvoice?.id === invoice.id ? 'bg-teal-50/50' : ''
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-slate-800">{invoice.invoiceNumber}</p>
                            {getStatusBadge(invoice.status)}
                          </div>
                          <p className="text-xs text-slate-500">{account?.name} • Due: {invoice.dueDate}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-slate-800">{formatCurrency(invoice.total)}</p>
                          <p className="text-xs text-slate-500">{invoice.date}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
            
            {/* Invoice Detail */}
            <Card className={`transition-all ${selectedInvoice ? 'opacity-100' : 'opacity-50'}`}>
              {selectedInvoice ? (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-800">{selectedInvoice.invoiceNumber}</h3>
                      <p className="text-xs text-slate-500">{accounts.find(a => a.id === selectedInvoice.accountId)?.name}</p>
                    </div>
                    {getStatusBadge(selectedInvoice.status)}
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-xs text-slate-500">Invoice Date</span>
                      <span className="text-sm font-medium text-slate-700">{selectedInvoice.date}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-xs text-slate-500">Due Date</span>
                      <span className="text-sm font-medium text-slate-700">{selectedInvoice.dueDate}</span>
                    </div>
                    {selectedInvoice.paidDate && (
                      <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                        <span className="text-xs text-emerald-600">Paid Date</span>
                        <span className="text-sm font-medium text-emerald-700">{selectedInvoice.paidDate}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-slate-100 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-700">{formatCurrency(selectedInvoice.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Tax (8%)</span>
                      <span className="text-slate-700">{formatCurrency(selectedInvoice.tax)}</span>
                    </div>
                    <div className="flex justify-between text-base font-semibold pt-2 border-t border-slate-100">
                      <span className="text-slate-800">Total</span>
                      <span className="text-teal-600">{formatCurrency(selectedInvoice.total)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button variant="secondary" size="sm" icon={Eye} className="flex-1">View PDF</Button>
                    <Button variant="secondary" size="sm" icon={Printer} className="flex-1">Print</Button>
                  </div>
                  {selectedInvoice.status !== 'paid' && (
                    <Button variant="primary" size="sm" icon={CreditCard} className="w-full mt-2">Pay Now</Button>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Select an invoice to view details</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
      
      {activeTab === 'transactions' && (
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Transaction History</h3>
            <Button variant="ghost" size="sm" icon={Download}>Export CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Date</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Description</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Reference</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Method</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Amount</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3 text-sm text-slate-600">{txn.date}</td>
                    <td className="px-5 py-3">
                      <Badge variant={txn.type === 'payment' ? 'success' : txn.type === 'credit' ? 'info' : 'warning'} size="xs">
                        {txn.type}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-800">{txn.description}</td>
                    <td className="px-5 py-3">
                      <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600">{txn.reference}</code>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-500">{txn.method || '-'}</td>
                    <td className={`px-5 py-3 text-sm font-medium text-right ${txn.amount < 0 ? 'text-emerald-600' : 'text-slate-800'}`}>
                      {txn.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(txn.amount))}
                    </td>
                    <td className="px-5 py-3 text-sm font-medium text-slate-800 text-right">{formatCurrency(txn.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      
      {activeTab === 'by-service' && (
        <div className="space-y-6">
          {/* Service Billing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {billingByService.map((item) => (
              <Card key={item.serviceId} className="hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-800">{item.serviceName}</h3>
                    <p className="text-xs text-slate-500">{accounts.find(a => a.id === item.accountId)?.name}</p>
                  </div>
                  <p className="text-xl font-bold text-teal-600">{formatCurrency(item.total)}</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Monthly Base</span>
                    <span className="text-slate-700">{formatCurrency(item.monthlyBase)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Data Charges</span>
                    <span className="text-slate-700">{formatCurrency(item.dataCharges)}</span>
                  </div>
                  {item.overageCharges > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-600">Overage</span>
                      <span className="text-amber-600">{formatCurrency(item.overageCharges)}</span>
                    </div>
                  )}
                  {item.supportCharges > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Support</span>
                      <span className="text-slate-700">{formatCurrency(item.supportCharges)}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div className="bg-teal-500" style={{ width: `${(item.monthlyBase / item.total) * 100}%` }} />
                      <div className="bg-indigo-500" style={{ width: `${(item.dataCharges / item.total) * 100}%` }} />
                      <div className="bg-amber-500" style={{ width: `${(item.overageCharges / item.total) * 100}%` }} />
                      <div className="bg-slate-400" style={{ width: `${(item.supportCharges / item.total) * 100}%` }} />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          {/* Service Comparison Chart */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Service Cost Comparison</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={billingByService} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `$${v}`} />
                  <YAxis type="category" dataKey="serviceName" tick={{ fontSize: 10 }} stroke="#94a3b8" width={150} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="monthlyBase" stackId="a" fill="#14b8a6" name="Base" />
                  <Bar dataKey="dataCharges" stackId="a" fill="#6366f1" name="Data" />
                  <Bar dataKey="overageCharges" stackId="a" fill="#f59e0b" name="Overage" />
                  <Bar dataKey="supportCharges" stackId="a" fill="#94a3b8" name="Support" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
      
      {activeTab === 'by-location' && (
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">Billing by Location</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Location</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Service</th>
                  <th className="text-center text-xs font-medium text-slate-500 px-5 py-3">Antennas</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Monthly Base</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Data Usage</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Data Charges</th>
                  <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {billingByLocation.map((item, index) => {
                  const service = services.find(s => s.id === item.serviceId);
                  return (
                    <tr key={index} className="hover:bg-slate-50/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">{item.location}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-600">{service?.name}</td>
                      <td className="px-5 py-3 text-center">
                        <Badge variant="teal" size="xs">{item.antennaCount}</Badge>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-600 text-right">{formatCurrency(item.monthlyCharges)}</td>
                      <td className="px-5 py-3 text-sm text-slate-600 text-right">{item.dataUsageGb.toLocaleString()} GB</td>
                      <td className="px-5 py-3 text-sm text-slate-600 text-right">{formatCurrency(item.dataCharges)}</td>
                      <td className="px-5 py-3 text-sm font-semibold text-teal-600 text-right">
                        {formatCurrency(item.monthlyCharges + item.dataCharges)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50">
                  <td colSpan="3" className="px-5 py-3 text-sm font-semibold text-slate-800">Total</td>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-800 text-right">
                    {formatCurrency(billingByLocation.reduce((sum, i) => sum + i.monthlyCharges, 0))}
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-800 text-right">
                    {billingByLocation.reduce((sum, i) => sum + i.dataUsageGb, 0).toLocaleString()} GB
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-slate-800 text-right">
                    {formatCurrency(billingByLocation.reduce((sum, i) => sum + i.dataCharges, 0))}
                  </td>
                  <td className="px-5 py-3 text-sm font-bold text-teal-600 text-right">
                    {formatCurrency(billingByLocation.reduce((sum, i) => sum + i.monthlyCharges + i.dataCharges, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
      
      {activeTab === 'by-account' && (
        <div className="space-y-6">
          {accounts.map((account) => {
            const accountInvoices = invoices.filter(i => i.accountId === account.id);
            const accountTransactions = transactions.filter(t => t.accountId === account.id);
            const accountServices = billingByService.filter(b => b.accountId === account.id);
            const totalMonthly = accountServices.reduce((sum, s) => sum + s.total, 0);
            
            return (
              <Card key={account.id}>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-100 to-indigo-100 flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{account.name}</h3>
                      <p className="text-sm text-slate-500">{account.type} Account • {account.paymentMethod}</p>
                      <p className="text-xs text-slate-400">{account.billingAddress}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(account.currentBalance)}</p>
                    <p className="text-xs text-slate-500">Current Balance</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Monthly Recurring</p>
                    <p className="text-lg font-semibold text-slate-800">{formatCurrency(totalMonthly)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Services</p>
                    <p className="text-lg font-semibold text-slate-800">{accountServices.length}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Open Invoices</p>
                    <p className="text-lg font-semibold text-slate-800">{accountInvoices.filter(i => i.status !== 'paid').length}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Credit Available</p>
                    <p className="text-lg font-semibold text-emerald-600">{formatCurrency(account.creditLimit - account.currentBalance)}</p>
                  </div>
                </div>
                
                {/* Services under this account */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Active Services</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {accountServices.map((svc) => (
                      <div key={svc.serviceId} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Server className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700">{svc.serviceName}</span>
                        </div>
                        <span className="text-sm font-medium text-teal-600">{formatCurrency(svc.total)}/mo</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Recent invoices for this account */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Recent Invoices</h4>
                  <div className="space-y-2">
                    {accountInvoices.slice(0, 3).map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Receipt className="w-4 h-4 text-slate-400" />
                          <div>
                            <span className="text-sm text-slate-700">{inv.invoiceNumber}</span>
                            <span className="text-xs text-slate-400 ml-2">{inv.date}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-slate-800">{formatCurrency(inv.total)}</span>
                          {getStatusBadge(inv.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// V4 ENHANCED DRUPAL 2.0-STYLE PLATFORM ADMINISTRATION HUB
// ============================================================================

const PlatformAdminHub = ({ retailerSites, retailerUsers, platformActivity, platformStats, roles, templates, brandingPresets, emailTemplates }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedSite, setSelectedSite] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showLaunchWizard, setShowLaunchWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    template: null,
    name: '',
    slug: '',
    industry: '',
    region: '',
    tier: 'Business',
    customDomain: false,
    domain: '',
    primaryColor: '#0066CC',
    secondaryColor: '#004499',
    accentColor: '#00A651',
    headerStyle: 'gradient',
    fontFamily: 'Inter',
    features: {
      billing: true,
      fleetMap: true,
      alerts: true,
      support: true,
      dataPools: true,
      apiAccess: false,
      customReports: false,
      whiteLabel: false,
      sso: false,
      mfa: true,
    },
    adminEmail: '',
    adminName: '',
    sendWelcome: true,
  });
  
  const sites = retailerSites || v4RetailerMicrosites;
  const users = retailerUsers || v4RetailerUsers;
  const activity = platformActivity || v4PlatformActivity;
  const stats = platformStats || v4PlatformStats;
  const siteRoles = roles || v4SiteRolesDetailed;
  const availableTemplates = templates || siteTemplates;
  const presets = brandingPresets || v4BrandingPresets;
  
  const tabs = [
    { id: 'dashboard', label: 'Platform Dashboard', icon: PanelLeft },
    { id: 'sites', label: 'All Microsites', icon: Globe2, badge: stats.totalMicrosites },
    { id: 'users', label: 'All Users', icon: Users, badge: stats.totalRetailerUsers },
    { id: 'templates', label: 'Site Templates', icon: Layout },
    { id: 'branding', label: 'Branding Studio', icon: Palette },
    { id: 'activity', label: 'Global Activity', icon: Activity },
    { id: 'settings', label: 'Platform Settings', icon: Settings },
  ];
  
  const filteredSites = sites.filter(site => {
    const matchesStatus = filterStatus === 'all' || site.status === filterStatus;
    const matchesSearch = !searchQuery || 
      site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.domain.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  
  const getStatusBadge = (status) => {
    const variants = { active: 'success', pending: 'warning', suspended: 'critical' };
    const labels = { active: 'Active', pending: 'Pending Setup', suspended: 'Suspended' };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };
  
  const getTierBadge = (tier) => {
    const variants = { Enterprise: 'indigo', Business: 'teal', Starter: 'default' };
    return <Badge variant={variants[tier] || 'default'}>{tier}</Badge>;
  };
  
  const getRoleBadge = (roleId) => {
    const role = siteRoles.find(r => r.id === roleId);
    if (!role) return <Badge>{roleId}</Badge>;
    const colorMap = { rose: 'critical', amber: 'warning', indigo: 'indigo', teal: 'teal', sky: 'info', slate: 'default' };
    return <Badge variant={colorMap[role.color] || 'default'}>{role.name}</Badge>;
  };
  
  const handleWizardNext = () => {
    if (wizardStep < 6) setWizardStep(wizardStep + 1);
  };
  
  const handleWizardBack = () => {
    if (wizardStep > 1) setWizardStep(wizardStep - 1);
  };
  
  const handleLaunchSite = () => {
    // In production, this would call an API
    alert('Microsite launch initiated! In production, this would create the site and send welcome email.');
    setShowLaunchWizard(false);
    setWizardStep(1);
    setWizardData({
      template: null, name: '', slug: '', industry: '', region: '', tier: 'Business',
      customDomain: false, domain: '', primaryColor: '#0066CC', secondaryColor: '#004499', accentColor: '#00A651',
      headerStyle: 'gradient', fontFamily: 'Inter',
      features: { billing: true, fleetMap: true, alerts: true, support: true, dataPools: true, apiAccess: false, customReports: false, whiteLabel: false, sso: false, mfa: true },
      adminEmail: '', adminName: '', sendWelcome: true,
    });
  };
  
  const applyBrandingPreset = (preset) => {
    setWizardData({ ...wizardData, primaryColor: preset.colors.primary, secondaryColor: preset.colors.secondary, accentColor: preset.colors.accent });
  };
  
  // Wizard Steps Render
  const renderWizardStep = () => {
    switch (wizardStep) {
      case 1: // Template Selection
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Select a Site Template</h3>
              <p className="text-slate-500">Choose a template that best fits your retailer's needs. You can customize everything later.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableTemplates.map((template) => (
                <div 
                  key={template.id}
                  onClick={() => setWizardData({ ...wizardData, template: template })}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    wizardData.template?.id === template.id 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      template.thumbnail === 'enterprise' ? 'bg-indigo-100' :
                      template.thumbnail === 'smb' ? 'bg-teal-100' :
                      template.thumbnail === 'maritime' ? 'bg-sky-100' : 'bg-slate-100'
                    }`}>
                      {template.thumbnail === 'enterprise' ? <Building2 className="w-6 h-6 text-indigo-600" /> :
                       template.thumbnail === 'smb' ? <Briefcase className="w-6 h-6 text-teal-600" /> :
                       template.thumbnail === 'maritime' ? <Ship className="w-6 h-6 text-sky-600" /> :
                       <Eye className="w-6 h-6 text-slate-600" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-800">{template.name}</h4>
                      <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.features.slice(0, 4).map((f) => (
                          <span key={f} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{f}</span>
                        ))}
                        {template.features.length > 4 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">+{template.features.length - 4}</span>
                        )}
                      </div>
                    </div>
                    {wizardData.template?.id === template.id && (
                      <CheckCircle className="w-6 h-6 text-indigo-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 2: // Site Details
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Site Details</h3>
              <p className="text-slate-500">Enter the basic information for this retailer's microsite.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Retailer Name *</label>
                <input 
                  type="text" 
                  value={wizardData.name}
                  onChange={(e) => setWizardData({ ...wizardData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                  placeholder="e.g., ACME Corporation"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site Slug *</label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-slate-500 text-sm">libertystarlink.com/</span>
                  <input 
                    type="text" 
                    value={wizardData.slug}
                    onChange={(e) => setWizardData({ ...wizardData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="acme"
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-r-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
                <select 
                  value={wizardData.industry}
                  onChange={(e) => setWizardData({ ...wizardData, industry: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select industry...</option>
                  <option value="Enterprise Technology">Enterprise Technology</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Maritime & Shipping">Maritime & Shipping</option>
                  <option value="Energy & Utilities">Energy & Utilities</option>
                  <option value="Construction">Construction</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Region</label>
                <select 
                  value={wizardData.region}
                  onChange={(e) => setWizardData({ ...wizardData, region: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select region...</option>
                  <option value="North America">North America</option>
                  <option value="South America">South America</option>
                  <option value="Europe">Europe</option>
                  <option value="Asia Pacific">Asia Pacific</option>
                  <option value="Middle East">Middle East</option>
                  <option value="Africa">Africa</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Tier</label>
                <div className="flex gap-2">
                  {['Enterprise', 'Business', 'Starter'].map((tier) => (
                    <button 
                      key={tier}
                      onClick={() => setWizardData({ ...wizardData, tier })}
                      className={`flex-1 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                        wizardData.tier === tier 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                          : 'border-slate-200 hover:border-indigo-300'
                      }`}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Custom Domain</label>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setWizardData({ ...wizardData, customDomain: !wizardData.customDomain })}
                    className={`relative w-12 h-6 rounded-full transition-all ${wizardData.customDomain ? 'bg-indigo-500' : 'bg-slate-200'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${wizardData.customDomain ? 'left-7' : 'left-1'}`} />
                  </button>
                  {wizardData.customDomain && (
                    <input 
                      type="text" 
                      value={wizardData.domain}
                      onChange={(e) => setWizardData({ ...wizardData, domain: e.target.value })}
                      placeholder="portal.example.com"
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 3: // Branding
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Branding & Theme</h3>
              <p className="text-slate-500">Customize the look and feel of the retailer's portal.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Quick Presets</label>
                  <div className="grid grid-cols-3 gap-2">
                    {presets.map((preset) => (
                      <button 
                        key={preset.id}
                        onClick={() => applyBrandingPreset(preset)}
                        className="p-2 rounded-xl border border-slate-200 hover:border-indigo-300 transition-all"
                      >
                        <div className="flex gap-1 mb-1">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.colors.primary }} />
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.colors.secondary }} />
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.colors.accent }} />
                        </div>
                        <p className="text-xs text-slate-600 truncate">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Primary</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={wizardData.primaryColor}
                        onChange={(e) => setWizardData({ ...wizardData, primaryColor: e.target.value })}
                        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
                      />
                      <input 
                        type="text" 
                        value={wizardData.primaryColor}
                        onChange={(e) => setWizardData({ ...wizardData, primaryColor: e.target.value })}
                        className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Secondary</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={wizardData.secondaryColor}
                        onChange={(e) => setWizardData({ ...wizardData, secondaryColor: e.target.value })}
                        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
                      />
                      <input 
                        type="text" 
                        value={wizardData.secondaryColor}
                        onChange={(e) => setWizardData({ ...wizardData, secondaryColor: e.target.value })}
                        className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Accent</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={wizardData.accentColor}
                        onChange={(e) => setWizardData({ ...wizardData, accentColor: e.target.value })}
                        className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
                      />
                      <input 
                        type="text" 
                        value={wizardData.accentColor}
                        onChange={(e) => setWizardData({ ...wizardData, accentColor: e.target.value })}
                        className="flex-1 px-2 py-1 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Header Style</label>
                  <div className="flex gap-2">
                    {['gradient', 'solid', 'transparent'].map((style) => (
                      <button 
                        key={style}
                        onClick={() => setWizardData({ ...wizardData, headerStyle: style })}
                        className={`flex-1 px-4 py-2 rounded-xl border text-sm font-medium capitalize transition-all ${
                          wizardData.headerStyle === style 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Font Family</label>
                  <select 
                    value={wizardData.fontFamily}
                    onChange={(e) => setWizardData({ ...wizardData, fontFamily: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Nunito">Nunito</option>
                  </select>
                </div>
              </div>
              
              {/* Live Preview */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Live Preview</label>
                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-lg">
                  <div 
                    className="p-4"
                    style={{ 
                      background: wizardData.headerStyle === 'gradient' 
                        ? `linear-gradient(135deg, ${wizardData.primaryColor}, ${wizardData.secondaryColor})`
                        : wizardData.headerStyle === 'solid' 
                        ? wizardData.primaryColor
                        : 'transparent',
                      borderBottom: wizardData.headerStyle === 'transparent' ? `2px solid ${wizardData.primaryColor}` : 'none'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                          <Globe className={`w-5 h-5 ${wizardData.headerStyle === 'transparent' ? 'text-slate-800' : 'text-white'}`} />
                        </div>
                        <span className={`font-semibold ${wizardData.headerStyle === 'transparent' ? 'text-slate-800' : 'text-white'}`} style={{ fontFamily: wizardData.fontFamily }}>
                          {wizardData.name || 'Retailer Portal'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${wizardData.headerStyle === 'transparent' ? 'bg-slate-100' : 'bg-white/20'}`}>
                          <Bell className={`w-4 h-4 ${wizardData.headerStyle === 'transparent' ? 'text-slate-600' : 'text-white'}`} />
                        </div>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${wizardData.headerStyle === 'transparent' ? 'bg-slate-100' : 'bg-white/20'}`}>
                          <User className={`w-4 h-4 ${wizardData.headerStyle === 'transparent' ? 'text-slate-600' : 'text-white'}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-3 bg-white rounded-xl border border-slate-100">
                        <p className="text-xs text-slate-500">Services</p>
                        <p className="text-lg font-bold" style={{ color: wizardData.primaryColor }}>4</p>
                      </div>
                      <div className="p-3 bg-white rounded-xl border border-slate-100">
                        <p className="text-xs text-slate-500">Antennas</p>
                        <p className="text-lg font-bold" style={{ color: wizardData.primaryColor }}>12</p>
                      </div>
                      <div className="p-3 bg-white rounded-xl border border-slate-100">
                        <p className="text-xs text-slate-500">Alerts</p>
                        <p className="text-lg font-bold" style={{ color: wizardData.accentColor }}>2</p>
                      </div>
                    </div>
                    <button 
                      className="w-full py-2 rounded-xl text-white text-sm font-medium"
                      style={{ backgroundColor: wizardData.primaryColor }}
                    >
                      View Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 4: // Features
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Enable Features</h3>
              <p className="text-slate-500">Select which modules and features will be available on this microsite.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { id: 'billing', name: 'Billing & Invoices', desc: 'View and download invoices, payment history', icon: CreditCard },
                { id: 'fleetMap', name: 'Fleet Map', desc: 'Interactive map showing all terminals', icon: Map },
                { id: 'alerts', name: 'Alerts & Notifications', desc: 'Real-time alerts and notification center', icon: Bell },
                { id: 'support', name: 'Support Tickets', desc: 'Create and track support tickets', icon: MessageSquare },
                { id: 'dataPools', name: 'Data Pools', desc: 'Monitor data usage and pool allocation', icon: Database },
                { id: 'apiAccess', name: 'API Access', desc: 'REST API for integrations', icon: Link, premium: true },
                { id: 'customReports', name: 'Custom Reports', desc: 'Generate custom analytics reports', icon: FileText, premium: true },
                { id: 'whiteLabel', name: 'White Label', desc: 'Remove Liberty branding completely', icon: Palette, premium: true },
                { id: 'sso', name: 'SSO Integration', desc: 'SAML/OIDC single sign-on', icon: Key, premium: true },
                { id: 'mfa', name: 'Multi-Factor Auth', desc: 'Require MFA for all users', icon: Shield },
              ].map((feature) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={feature.id}
                    onClick={() => setWizardData({ 
                      ...wizardData, 
                      features: { ...wizardData.features, [feature.id]: !wizardData.features[feature.id] }
                    })}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      wizardData.features[feature.id]
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        wizardData.features[feature.id] ? 'bg-indigo-100' : 'bg-slate-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${wizardData.features[feature.id] ? 'text-indigo-600' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-800">{feature.name}</h4>
                          {feature.premium && <Badge variant="indigo">Premium</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">{feature.desc}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        wizardData.features[feature.id] 
                          ? 'border-indigo-500 bg-indigo-500' 
                          : 'border-slate-300'
                      }`}>
                        {wizardData.features[feature.id] && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      
      case 5: // Initial Admin
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Site Administrator</h3>
              <p className="text-slate-500">Set up the initial administrator account for this microsite.</p>
            </div>
            
            <div className="max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin Name *</label>
                <input 
                  type="text" 
                  value={wizardData.adminName}
                  onChange={(e) => setWizardData({ ...wizardData, adminName: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Admin Email *</label>
                <input 
                  type="email" 
                  value={wizardData.adminEmail}
                  onChange={(e) => setWizardData({ ...wizardData, adminEmail: e.target.value })}
                  placeholder="admin@example.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <button 
                  onClick={() => setWizardData({ ...wizardData, sendWelcome: !wizardData.sendWelcome })}
                  className={`relative w-12 h-6 rounded-full transition-all ${wizardData.sendWelcome ? 'bg-indigo-500' : 'bg-slate-200'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${wizardData.sendWelcome ? 'left-7' : 'left-1'}`} />
                </button>
                <div>
                  <p className="font-medium text-slate-800">Send Welcome Email</p>
                  <p className="text-sm text-slate-500">Administrator will receive login instructions</p>
                </div>
              </div>
              
              <div className="p-4 border border-slate-200 rounded-xl">
                <h4 className="font-medium text-slate-800 mb-2">Administrator Permissions</h4>
                <div className="space-y-2">
                  {['Manage all site users', 'Configure site branding', 'Enable/disable features', 'View all billing information', 'Access activity logs'].map((perm) => (
                    <div key={perm} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      {perm}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      
      case 6: // Review & Launch
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Review & Launch</h3>
              <p className="text-slate-500">Review your microsite configuration before launching.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Card>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" /> Site Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-medium">{wizardData.name || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Slug:</span><span className="font-medium">{wizardData.slug || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Industry:</span><span className="font-medium">{wizardData.industry || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Region:</span><span className="font-medium">{wizardData.region || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Tier:</span>{getTierBadge(wizardData.tier)}</div>
                    <div className="flex justify-between"><span className="text-slate-500">Template:</span><span className="font-medium">{wizardData.template?.name || '-'}</span></div>
                  </div>
                </Card>
                
                <Card>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-600" /> Branding
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Colors:</span>
                      <div className="flex gap-1">
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: wizardData.primaryColor }} title="Primary" />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: wizardData.secondaryColor }} title="Secondary" />
                        <div className="w-6 h-6 rounded" style={{ backgroundColor: wizardData.accentColor }} title="Accent" />
                      </div>
                    </div>
                    <div className="flex justify-between"><span className="text-slate-500">Header:</span><span className="font-medium capitalize">{wizardData.headerStyle}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Font:</span><span className="font-medium">{wizardData.fontFamily}</span></div>
                  </div>
                </Card>
              </div>
              
              <div className="space-y-4">
                <Card>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <ToggleRight className="w-5 h-5 text-teal-600" /> Enabled Features
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(wizardData.features).filter(([_, enabled]) => enabled).map(([feature]) => (
                      <Badge key={feature} variant="success">{feature}</Badge>
                    ))}
                  </div>
                </Card>
                
                <Card>
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-sky-600" /> Site Administrator
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-medium">{wizardData.adminName || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Email:</span><span className="font-medium">{wizardData.adminEmail || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Welcome Email:</span><Badge variant={wizardData.sendWelcome ? 'success' : 'default'}>{wizardData.sendWelcome ? 'Will be sent' : 'Not sending'}</Badge></div>
                  </div>
                </Card>
                
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
                  <h4 className="font-semibold text-indigo-800 mb-2">Ready to Launch!</h4>
                  <p className="text-sm text-indigo-600">Click "Launch Microsite" to create this retailer portal. The site administrator will receive an email with login instructions.</p>
                </Card>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
            <Layers className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Platform Administration Hub</h1>
            <p className="text-slate-500">Drupal 2.0-style multi-site management for white-labeled retailer portals</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sites..."
              className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
          <Button variant="primary" icon={Rocket} onClick={() => setShowLaunchWizard(true)}>Launch New Site</Button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-white text-indigo-700 border border-slate-200 border-b-white -mb-[1px] shadow-sm' 
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.badge && <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">{tab.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Launch Wizard Modal */}
      {showLaunchWizard && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Wizard Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Launch New Microsite</h2>
                    <p className="text-sm text-slate-500">Step {wizardStep} of 6</p>
                  </div>
                </div>
                <button onClick={() => setShowLaunchWizard(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center gap-2">
                {['Template', 'Details', 'Branding', 'Features', 'Admin', 'Review'].map((step, i) => (
                  <div key={step} className="flex-1">
                    <div className={`h-2 rounded-full transition-all ${i + 1 <= wizardStep ? 'bg-indigo-500' : 'bg-slate-200'}`} />
                    <p className={`text-xs mt-1 ${i + 1 === wizardStep ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Wizard Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {renderWizardStep()}
            </div>
            
            {/* Wizard Footer */}
            <div className="p-6 border-t border-slate-200 flex items-center justify-between">
              <button 
                onClick={handleWizardBack}
                disabled={wizardStep === 1}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowLaunchWizard(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                {wizardStep < 6 ? (
                  <Button 
                    variant="primary" 
                    onClick={handleWizardNext}
                    disabled={wizardStep === 1 && !wizardData.template}
                  >
                    Continue <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button variant="primary" icon={Rocket} onClick={handleLaunchSite}>
                    Launch Microsite
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center">
                  <Globe2 className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-700">{stats.totalMicrosites}</p>
                  <p className="text-sm text-indigo-600">Total Sites</p>
                </div>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{stats.activeMicrosites}</p>
                  <p className="text-sm text-emerald-600">Active</p>
                </div>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-sky-700">{stats.totalRetailerUsers}</p>
                  <p className="text-sm text-sky-600">Users</p>
                </div>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{(stats.totalPageViews30d / 1000).toFixed(1)}k</p>
                  <p className="text-sm text-amber-600">Page Views</p>
                </div>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">${(stats.totalMonthlyRevenue / 1000).toFixed(1)}k</p>
                  <p className="text-sm text-emerald-600">MRR</p>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />Quick Actions
              </h3>
              <div className="space-y-2">
                <button onClick={() => setShowLaunchWizard(true)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Rocket className="w-5 h-5 text-indigo-600" /></div>
                  <div><p className="font-medium text-slate-800">Launch New Site</p><p className="text-xs text-slate-500">Create retailer portal</p></div>
                </button>
                <button onClick={() => setActiveTab('users')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-teal-50 hover:border-teal-200 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center"><Users className="w-5 h-5 text-teal-600" /></div>
                  <div><p className="font-medium text-slate-800">Manage Users</p><p className="text-xs text-slate-500">View all retailer users</p></div>
                </button>
                <button onClick={() => setActiveTab('branding')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-purple-50 hover:border-purple-200 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"><Palette className="w-5 h-5 text-purple-600" /></div>
                  <div><p className="font-medium text-slate-800">Branding Studio</p><p className="text-xs text-slate-500">Customize themes</p></div>
                </button>
              </div>
            </Card>
            
            {/* Active Microsites */}
            <Card className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-indigo-500" />Retailer Microsites
                </h3>
                <button onClick={() => setActiveTab('sites')} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">View All →</button>
              </div>
              <div className="space-y-3">
                {sites.slice(0, 4).map((site) => {
                  const siteUserCount = users.filter(u => u.micrositeId === site.id).length;
                  return (
                    <div 
                      key={site.id} 
                      className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-all cursor-pointer"
                      onClick={() => { setSelectedSite(site); setActiveTab('sites'); }}
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: site.branding.primaryColor + '20' }}>
                        <Globe className="w-6 h-6" style={{ color: site.branding.primaryColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800 truncate">{site.name}</p>
                          {getStatusBadge(site.status)}
                        </div>
                        <p className="text-xs text-slate-500">{site.domain}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">{siteUserCount} users</p>
                        <p className="text-xs text-slate-500">{site.tier}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
          
          {/* Activity & Alerts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-500" />Recent Platform Activity
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activity.slice(0, 6).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      log.type.includes('login') ? 'bg-sky-100' : 
                      log.type.includes('user') ? 'bg-emerald-100' : 
                      log.type.includes('branding') ? 'bg-purple-100' :
                      log.type.includes('site') ? 'bg-indigo-100' : 'bg-slate-100'
                    }`}>
                      {log.type.includes('login') ? <LogIn className="w-4 h-4 text-sky-600" /> : 
                       log.type.includes('user') ? <UserPlus className="w-4 h-4 text-emerald-600" /> : 
                       log.type.includes('branding') ? <Palette className="w-4 h-4 text-purple-600" /> :
                       log.type.includes('site') ? <Globe className="w-4 h-4 text-indigo-600" /> :
                       <Activity className="w-4 h-4 text-slate-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700"><span className="font-medium">{log.userName}</span> - {log.details}</p>
                      <p className="text-xs text-slate-400">{log.siteName} • {log.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />Sites Needing Attention
              </h3>
              <div className="space-y-3">
                {sites.filter(s => s.status !== 'active').map((site) => (
                  <div key={site.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-amber-200 hover:bg-amber-50/30">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      site.status === 'pending' ? 'bg-amber-100' : 'bg-rose-100'
                    }`}>
                      {site.status === 'pending' ? <Clock className="w-5 h-5 text-amber-600" /> : <XCircle className="w-5 h-5 text-rose-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{site.name}</p>
                      <p className="text-xs text-slate-500">
                        {site.status === 'pending' ? 'Awaiting setup completion' : 'Payment issue - site suspended'}
                      </p>
                    </div>
                    {getStatusBadge(site.status)}
                  </div>
                ))}
                {sites.filter(s => s.status !== 'active').length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                    <p>All sites are healthy!</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* All Sites Tab */}
      {activeTab === 'sites' && !selectedSite && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{filteredSites.length} microsite{filteredSites.length !== 1 ? 's' : ''}</p>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSites.map((site) => {
                const siteUserCount = users.filter(u => u.micrositeId === site.id).length;
                return (
                  <Card key={site.id} className="hover:shadow-lg transition-all cursor-pointer" onClick={() => setSelectedSite(site)}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: site.branding.primaryColor + '20' }}>
                        <Globe className="w-7 h-7" style={{ color: site.branding.primaryColor }} />
                      </div>
                      {getStatusBadge(site.status)}
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1">{site.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{site.domain}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-slate-600"><Users className="w-4 h-4" />{siteUserCount}</span>
                      <span className="flex items-center gap-1 text-slate-600"><Server className="w-4 h-4" />{site.usage.services}</span>
                      <span className="flex items-center gap-1 text-slate-600"><Router className="w-4 h-4" />{site.usage.antennas}</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      {getTierBadge(site.tier)}
                      <span className="text-sm text-slate-500">${site.billingInfo.monthlyValue.toLocaleString()}/mo</span>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Site</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Tier</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Users</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">MRR</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSites.map((site) => {
                    const siteUserCount = users.filter(u => u.micrositeId === site.id).length;
                    return (
                      <tr key={site.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedSite(site)}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: site.branding.primaryColor + '20' }}>
                              <Globe className="w-5 h-5" style={{ color: site.branding.primaryColor }} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{site.name}</p>
                              <p className="text-xs text-slate-500">{site.domain}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(site.status)}</td>
                        <td className="py-3 px-4">{getTierBadge(site.tier)}</td>
                        <td className="py-3 px-4 text-slate-600">{siteUserCount}</td>
                        <td className="py-3 px-4 text-slate-600">${site.billingInfo.monthlyValue.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <button className="p-2 hover:bg-slate-100 rounded-lg"><MoreVertical className="w-4 h-4 text-slate-400" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
      
      {/* Site Detail View */}
      {activeTab === 'sites' && selectedSite && (
        <div className="space-y-6">
          <button onClick={() => setSelectedSite(null)} className="flex items-center gap-2 text-slate-600 hover:text-slate-800">
            <ChevronLeft className="w-4 h-4" /> Back to All Sites
          </button>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: selectedSite.branding.primaryColor + '20' }}>
                      <Globe className="w-8 h-8" style={{ color: selectedSite.branding.primaryColor }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-slate-800">{selectedSite.name}</h2>
                        {getStatusBadge(selectedSite.status)}
                      </div>
                      <p className="text-slate-500">{selectedSite.domain}</p>
                      {selectedSite.customDomain && (
                        <p className="text-sm text-indigo-600 flex items-center gap-1"><Link className="w-3 h-3" />{selectedSite.customDomain}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" icon={ExternalLink}>Visit Portal</Button>
                    <Button variant="secondary" icon={Settings}>Configure</Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Users</p>
                    <p className="text-2xl font-bold text-slate-800">{selectedSite.usage.users}</p>
                    <p className="text-xs text-slate-400">of {selectedSite.limits.maxUsers}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Services</p>
                    <p className="text-2xl font-bold text-slate-800">{selectedSite.usage.services}</p>
                    <p className="text-xs text-slate-400">of {selectedSite.limits.maxServices}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Antennas</p>
                    <p className="text-2xl font-bold text-slate-800">{selectedSite.usage.antennas}</p>
                    <p className="text-xs text-slate-400">of {selectedSite.limits.maxAntennas}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500">Page Views</p>
                    <p className="text-2xl font-bold text-slate-800">{selectedSite.analytics.pageViews30d.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">last 30 days</p>
                  </div>
                </div>
              </Card>
              
              <Card>
                <h3 className="font-semibold text-slate-800 mb-4">Site Users</h3>
                <div className="space-y-2">
                  {users.filter(u => u.micrositeId === selectedSite.id).map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-medium">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRoleBadge(user.role)}
                        <Badge variant={user.status === 'active' ? 'success' : user.status === 'invited' ? 'warning' : 'critical'}>{user.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
            
            <div className="space-y-6">
              <Card>
                <h3 className="font-semibold text-slate-800 mb-4">Site Information</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Industry:</span><span className="font-medium">{selectedSite.industry}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Region:</span><span className="font-medium">{selectedSite.region}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tier:</span>{getTierBadge(selectedSite.tier)}</div>
                  <div className="flex justify-between"><span className="text-slate-500">Account Manager:</span><span className="font-medium">{selectedSite.accountManager}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Created:</span><span className="font-medium">{selectedSite.createdAt}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Launched:</span><span className="font-medium">{selectedSite.launchedAt || 'Not yet'}</span></div>
                </div>
              </Card>
              
              <Card>
                <h3 className="font-semibold text-slate-800 mb-4">Billing</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Plan:</span><span className="font-medium">{selectedSite.billingInfo.plan}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Monthly:</span><span className="font-medium text-emerald-600">${selectedSite.billingInfo.monthlyValue.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Payment:</span><span className="font-medium">{selectedSite.billingInfo.paymentMethod}</span></div>
                  {selectedSite.billingInfo.contractEnd && (
                    <div className="flex justify-between"><span className="text-slate-500">Contract Ends:</span><span className="font-medium">{selectedSite.billingInfo.contractEnd}</span></div>
                  )}
                </div>
              </Card>
              
              <Card>
                <h3 className="font-semibold text-slate-800 mb-4">Primary Contact</h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-slate-800">{selectedSite.contacts.primary.name}</p>
                  <p className="text-slate-500 flex items-center gap-2"><Mail className="w-4 h-4" />{selectedSite.contacts.primary.email}</p>
                  <p className="text-slate-500 flex items-center gap-2"><Phone className="w-4 h-4" />{selectedSite.contacts.primary.phone}</p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
      
      {/* All Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">All Retailer Users</h3>
              <div className="flex items-center gap-2">
                <select className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
                  <option>All Sites</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
                  <option>All Roles</option>
                  {siteRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Site</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">MFA</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Last Login</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const site = sites.find(s => s.id === user.micrositeId);
                  return (
                    <tr key={user.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-medium text-sm">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{user.name}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: site?.branding.primaryColor + '20' }}>
                            <Globe className="w-3 h-3" style={{ color: site?.branding.primaryColor }} />
                          </div>
                          <span className="text-sm text-slate-600">{site?.name || '-'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-3 px-4">
                        <Badge variant={user.status === 'active' ? 'success' : user.status === 'invited' ? 'warning' : 'critical'}>{user.status}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        {user.mfaEnabled ? (
                          <span className="flex items-center gap-1 text-emerald-600"><Shield className="w-4 h-4" />Enabled</span>
                        ) : (
                          <span className="text-slate-400">Disabled</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{user.lastLogin || 'Never'}</td>
                      <td className="py-3 px-4 text-right">
                        <button className="p-2 hover:bg-slate-100 rounded-lg" title="Impersonate User">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}
      
      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Site Templates</h3>
              <p className="text-sm text-slate-500">Pre-configured templates for quick microsite deployment</p>
            </div>
            <Button variant="secondary" icon={Plus}>Create Template</Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {availableTemplates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg transition-all">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    template.thumbnail === 'enterprise' ? 'bg-indigo-100' :
                    template.thumbnail === 'smb' ? 'bg-teal-100' :
                    template.thumbnail === 'maritime' ? 'bg-sky-100' : 'bg-slate-100'
                  }`}>
                    {template.thumbnail === 'enterprise' ? <Building2 className="w-7 h-7 text-indigo-600" /> :
                     template.thumbnail === 'smb' ? <Briefcase className="w-7 h-7 text-teal-600" /> :
                     template.thumbnail === 'maritime' ? <Globe className="w-7 h-7 text-sky-600" /> :
                     <Eye className="w-7 h-7 text-slate-600" />}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">{template.name}</h4>
                    <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Included Features:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{f}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-xs text-slate-500">
                    Limits: {template.defaultLimits.maxUsers} users, {template.defaultLimits.maxAntennas} antennas
                  </div>
                  <Button variant="secondary" size="sm">Use Template</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {/* Branding Studio Tab */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-slate-800">Branding Studio</h3>
            <p className="text-sm text-slate-500">Manage brand presets and email templates for all microsites</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-500" />Color Presets
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {presets.map((preset) => (
                  <div key={preset.id} className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 cursor-pointer transition-all">
                    <div className="flex gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: preset.colors.primary }} />
                      <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: preset.colors.secondary }} />
                      <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: preset.colors.accent }} />
                    </div>
                    <p className="font-medium text-slate-800">{preset.name}</p>
                  </div>
                ))}
              </div>
              <Button variant="secondary" icon={Plus} className="w-full mt-4">Create New Preset</Button>
            </Card>
            
            <Card>
              <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-500" />Email Templates
              </h4>
              <div className="space-y-3">
                {v4EmailTemplates.map((template) => (
                  <div key={template.id} className="p-3 border border-slate-200 rounded-xl hover:border-indigo-300 cursor-pointer transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{template.name}</p>
                        <p className="text-xs text-slate-500">{template.description}</p>
                      </div>
                      <Button variant="secondary" size="sm" icon={Edit3}>Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <Card>
          <h3 className="font-semibold text-slate-800 mb-4">Global Platform Activity</h3>
          <div className="space-y-3">
            {activity.map((log) => (
              <div key={log.id} className="flex items-start gap-4 p-4 border border-slate-100 rounded-xl hover:bg-slate-50">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  log.type.includes('created') ? 'bg-emerald-100' :
                  log.type.includes('login') ? 'bg-sky-100' : 
                  log.type.includes('suspended') ? 'bg-rose-100' :
                  log.type.includes('branding') ? 'bg-purple-100' : 'bg-slate-100'
                }`}>
                  {log.type.includes('created') ? <Rocket className="w-5 h-5 text-emerald-600" /> :
                   log.type.includes('login') ? <LogIn className="w-5 h-5 text-sky-600" /> :
                   log.type.includes('suspended') ? <XCircle className="w-5 h-5 text-rose-600" /> :
                   log.type.includes('branding') ? <Palette className="w-5 h-5 text-purple-600" /> :
                   <Activity className="w-5 h-5 text-slate-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-slate-800"><span className="font-medium">{log.userName}</span> - {log.details}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Globe className="w-3 h-3" />{log.siteName}
                    </span>
                    <span className="text-xs text-slate-400">{log.timestamp}</span>
                    <span className="text-xs text-slate-400">IP: {log.ipAddress}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Platform Settings</h3>
            <div className="space-y-4">
              {[
                { name: 'Require MFA for all site admins', desc: 'Force multi-factor authentication', enabled: true },
                { name: 'Auto-suspend on payment failure', desc: 'Automatically suspend sites with failed payments', enabled: true },
                { name: 'Allow custom domains', desc: 'Let retailers use their own domain names', enabled: true },
                { name: 'Enable SSO integration', desc: 'Allow SAML/OIDC single sign-on', enabled: false },
                { name: 'API rate limiting', desc: 'Enforce API call limits per site', enabled: true },
              ].map((setting) => (
                <div key={setting.name} className="flex items-center justify-between p-3 border border-slate-100 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-800">{setting.name}</p>
                    <p className="text-sm text-slate-500">{setting.desc}</p>
                  </div>
                  <button className={`relative w-12 h-6 rounded-full transition-all ${setting.enabled ? 'bg-indigo-500' : 'bg-slate-200'}`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${setting.enabled ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
          
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Default Site Limits</h3>
            <div className="space-y-4">
              {[
                { name: 'Max Users per Site', value: 50 },
                { name: 'Max Services per Site', value: 20 },
                { name: 'Max Antennas per Site', value: 100 },
                { name: 'Data Retention (days)', value: 365 },
                { name: 'API Calls per Month', value: 50000 },
              ].map((limit) => (
                <div key={limit.name} className="flex items-center justify-between">
                  <span className="text-slate-600">{limit.name}</span>
                  <input 
                    type="number" 
                    defaultValue={limit.value} 
                    className="w-24 px-3 py-1 border border-slate-200 rounded-lg text-right"
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const ACMESiteAdminPortal = ({ site, users, roles, activity, permissionCategories }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'network_viewer', sendWelcome: true });
  const [brandingTab, setBrandingTab] = useState('colors');
  
  // Use ACME as default if not provided
  const currentSite = site || v4RetailerMicrosites.find(s => s.slug === 'acme') || v4RetailerMicrosites[0];
  const siteUsers = users || v4RetailerUsers.filter(u => u.micrositeId === currentSite.id);
  const siteRoles = roles || v4SiteRolesDetailed;
  const siteActivity = activity || v4PlatformActivity.filter(a => a.siteId === currentSite.id);
  const permCategories = permissionCategories || sitePermissionCategories;
  
  const tabs = [
    { id: 'overview', label: 'Overview', icon: PanelLeft },
    { id: 'users', label: 'Users', icon: Users, badge: siteUsers.length },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
    { id: 'branding', label: 'Portal Branding', icon: Palette },
    { id: 'features', label: 'Features', icon: ToggleRight },
    { id: 'activity', label: 'Activity Log', icon: History },
    { id: 'settings', label: 'Site Settings', icon: Settings },
  ];
  
  const getStatusBadge = (status) => {
    const variants = { active: 'success', invited: 'warning', suspended: 'critical' };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };
  
  const getRoleBadge = (roleId) => {
    const role = siteRoles.find(r => r.id === roleId);
    if (!role) return <Badge>{roleId}</Badge>;
    const colorMap = { rose: 'critical', amber: 'warning', indigo: 'indigo', teal: 'teal', sky: 'info', slate: 'default' };
    return <Badge variant={colorMap[role.color] || 'default'}>{role.name}</Badge>;
  };
  
  const handleInviteUser = () => {
    alert(`Invitation sent to ${inviteData.email} as ${inviteData.role}`);
    setShowInviteModal(false);
    setInviteData({ email: '', name: '', role: 'network_viewer', sendWelcome: true });
  };
  
  // Calculate stats
  const pendingInvites = siteUsers.filter(u => u.status === 'invited').length;
  const activeUsers = siteUsers.filter(u => u.status === 'active').length;
  const mfaEnabled = siteUsers.filter(u => u.mfaEnabled).length;
  
  const usageStats = {
    users: { used: siteUsers.length, limit: currentSite.limits.maxUsers },
    services: { used: currentSite.usage.services, limit: currentSite.limits.maxServices },
    antennas: { used: currentSite.usage.antennas, limit: currentSite.limits.maxAntennas },
    storage: { used: currentSite.usage.storageGb, limit: currentSite.limits.storageGb },
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header with Site Branding Preview */}
      <div 
        className="rounded-2xl p-6 text-white relative overflow-hidden"
        style={{ 
          background: currentSite.branding.headerStyle === 'gradient' 
            ? `linear-gradient(135deg, ${currentSite.branding.primaryColor}, ${currentSite.branding.secondaryColor})`
            : currentSite.branding.primaryColor 
        }}
      >
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{currentSite.name}</h1>
                <Badge variant="success" className="bg-white/20 text-white border-white/30">Site Admin</Badge>
              </div>
              <p className="text-white/80">{currentSite.domain}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl flex items-center gap-2 transition-all">
              <ExternalLink className="w-4 h-4" /> View Portal
            </button>
            <button className="px-4 py-2 bg-white hover:bg-white/90 rounded-xl flex items-center gap-2 transition-all" style={{ color: currentSite.branding.primaryColor }}>
              <UserPlus className="w-4 h-4" /> Invite User
            </button>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/4 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-white border border-slate-200 border-b-white -mb-[1px] shadow-sm' 
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}
              style={{ color: activeTab === tab.id ? currentSite.branding.primaryColor : undefined }}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.badge && <span className="px-1.5 py-0.5 text-xs rounded-full" style={{ backgroundColor: currentSite.branding.primaryColor + '20', color: currentSite.branding.primaryColor }}>{tab.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: currentSite.branding.primaryColor + '20' }}>
                    <UserPlus className="w-5 h-5" style={{ color: currentSite.branding.primaryColor }} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">Invite New User</h2>
                    <p className="text-sm text-slate-500">Add a user to {currentSite.name}</p>
                  </div>
                </div>
                <button onClick={() => setShowInviteModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input 
                  type="text" 
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': currentSite.branding.primaryColor }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
                <input 
                  type="email" 
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="john@acmecorp.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                <select 
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2"
                >
                  {siteRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name} - {role.description}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <button 
                  onClick={() => setInviteData({ ...inviteData, sendWelcome: !inviteData.sendWelcome })}
                  className={`relative w-12 h-6 rounded-full transition-all`}
                  style={{ backgroundColor: inviteData.sendWelcome ? currentSite.branding.primaryColor : '#e2e8f0' }}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${inviteData.sendWelcome ? 'left-7' : 'left-1'}`} />
                </button>
                <div>
                  <p className="font-medium text-slate-800">Send Welcome Email</p>
                  <p className="text-sm text-slate-500">User will receive login instructions</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => setShowInviteModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800">Cancel</button>
              <button 
                onClick={handleInviteUser}
                disabled={!inviteData.email || !inviteData.name}
                className="px-6 py-2 text-white rounded-xl disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: currentSite.branding.primaryColor }}
              >
                <Send className="w-4 h-4" /> Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: currentSite.branding.primaryColor + '20' }}>
                  <Users className="w-6 h-6" style={{ color: currentSite.branding.primaryColor }} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{siteUsers.length}</p>
                  <p className="text-sm text-slate-500">Total Users</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{activeUsers}</p>
                  <p className="text-sm text-slate-500">Active Users</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{pendingInvites}</p>
                  <p className="text-sm text-slate-500">Pending Invites</p>
                </div>
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{mfaEnabled}</p>
                  <p className="text-sm text-slate-500">MFA Enabled</p>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Usage Quotas */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Resource Usage</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(usageStats).map(([key, value]) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600 capitalize">{key}</span>
                    <span className="text-sm font-medium text-slate-800">{value.used} / {value.limit}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${(value.used / value.limit) * 100}%`,
                        backgroundColor: (value.used / value.limit) > 0.8 ? '#f59e0b' : currentSite.branding.primaryColor
                      }} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />Quick Actions
              </h3>
              <div className="space-y-2">
                <button onClick={() => setShowInviteModal(true)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-indigo-200 transition-all text-left" style={{ '--hover-bg': currentSite.branding.primaryColor + '10' }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: currentSite.branding.primaryColor + '20' }}>
                    <UserPlus className="w-5 h-5" style={{ color: currentSite.branding.primaryColor }} />
                  </div>
                  <div><p className="font-medium text-slate-800">Invite User</p><p className="text-xs text-slate-500">Add a new team member</p></div>
                </button>
                <button onClick={() => setActiveTab('branding')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-purple-200 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Palette className="w-5 h-5 text-purple-600" />
                  </div>
                  <div><p className="font-medium text-slate-800">Customize Branding</p><p className="text-xs text-slate-500">Update portal appearance</p></div>
                </button>
                <button onClick={() => setActiveTab('roles')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-teal-200 transition-all text-left">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-teal-600" />
                  </div>
                  <div><p className="font-medium text-slate-800">Manage Roles</p><p className="text-xs text-slate-500">Configure user permissions</p></div>
                </button>
              </div>
            </Card>
            
            {/* Recent Activity */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-500" />Recent Activity
                </h3>
                <button onClick={() => setActiveTab('activity')} className="text-sm font-medium" style={{ color: currentSite.branding.primaryColor }}>View All →</button>
              </div>
              <div className="space-y-3">
                {siteActivity.slice(0, 4).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      log.type.includes('login') ? 'bg-sky-100' : 
                      log.type.includes('user') ? 'bg-emerald-100' : 
                      log.type.includes('branding') ? 'bg-purple-100' : 'bg-slate-100'
                    }`}>
                      {log.type.includes('login') ? <LogIn className="w-4 h-4 text-sky-600" /> : 
                       log.type.includes('user') ? <UserPlus className="w-4 h-4 text-emerald-600" /> : 
                       log.type.includes('branding') ? <Palette className="w-4 h-4 text-purple-600" /> :
                       <Activity className="w-4 h-4 text-slate-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700"><span className="font-medium">{log.userName}</span></p>
                      <p className="text-xs text-slate-500">{log.details}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{log.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          
          {/* Users by Role */}
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Users by Role</h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {siteRoles.map((role) => {
                const count = siteUsers.filter(u => u.role === role.id).length;
                const colorMap = { rose: '#f43f5e', amber: '#f59e0b', indigo: '#6366f1', teal: '#14b8a6', sky: '#0ea5e9', slate: '#64748b' };
                return (
                  <div key={role.id} className="p-4 border border-slate-100 rounded-xl hover:border-slate-200 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorMap[role.color] || '#64748b' }} />
                        <span className="text-sm font-medium text-slate-700">{role.name}</span>
                      </div>
                      <span className="text-lg font-bold text-slate-800">{count}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{role.description}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
      
      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Search users..."
                  className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm w-64"
                />
              </div>
              <select className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
                <option>All Roles</option>
                {siteRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <select className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
                <option>All Status</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <button 
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 text-white rounded-xl flex items-center gap-2"
              style={{ backgroundColor: currentSite.branding.primaryColor }}
            >
              <UserPlus className="w-4 h-4" /> Invite User
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">MFA</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Last Login</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteUsers.map((user) => (
                      <tr 
                        key={user.id} 
                        className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${selectedUser?.id === user.id ? 'bg-slate-50' : ''}`}
                        onClick={() => setSelectedUser(user)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                              style={{ background: `linear-gradient(135deg, ${currentSite.branding.primaryColor}, ${currentSite.branding.secondaryColor})` }}
                            >
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{user.name}</p>
                              <p className="text-xs text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                        <td className="py-3 px-4">{getStatusBadge(user.status)}</td>
                        <td className="py-3 px-4">
                          {user.mfaEnabled ? (
                            <span className="flex items-center gap-1 text-emerald-600 text-sm"><Shield className="w-4 h-4" />On</span>
                          ) : (
                            <span className="text-slate-400 text-sm">Off</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">{user.lastLogin || 'Never'}</td>
                        <td className="py-3 px-4 text-right">
                          <button className="p-2 hover:bg-slate-100 rounded-lg">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
            
            {/* User Detail Panel */}
            <div>
              {selectedUser ? (
                <Card className="sticky top-4">
                  <div className="text-center mb-6">
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3"
                      style={{ background: `linear-gradient(135deg, ${currentSite.branding.primaryColor}, ${currentSite.branding.secondaryColor})` }}
                    >
                      {selectedUser.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <h3 className="font-semibold text-slate-800">{selectedUser.name}</h3>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      {getRoleBadge(selectedUser.role)}
                      {getStatusBadge(selectedUser.status)}
                    </div>
                  </div>
                  
                  <div className="space-y-4 border-t border-slate-100 pt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Department</span>
                      <span className="font-medium text-slate-800">{selectedUser.department}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Invited By</span>
                      <span className="font-medium text-slate-800">{selectedUser.invitedBy}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Created</span>
                      <span className="font-medium text-slate-800">{selectedUser.createdAt}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Last Login</span>
                      <span className="font-medium text-slate-800">{selectedUser.lastLogin || 'Never'}</span>
                    </div>
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-slate-500">MFA Enabled</span>
                      {selectedUser.mfaEnabled ? (
                        <Badge variant="success">Enabled</Badge>
                      ) : (
                        <Badge variant="warning">Disabled</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2 border-t border-slate-100 pt-4 mt-4">
                    <button className="w-full px-4 py-2 text-sm rounded-xl border border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-2">
                      <Edit3 className="w-4 h-4" /> Edit User
                    </button>
                    <button className="w-full px-4 py-2 text-sm rounded-xl border border-slate-200 hover:bg-slate-50 flex items-center justify-center gap-2">
                      <Key className="w-4 h-4" /> Reset Password
                    </button>
                    {selectedUser.status === 'active' ? (
                      <button className="w-full px-4 py-2 text-sm rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center justify-center gap-2">
                        <Lock className="w-4 h-4" /> Suspend User
                      </button>
                    ) : (
                      <button className="w-full px-4 py-2 text-sm rounded-xl border border-emerald-200 text-emerald-600 hover:bg-emerald-50 flex items-center justify-center gap-2">
                        <Unlock className="w-4 h-4" /> Activate User
                      </button>
                    )}
                  </div>
                </Card>
              ) : (
                <Card className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Select a user to view details</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Roles & Permissions Tab */}
      {activeTab === 'roles' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Site Roles & Permissions</h3>
              <p className="text-sm text-slate-500">Configure what each role can access and modify</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-slate-700 text-sm">Available Roles</h4>
              {siteRoles.map((role) => {
                const count = siteUsers.filter(u => u.role === role.id).length;
                const colorMap = { rose: '#f43f5e', amber: '#f59e0b', indigo: '#6366f1', teal: '#14b8a6', sky: '#0ea5e9', slate: '#64748b' };
                return (
                  <div 
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      selectedRole?.id === role.id 
                        ? 'border-2' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={{ borderColor: selectedRole?.id === role.id ? colorMap[role.color] : undefined }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorMap[role.color] }} />
                        <span className="font-medium text-slate-800">{role.name}</span>
                      </div>
                      <span className="text-sm text-slate-500">{count} users</span>
                    </div>
                    <p className="text-xs text-slate-500">{role.description}</p>
                  </div>
                );
              })}
            </div>
            
            <div className="lg:col-span-2">
              {selectedRole ? (
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-semibold text-slate-800">{selectedRole.name} Permissions</h3>
                      <p className="text-sm text-slate-500">{selectedRole.description}</p>
                    </div>
                    <Badge variant="info">Max {selectedRole.maxPerSite} per site</Badge>
                  </div>
                  
                  <div className="space-y-6">
                    {permCategories.map((category) => (
                      <div key={category.category}>
                        <h4 className="font-medium text-slate-700 mb-3">{category.category}</h4>
                        <div className="space-y-2">
                          {category.permissions.map((perm) => {
                            const hasPermission = selectedRole.permissions.some(p => 
                              p === perm.id || p === `${perm.id.split('.')[0]}.*` || p === '*'
                            );
                            return (
                              <div key={perm.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                <div>
                                  <p className="font-medium text-slate-700 text-sm">{perm.name}</p>
                                  <p className="text-xs text-slate-500">{perm.description}</p>
                                </div>
                                {hasPermission ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-slate-300" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {selectedRole.canInviteRoles.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <h4 className="font-medium text-slate-700 mb-2">Can Invite These Roles:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedRole.canInviteRoles.map((roleId) => {
                          const role = siteRoles.find(r => r.id === roleId);
                          return role ? getRoleBadge(role.id) : null;
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              ) : (
                <Card className="text-center py-12">
                  <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Select a role to view permissions</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Portal Branding Tab */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            {['colors', 'header', 'typography', 'email'].map((tab) => (
              <button
                key={tab}
                onClick={() => setBrandingTab(tab)}
                className={`px-4 py-2 rounded-t-xl text-sm font-medium capitalize transition-all ${
                  brandingTab === tab 
                    ? 'bg-white border border-slate-200 border-b-white -mb-[1px]' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              {brandingTab === 'colors' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-slate-800">Color Scheme</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Primary Color', key: 'primaryColor', value: currentSite.branding.primaryColor },
                      { label: 'Secondary Color', key: 'secondaryColor', value: currentSite.branding.secondaryColor },
                      { label: 'Accent Color', key: 'accentColor', value: currentSite.branding.accentColor },
                    ].map((color) => (
                      <div key={color.key}>
                        <label className="block text-sm font-medium text-slate-700 mb-2">{color.label}</label>
                        <div className="flex items-center gap-3">
                          <input type="color" defaultValue={color.value} className="w-12 h-12 rounded-xl border border-slate-200 cursor-pointer" />
                          <input type="text" defaultValue={color.value} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {brandingTab === 'header' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-slate-800">Header Style</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Style</label>
                      <div className="flex gap-2">
                        {['gradient', 'solid', 'transparent'].map((style) => (
                          <button
                            key={style}
                            className={`flex-1 px-4 py-2 rounded-xl border text-sm capitalize ${
                              currentSite.branding.headerStyle === style
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Logo</label>
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                        <Image className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-500">Drag & drop or click to upload</p>
                        <p className="text-xs text-slate-400 mt-1">SVG, PNG or JPG (max 2MB)</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {brandingTab === 'typography' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-slate-800">Typography</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Font Family</label>
                      <select defaultValue={currentSite.branding.fontFamily} className="w-full px-4 py-2 border border-slate-200 rounded-xl">
                        <option value="Inter">Inter</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="Poppins">Poppins</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Border Radius</label>
                      <select defaultValue={currentSite.branding.borderRadius} className="w-full px-4 py-2 border border-slate-200 rounded-xl">
                        <option value="rounded-none">None</option>
                        <option value="rounded">Small</option>
                        <option value="rounded-lg">Medium</option>
                        <option value="rounded-xl">Large</option>
                        <option value="rounded-2xl">Extra Large</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
              
              {brandingTab === 'email' && (
                <div className="space-y-6">
                  <h3 className="font-semibold text-slate-800">Email Branding</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email Logo</label>
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                        <Mail className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-500">Upload email header logo</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email Footer Text</label>
                      <textarea 
                        rows={3}
                        defaultValue={`© ${new Date().getFullYear()} ${currentSite.name}. All rights reserved.`}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6 pt-6 border-t border-slate-100 flex justify-end gap-2">
                <button className="px-4 py-2 text-slate-600 hover:text-slate-800">Reset</button>
                <button 
                  className="px-6 py-2 text-white rounded-xl"
                  style={{ backgroundColor: currentSite.branding.primaryColor }}
                >
                  Save Changes
                </button>
              </div>
            </Card>
            
            {/* Live Preview */}
            <Card>
              <h3 className="font-semibold text-slate-800 mb-4">Live Preview</h3>
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div 
                  className="p-4"
                  style={{ 
                    background: currentSite.branding.headerStyle === 'gradient' 
                      ? `linear-gradient(135deg, ${currentSite.branding.primaryColor}, ${currentSite.branding.secondaryColor})`
                      : currentSite.branding.primaryColor 
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-semibold text-white" style={{ fontFamily: currentSite.branding.fontFamily }}>
                        {currentSite.name}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-lg bg-white/20" />
                      <div className="w-8 h-8 rounded-lg bg-white/20" />
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-slate-50">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="p-2 bg-white rounded-lg border border-slate-100">
                      <p className="text-[10px] text-slate-500">Services</p>
                      <p className="font-bold" style={{ color: currentSite.branding.primaryColor }}>4</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-slate-100">
                      <p className="text-[10px] text-slate-500">Alerts</p>
                      <p className="font-bold" style={{ color: currentSite.branding.accentColor }}>2</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-slate-100">
                      <p className="text-[10px] text-slate-500">Antennas</p>
                      <p className="font-bold" style={{ color: currentSite.branding.primaryColor }}>12</p>
                    </div>
                  </div>
                  <button 
                    className="w-full py-2 rounded-lg text-white text-sm"
                    style={{ backgroundColor: currentSite.branding.primaryColor }}
                  >
                    View Dashboard
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Features Tab */}
      {activeTab === 'features' && (
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-slate-800">Enabled Features</h3>
            <p className="text-sm text-slate-500">Control which modules are available on your portal</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: 'billing', name: 'Billing & Invoices', desc: 'View invoices, payments, and billing history', icon: CreditCard, enabled: currentSite.features.billing },
              { id: 'fleetMap', name: 'Fleet Map', desc: 'Interactive map of all terminals', icon: Map, enabled: currentSite.features.fleetMap },
              { id: 'alerts', name: 'Alerts & Notifications', desc: 'Real-time alert center', icon: Bell, enabled: currentSite.features.alerts },
              { id: 'support', name: 'Support Tickets', desc: 'Create and track support tickets', icon: MessageSquare, enabled: currentSite.features.support },
              { id: 'dataPools', name: 'Data Pools', desc: 'Monitor data usage and allocation', icon: Database, enabled: currentSite.features.dataPools },
              { id: 'apiAccess', name: 'API Access', desc: 'REST API for integrations', icon: Link, enabled: currentSite.features.apiAccess, premium: true },
              { id: 'customReports', name: 'Custom Reports', desc: 'Generate analytics reports', icon: FileText, enabled: currentSite.features.customReports, premium: true },
              { id: 'sso', name: 'SSO Integration', desc: 'SAML/OIDC single sign-on', icon: Key, enabled: currentSite.features.sso, premium: true },
              { id: 'mfa', name: 'Require MFA', desc: 'Force multi-factor for all users', icon: Shield, enabled: currentSite.features.mfa },
            ].map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.id} className={`p-4 rounded-xl border ${feature.enabled ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${feature.enabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        <Icon className={`w-5 h-5 ${feature.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-800">{feature.name}</h4>
                          {feature.premium && <Badge variant="indigo">Premium</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">{feature.desc}</p>
                      </div>
                    </div>
                    <button 
                      className={`relative w-12 h-6 rounded-full transition-all ${feature.enabled ? 'bg-emerald-500' : 'bg-slate-200'}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${feature.enabled ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Activity Log</h3>
            <div className="flex items-center gap-2">
              <select className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm">
                <option>All Activities</option>
                <option>Logins</option>
                <option>User Changes</option>
                <option>Settings</option>
                <option>Branding</option>
              </select>
              <input type="date" className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
            </div>
          </div>
          <div className="space-y-3">
            {siteActivity.map((log) => (
              <div key={log.id} className="flex items-start gap-4 p-4 border border-slate-100 rounded-xl hover:bg-slate-50">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  log.type.includes('login') ? 'bg-sky-100' :
                  log.type.includes('user') ? 'bg-emerald-100' :
                  log.type.includes('branding') ? 'bg-purple-100' : 'bg-slate-100'
                }`}>
                  {log.type.includes('login') ? <LogIn className="w-5 h-5 text-sky-600" /> :
                   log.type.includes('user') ? <UserPlus className="w-5 h-5 text-emerald-600" /> :
                   log.type.includes('branding') ? <Palette className="w-5 h-5 text-purple-600" /> :
                   <Activity className="w-5 h-5 text-slate-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-slate-800"><span className="font-medium">{log.userName}</span></p>
                  <p className="text-sm text-slate-600">{log.details}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                    <span>{log.timestamp}</span>
                    <span>IP: {log.ipAddress}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Site Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site Name</label>
                <input type="text" defaultValue={currentSite.name} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site URL</label>
                <input type="text" defaultValue={currentSite.domain} className="w-full px-4 py-2 border border-slate-200 rounded-xl bg-slate-50" disabled />
              </div>
              {currentSite.customDomain && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Custom Domain</label>
                  <div className="flex items-center gap-2">
                    <input type="text" defaultValue={currentSite.customDomain} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl" />
                    <Badge variant="success">Verified</Badge>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
                <select defaultValue={currentSite.industry} className="w-full px-4 py-2 border border-slate-200 rounded-xl">
                  <option>Enterprise Technology</option>
                  <option>Manufacturing</option>
                  <option>Maritime & Shipping</option>
                  <option>Energy & Utilities</option>
                  <option>Construction</option>
                </select>
              </div>
            </div>
          </Card>
          
          <Card>
            <h3 className="font-semibold text-slate-800 mb-4">Contacts</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Primary Contact</label>
                <input type="text" defaultValue={currentSite.contacts.primary.name} className="w-full px-4 py-2 border border-slate-200 rounded-xl mb-2" />
                <input type="email" defaultValue={currentSite.contacts.primary.email} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Billing Contact</label>
                <input type="text" defaultValue={currentSite.contacts.billing.name} className="w-full px-4 py-2 border border-slate-200 rounded-xl mb-2" />
                <input type="email" defaultValue={currentSite.contacts.billing.email} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              </div>
            </div>
          </Card>
          
          <Card className="lg:col-span-2">
            <h3 className="font-semibold text-slate-800 mb-4">Danger Zone</h3>
            <div className="p-4 border border-rose-200 rounded-xl bg-rose-50">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-rose-800">Delete This Site</h4>
                  <p className="text-sm text-rose-600">Once deleted, all data will be permanently removed.</p>
                </div>
                <button className="px-4 py-2 bg-rose-600 text-white rounded-xl hover:bg-rose-700">
                  Delete Site
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// USER MANAGEMENT MODULE
// ============================================================================

const UserManagementPage = ({ users, roles, auditLog }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('access');
  
  const filteredUsers = users.filter(user => {
    if (searchQuery && !user.name.toLowerCase().includes(searchQuery.toLowerCase()) && !user.email.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterRole !== 'all' && !user.roles.includes(filterRole)) return false;
    if (filterStatus !== 'all' && user.status !== filterStatus) return false;
    if (filterType !== 'all' && user.orgType !== filterType) return false;
    return true;
  });
  
  const getRoleBadge = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return <Badge>{roleId}</Badge>;
    const colorMap = { rose: 'critical', amber: 'warning', indigo: 'indigo', teal: 'teal', emerald: 'success', sky: 'info', slate: 'default', purple: 'purple', orange: 'warning', cyan: 'info' };
    return <Badge variant={colorMap[role.color] || 'default'}>{role.name}</Badge>;
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'locked': return <Badge variant="critical">Locked</Badge>;
      case 'invited': return <Badge variant="warning">Invited</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };
  
  const userAudit = selectedUser ? auditLog.filter(l => l.userId === selectedUser.id) : [];

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-slate-500">Manage users, roles, and access to Starlink services</p>
        </div>
        <Button variant="primary" icon={UserPlus} onClick={() => setShowCreateModal(true)}>Create User</Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{users.length}</p><p className="text-xs text-slate-500">Total Users</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><UserCheck className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{users.filter(u => u.status === 'active').length}</p><p className="text-xs text-slate-500">Active</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Mail className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{users.filter(u => u.status === 'invited').length}</p><p className="text-xs text-slate-500">Pending</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center"><Building className="w-5 h-5 text-sky-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{users.filter(u => u.orgType === 'liberty').length}</p><p className="text-xs text-slate-500">Liberty Internal</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center"><Lock className="w-5 h-5 text-rose-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{users.filter(u => u.status === 'locked').length}</p><p className="text-xs text-slate-500">Locked</p></div>
          </div>
        </Card>
      </div>
      
      {/* Filter Bar */}
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm">
              <option value="all">All Roles</option>
              {roles.map(r => (<option key={r.id} value={r.id}>{r.name}</option>))}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="locked">Locked</option>
              <option value="invited">Invited</option>
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-xl text-sm">
              <option value="all">All Types</option>
              <option value="liberty">Liberty Internal</option>
              <option value="customer">Customer User</option>
            </select>
          </div>
        </div>
      </Card>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-2">
          <Card padding="p-0">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Users ({filteredUsers.length})</h3>
              <Button variant="secondary" size="sm" icon={Download}>Export</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Name</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Organization</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Role(s)</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Last Login</th>
                    <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Status</th>
                    <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`hover:bg-slate-50/50 cursor-pointer ${selectedUser?.id === user.id ? 'bg-indigo-50/50' : ''}`} onClick={() => setSelectedUser(user)}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${user.orgType === 'liberty' ? 'bg-indigo-100' : 'bg-teal-100'}`}>
                            <span className={`text-xs font-semibold ${user.orgType === 'liberty' ? 'text-indigo-600' : 'text-teal-600'}`}>{user.name.split(' ').map(n => n[0]).join('')}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{user.name}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {user.orgType === 'liberty' ? <Building className="w-4 h-4 text-indigo-500" /> : <Building2 className="w-4 h-4 text-teal-500" />}
                          <span className="text-sm text-slate-600">{user.organization}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.slice(0, 2).map(roleId => (<span key={roleId}>{getRoleBadge(roleId)}</span>))}
                          {user.roles.length > 2 && <Badge variant="default">+{user.roles.length - 2}</Badge>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-500">{user.lastLogin || 'Never'}</td>
                      <td className="px-5 py-3">{getStatusBadge(user.status)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-slate-400" /></button>
                          <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="Edit"><Edit3 className="w-4 h-4 text-slate-400" /></button>
                          <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="More"><MoreVertical className="w-4 h-4 text-slate-400" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
        
        {/* User Detail Panel */}
        <div>
          {selectedUser ? (
            <Card className="sticky top-6">
              {/* Profile Header */}
              <div className="text-center mb-4 pb-4 border-b border-slate-100">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 ${selectedUser.orgType === 'liberty' ? 'bg-indigo-600' : 'bg-teal-600'}`}>
                  <span className="text-xl font-bold text-white">{selectedUser.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <h3 className="font-semibold text-slate-800">{selectedUser.name}</h3>
                <p className="text-sm text-slate-500">{selectedUser.email}</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  {getStatusBadge(selectedUser.status)}
                  {selectedUser.mfaEnabled && <span className="flex items-center gap-1 text-xs text-emerald-600"><Fingerprint className="w-3 h-3" /> MFA</span>}
                </div>
              </div>
              
              {/* Detail Tabs */}
              <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-lg p-1">
                {[
                  { id: 'access', label: 'Access & Roles' },
                  { id: 'services', label: 'Services' },
                  { id: 'audit', label: 'Audit' },
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setActiveDetailTab(tab.id)}
                    className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeDetailTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Access & Roles Tab */}
              {activeDetailTab === 'access' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Assigned Roles</p>
                    <div className="space-y-2">
                      {selectedUser.roles.map(roleId => {
                        const role = roles.find(r => r.id === roleId);
                        return (
                          <div key={roleId} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">{getRoleBadge(roleId)}</div>
                            <label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" defaultChecked className="sr-only peer" /><div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div></label>
                          </div>
                        );
                      })}
                    </div>
                    <Button variant="ghost" size="sm" icon={Plus} className="w-full mt-2">Add Role</Button>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">User Details</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2"><Building className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{selectedUser.organization}</span></div>
                      <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{selectedUser.department}</span></div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{selectedUser.phone}</span></div>
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /><span className="text-slate-600">Created: {selectedUser.createdAt}</span></div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Services Tab */}
              {activeDetailTab === 'services' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Service Access Scope</p>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="text-sm font-medium text-slate-700">All Services</span></div>
                      <p className="text-xs text-slate-500">This user has access to all service lines within their organization.</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Antenna Groups</p>
                    <div className="space-y-2">
                      {['All Antennas', 'Headquarters', 'Remote Sites'].map((group, i) => (
                        <div key={group} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <span className="text-sm text-slate-700">{group}</span>
                          {i === 0 ? <Badge variant="success" size="xs">Full Access</Badge> : <Badge variant="default" size="xs">View Only</Badge>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Audit Tab */}
              {activeDetailTab === 'audit' && (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {userAudit.length > 0 ? userAudit.map((log) => (
                    <div key={log.id} className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
                      <div className={`w-6 h-6 rounded flex items-center justify-center ${log.action.includes('login') ? 'bg-sky-100' : log.action.includes('role') ? 'bg-purple-100' : 'bg-slate-100'}`}>
                        {log.action.includes('login') ? <LogIn className="w-3 h-3 text-sky-600" /> : log.action.includes('role') ? <Shield className="w-3 h-3 text-purple-600" /> : <Activity className="w-3 h-3 text-slate-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700">{log.details}</p>
                        <p className="text-[10px] text-slate-400">{log.timestamp}</p>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8"><History className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No activity recorded</p></div>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" icon={Edit3} className="flex-1">Edit</Button>
                  <Button variant="secondary" size="sm" icon={KeyRound} className="flex-1">Reset PW</Button>
                </div>
                {selectedUser.orgType === 'liberty' && (
                  <Button variant="ghost" size="sm" icon={User} className="w-full text-indigo-600">Impersonate User</Button>
                )}
                {selectedUser.status === 'active' ? (
                  <Button variant="ghost" size="sm" icon={UserMinus} className="w-full text-rose-600">Disable User</Button>
                ) : (
                  <Button variant="ghost" size="sm" icon={UserCheck} className="w-full text-emerald-600">Enable User</Button>
                )}
              </div>
            </Card>
          ) : (
            <Card className="text-center py-12">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Select a user to view details</p>
            </Card>
          )}
        </div>
      </div>
      
      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Create New User</h2>
                <p className="text-sm text-slate-500">Add a new user to the system</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-xs text-slate-500 mb-1">First Name *</label><input type="text" placeholder="John" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">Last Name *</label><input type="text" placeholder="Smith" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">Email *</label><input type="email" placeholder="john.smith@company.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">Phone</label><input type="tel" placeholder="+1 (555) 000-0000" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
                  <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Organization *</label><select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="">Select organization...</option><option value="liberty">Liberty (Internal)</option><option value="acme">ACME Corporation</option><option value="techstart">TechStart Industries</option><option value="maritime">Maritime Solutions</option></select></div>
                </div>
              </div>
              
              {/* User Type */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">User Type</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'liberty_admin', name: 'Liberty Admin', desc: 'Full platform access', icon: ShieldCheck },
                    { id: 'liberty_support', name: 'Liberty Support', desc: 'Support & monitoring', icon: HelpCircle },
                    { id: 'customer', name: 'Customer User', desc: 'Customer portal access', icon: Users },
                  ].map((type) => (
                    <label key={type.id} className="flex items-start gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30">
                      <input type="radio" name="userType" value={type.id} className="mt-1" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{type.name}</p>
                        <p className="text-xs text-slate-500">{type.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Role Assignment */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">Role Assignment</h3>
                <div className="grid grid-cols-2 gap-2">
                  {roles.slice(0, 8).map((role) => (
                    <label key={role.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg cursor-pointer hover:border-indigo-300">
                      <input type="checkbox" className="rounded" />
                      <div className="flex-1 min-w-0">
                        {getRoleBadge(role.id)}
                        <p className="text-[10px] text-slate-500 truncate mt-0.5">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Scope */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">Access Scope</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer"><input type="radio" name="scope" defaultChecked className="rounded-full" /><div><p className="text-sm font-medium text-slate-700">Applies to all services</p><p className="text-xs text-slate-500">User can access all service lines</p></div></label>
                  <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer"><input type="radio" name="scope" className="rounded-full" /><div><p className="text-sm font-medium text-slate-700">Limit to specific services</p><p className="text-xs text-slate-500">Restrict access to selected service lines</p></div></label>
                </div>
              </div>
              
              {/* Invite Options */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3">Invite Options</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2"><input type="checkbox" defaultChecked className="rounded" /><span className="text-sm text-slate-700">Send email invitation</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" className="rounded" /><span className="text-sm text-slate-700">Set temporary password</span></label>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6 pt-4 border-t border-slate-100">
              <Button variant="secondary" className="flex-1" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button variant="primary" icon={UserPlus} className="flex-1">Create User</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// ACCOUNT MANAGEMENT MODULE
// ============================================================================

const AccountManagementPage = ({ accounts, users, stats }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeSection, setActiveSection] = useState('hierarchy');
  const [quickFilter, setQuickFilter] = useState('all');
  const [expandedSites, setExpandedSites] = useState([]);
  
  const toggleSiteExpand = (siteId) => {
    setExpandedSites(prev => prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]);
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'suspended': return <Badge variant="critical">Suspended</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };
  
  const filteredAccounts = quickFilter === 'all' ? accounts : 
    quickFilter === 'issues' ? accounts.filter(a => a.hierarchy.totalAlerts > 0) :
    quickFilter === 'active' ? accounts.filter(a => a.status === 'active') :
    accounts;
  
  const accountUsers = selectedAccount ? users.filter(u => u.organization === selectedAccount.name) : [];

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Account Management</h1>
          <p className="text-slate-500">View account details, hierarchy, and users</p>
        </div>
        <Button variant="primary" icon={Plus}>Add Account</Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Building className="w-5 h-5 text-indigo-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{stats.totalAccounts}</p><p className="text-xs text-slate-500">Total Accounts</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{stats.activeAccounts}</p><p className="text-xs text-slate-500">Active</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center"><Server className="w-5 h-5 text-sky-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{stats.totalServices}</p><p className="text-xs text-slate-500">Services</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Router className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">{stats.totalTerminals}</p><p className="text-xs text-slate-500">Terminals</p></div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-xl font-bold text-slate-800">${(stats.monthlyRevenue / 1000).toFixed(0)}K</p><p className="text-xs text-slate-500">Monthly Revenue</p></div>
          </div>
        </Card>
      </div>
      
      {/* Quick Filters */}
      <div className="flex items-center gap-2">
        {[
          { id: 'all', label: 'All Accounts' },
          { id: 'active', label: 'Active Only' },
          { id: 'issues', label: 'Sites with Issues', icon: AlertTriangle },
        ].map((filter) => (
          <button key={filter.id} onClick={() => setQuickFilter(filter.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              quickFilter === filter.id ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200'
            }`}>
            {filter.icon && <filter.icon className="w-4 h-4" />}
            {filter.label}
          </button>
        ))}
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-800">Accounts ({filteredAccounts.length})</h3>
          {filteredAccounts.map((account) => (
            <Card key={account.id} hover className={`cursor-pointer ${selectedAccount?.id === account.id ? 'ring-2 ring-indigo-500' : ''}`} onClick={() => setSelectedAccount(account)}>
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${account.status === 'active' ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                  <Building className={`w-6 h-6 ${account.status === 'active' ? 'text-indigo-600' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-slate-800 truncate">{account.name}</h4>
                    {getStatusBadge(account.status)}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{account.segment} • {account.industry}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Server className="w-3 h-3" />{account.hierarchy.totalServices} services</span>
                    <span className="flex items-center gap-1"><Router className="w-3 h-3" />{account.hierarchy.totalTerminals} terminals</span>
                    {account.hierarchy.totalAlerts > 0 && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />{account.hierarchy.totalAlerts}</span>}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Account Detail */}
        <div className="lg:col-span-2">
          {selectedAccount ? (
            <div className="space-y-6">
              {/* Account Summary Card */}
              <Card>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${selectedAccount.status === 'active' ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                      <Building className={`w-8 h-8 ${selectedAccount.status === 'active' ? 'text-indigo-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-slate-800">{selectedAccount.name}</h2>
                        {getStatusBadge(selectedAccount.status)}
                      </div>
                      <p className="text-sm text-slate-500">{selectedAccount.accountId}</p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" icon={Edit3}>Edit</Button>
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500">Segment</p>
                    <p className="font-medium text-slate-800">{selectedAccount.segment}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500">Region</p>
                    <p className="font-medium text-slate-800">{selectedAccount.region}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500">Industry</p>
                    <p className="font-medium text-slate-800">{selectedAccount.industry}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500">Monthly Spend</p>
                    <p className="font-medium text-slate-800">${selectedAccount.stats.monthlySpend.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border border-slate-200 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Contract Period</p>
                    <p className="text-sm font-medium text-slate-800">{selectedAccount.contractStart} — {selectedAccount.contractEnd}</p>
                  </div>
                  <div className="p-3 border border-slate-200 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Plan Bundle</p>
                    <p className="text-sm font-medium text-slate-800">{selectedAccount.planBundle}</p>
                  </div>
                </div>
                
                {/* Data Pool */}
                <div className="mt-4 p-3 bg-indigo-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-indigo-800">Account Data Pool</p>
                    <p className="text-sm font-medium text-indigo-800">{((selectedAccount.dataPool.used / selectedAccount.dataPool.allocated) * 100).toFixed(0)}% used</p>
                  </div>
                  <div className="h-2 bg-indigo-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${(selectedAccount.dataPool.used / selectedAccount.dataPool.allocated) * 100}%` }} />
                  </div>
                  <p className="text-xs text-indigo-600 mt-1">{selectedAccount.dataPool.used.toLocaleString()} / {selectedAccount.dataPool.allocated.toLocaleString()} {selectedAccount.dataPool.unit}</p>
                </div>
              </Card>
              
              {/* Section Tabs */}
              <div className="flex items-center gap-2">
                {[
                  { id: 'hierarchy', label: 'Hierarchy & Structure', icon: GitBranch },
                  { id: 'users', label: 'Users', icon: Users },
                  { id: 'services', label: 'Services', icon: Server },
                ].map((section) => (
                  <button key={section.id} onClick={() => setActiveSection(section.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      activeSection === section.id ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-slate-600 hover:bg-slate-50'
                    }`}>
                    <section.icon className="w-4 h-4" />
                    {section.label}
                  </button>
                ))}
              </div>
              
              {/* Hierarchy Section */}
              {activeSection === 'hierarchy' && (
                <Card>
                  <h3 className="font-semibold text-slate-800 mb-4">Account Hierarchy</h3>
                  <div className="space-y-2">
                    {/* Account Root */}
                    <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200">
                      <div className="flex items-center gap-3">
                        <Building className="w-5 h-5 text-indigo-600" />
                        <div className="flex-1">
                          <p className="font-medium text-indigo-800">{selectedAccount.name}</p>
                          <p className="text-xs text-indigo-600">{selectedAccount.hierarchy.sites.length} sites • {selectedAccount.hierarchy.totalServices} services • {selectedAccount.hierarchy.totalTerminals} terminals</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Sites */}
                    <div className="ml-6 space-y-2">
                      {selectedAccount.hierarchy.sites.map((site) => (
                        <div key={site.id}>
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 cursor-pointer hover:border-indigo-200" onClick={() => toggleSiteExpand(site.id)}>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                {expandedSites.includes(site.id) ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                <MapPin className="w-5 h-5 text-slate-500" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-800">{site.name}</p>
                                <p className="text-xs text-slate-500">{site.location}</p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                <span>{site.services} services</span>
                                <span>{site.terminals} terminals</span>
                                {site.alerts > 0 && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />{site.alerts}</span>}
                              </div>
                            </div>
                          </div>
                          
                          {expandedSites.includes(site.id) && (
                            <div className="ml-8 mt-2 space-y-1">
                              {Array(site.terminals).fill(0).map((_, i) => (
                                <div key={i} className="p-2 bg-white rounded-lg border border-slate-100 flex items-center gap-2">
                                  <Router className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm text-slate-600">Terminal {i + 1}</span>
                                  <Badge variant="success" size="xs">Online</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Users Section */}
              {activeSection === 'users' && (
                <Card padding="p-0">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Users under this Account ({accountUsers.length})</h3>
                    <Button variant="secondary" size="sm" icon={Users}>Manage Users</Button>
                  </div>
                  {accountUsers.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Name</th>
                          <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Role(s)</th>
                          <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Status</th>
                          <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Last Login</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {accountUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-slate-50/50">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center"><span className="text-xs font-semibold text-teal-600">{user.name.split(' ').map(n => n[0]).join('')}</span></div>
                                <div><p className="text-sm font-medium text-slate-800">{user.name}</p><p className="text-xs text-slate-500">{user.email}</p></div>
                              </div>
                            </td>
                            <td className="px-5 py-3"><div className="flex flex-wrap gap-1">{user.roles.slice(0, 2).map(r => <Badge key={r} variant="default" size="xs">{r.replace(/_/g, ' ')}</Badge>)}</div></td>
                            <td className="px-5 py-3"><Badge variant={user.status === 'active' ? 'success' : 'warning'} size="xs">{user.status}</Badge></td>
                            <td className="px-5 py-3 text-sm text-slate-500">{user.lastLogin || 'Never'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-8 text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No users found for this account</p></div>
                  )}
                </Card>
              )}
              
              {/* Services Section */}
              {activeSection === 'services' && (
                <Card padding="p-0">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Services under this Account</h3>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className={quickFilter === 'issues' ? 'text-amber-600' : ''}><AlertTriangle className="w-4 h-4 mr-1" />Sites with issues</Button>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {selectedAccount.hierarchy.sites.map((site) => (
                      <div key={site.id} className="p-4 hover:bg-slate-50/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${site.alerts > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                              <Server className={`w-5 h-5 ${site.alerts > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{site.name}</p>
                              <p className="text-xs text-slate-500">{site.location}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm font-medium text-slate-800">{site.services} services</p>
                              <p className="text-xs text-slate-500">{site.terminals} terminals</p>
                            </div>
                            {site.alerts > 0 ? (
                              <Badge variant="warning">{site.alerts} alerts</Badge>
                            ) : (
                              <Badge variant="success">Healthy</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <Card className="text-center py-16">
              <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Select an account to view details</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const SiteUsersAdminPage = ({ microsites, siteUsers, siteRoles, activityLog }) => {
  const [selectedSiteFilter, setSelectedSiteFilter] = useState('all');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  
  const filteredUsers = siteUsers.filter(user => {
    if (selectedSiteFilter !== 'all' && user.siteId !== selectedSiteFilter) return false;
    if (selectedRoleFilter !== 'all' && user.role !== selectedRoleFilter) return false;
    if (searchQuery && !user.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !user.email.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  const getRoleBadge = (roleId) => {
    const role = siteRoles.find(r => r.id === roleId);
    if (!role) return <Badge>{roleId}</Badge>;
    const colorMap = {
      rose: 'critical',
      amber: 'warning',
      indigo: 'indigo',
      teal: 'teal',
      sky: 'info',
      slate: 'default',
    };
    return <Badge variant={colorMap[role.color] || 'default'}>{role.name}</Badge>;
  };
  
  // Group users by site for summary
  const usersBySite = microsites.map(site => ({
    site,
    users: siteUsers.filter(u => u.siteId === site.id),
    activeCount: siteUsers.filter(u => u.siteId === site.id && u.status === 'active').length,
  }));

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Administration</h1>
          <p className="text-slate-500">Manage users across all microsites and portals</p>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => setShowAddUserModal(true)}>
          Add New User
        </Button>
      </div>
      
      {/* Summary Cards by Site */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {usersBySite.slice(0, 4).map(({ site, users, activeCount }) => (
          <Card key={site.id} padding="p-4" hover className="cursor-pointer" onClick={() => setSelectedSiteFilter(site.id)}>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: site.branding.primaryColor + '20' }}
              >
                <Building2 className="w-5 h-5" style={{ color: site.branding.primaryColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{site.name}</p>
                <p className="text-xs text-slate-500">{users.length} users</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: `${(activeCount / Math.max(users.length, 1)) * 100}%` }}
                />
              </div>
              <span className="text-xs text-slate-500">{activeCount} active</span>
            </div>
          </Card>
        ))}
      </div>
      
      {/* Filters */}
      <Card padding="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <SearchInput 
              placeholder="Search users by name or email..." 
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
          <select
            value={selectedSiteFilter}
            onChange={(e) => setSelectedSiteFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          >
            <option value="all">All Sites</option>
            {microsites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <select
            value={selectedRoleFilter}
            onChange={(e) => setSelectedRoleFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm"
          >
            <option value="all">All Roles</option>
            {siteRoles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
          <Button variant="ghost" size="sm" icon={Download}>Export</Button>
        </div>
      </Card>
      
      {/* Users Table */}
      <Card padding="p-0">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">All Users</h3>
          <Badge variant="info">{filteredUsers.length} users</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Site</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Role</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Department</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Security</th>
                <th className="text-left text-xs font-medium text-slate-500 px-5 py-3">Last Login</th>
                <th className="text-right text-xs font-medium text-slate-500 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredUsers.map((user) => {
                const site = microsites.find(s => s.id === user.siteId);
                return (
                  <tr key={user.id} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-9 h-9 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: site?.branding.primaryColor + '20' || '#e2e8f0' }}
                        >
                          <span 
                            className="text-xs font-semibold"
                            style={{ color: site?.branding.primaryColor || '#64748b' }}
                          >
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: site?.branding.primaryColor || '#94a3b8' }}
                        />
                        <span className="text-sm text-slate-600">{site?.name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">{getRoleBadge(user.role)}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{user.department}</td>
                    <td className="px-5 py-3">
                      <Badge variant={user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'default'}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {user.mfaEnabled ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-xs">
                            <Lock className="w-3 h-3" /> MFA
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-500 text-xs">
                            <Unlock className="w-3 h-3" /> No MFA
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-500">
                      {user.lastLogin || <span className="text-slate-400">Never</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="View Details">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                        <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="Edit User">
                          <Edit3 className="w-4 h-4 text-slate-400" />
                        </button>
                        <button className="p-1.5 hover:bg-slate-100 rounded-lg" title="Reset Password">
                          <RefreshCw className="w-4 h-4 text-slate-400" />
                        </button>
                        {user.status === 'active' ? (
                          <button className="p-1.5 hover:bg-amber-50 rounded-lg" title="Deactivate">
                            <Power className="w-4 h-4 text-amber-500" />
                          </button>
                        ) : (
                          <button className="p-1.5 hover:bg-emerald-50 rounded-lg" title="Activate">
                            <Power className="w-4 h-4 text-emerald-500" />
                          </button>
                        )}
                        <button className="p-1.5 hover:bg-rose-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4 text-rose-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No users found matching your filters</p>
          </div>
        )}
      </Card>
      
      {/* Role Legend */}
      <Card>
        <h3 className="font-semibold text-slate-800 mb-4">Role Permissions Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {siteRoles.map((role) => (
            <div key={role.id} className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                {getRoleBadge(role.id)}
              </div>
              <p className="text-xs text-slate-600 mb-2">{role.description}</p>
              <div className="flex flex-wrap gap-1">
                {role.permissions.slice(0, 4).map((perm, i) => (
                  <span key={i} className="text-[10px] bg-white px-1.5 py-0.5 rounded text-slate-500 border border-slate-200">
                    {perm}
                  </span>
                ))}
                {role.permissions.length > 4 && (
                  <span className="text-[10px] text-slate-400">+{role.permissions.length - 4} more</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const SupportPage = () => (
  <div className="p-4 lg:p-6 space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-slate-800">Support</h1>
      <p className="text-slate-500">Get help with your Liberty services</p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card hover className="text-center">
        <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-4">
          <MessageSquare className="w-6 h-6 text-teal-600" />
        </div>
        <h3 className="font-semibold text-slate-800 mb-2">Open a Ticket</h3>
        <p className="text-sm text-slate-500 mb-4">Submit a support request to our team</p>
        <Button variant="primary" className="w-full">Create Ticket</Button>
      </Card>
      
      <Card hover className="text-center">
        <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
          <FileText className="w-6 h-6 text-indigo-600" />
        </div>
        <h3 className="font-semibold text-slate-800 mb-2">Documentation</h3>
        <p className="text-sm text-slate-500 mb-4">Browse guides and tutorials</p>
        <Button variant="secondary" className="w-full">View Docs</Button>
      </Card>
      
      <Card hover className="text-center">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="w-6 h-6 text-amber-600" />
        </div>
        <h3 className="font-semibold text-slate-800 mb-2">FAQs</h3>
        <p className="text-sm text-slate-500 mb-4">Find answers to common questions</p>
        <Button variant="secondary" className="w-full">Browse FAQs</Button>
      </Card>
    </div>
  </div>
);

// ============================================================================
// MAIN APP
// ============================================================================

export default function App() {
  const [currentPage, setCurrentPage] = useState('connectivity');
  const [role, setRole] = useState('LIBERTY_CUSTOMER');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Filter data based on role
  const visibleServices = role === 'LIBERTY_ADMIN' 
    ? mockServices 
    : mockServices.filter(s => s.ownerUserId === 'u1');
  
  const visibleAntennas = mockAntennas.filter(a => 
    visibleServices.some(s => s.antennas.includes(a.id))
  );
  
  const visibleAlerts = mockAlerts.filter(a =>
    visibleServices.some(s => s.id === a.serviceId)
  );
  
  const handleNavigate = (page) => {
    setCurrentPage(page);
    setSelectedServiceId(null);
  };
  
  const handleServiceSelect = (serviceId) => {
    setSelectedServiceId(serviceId);
    setCurrentPage('services');
  };
  
  const renderPage = () => {
    if (currentPage === 'services' && selectedServiceId) {
      const service = visibleServices.find(s => s.id === selectedServiceId);
      if (service) {
        return (
          <ServiceDetailPage 
            service={service}
            antennas={visibleAntennas}
            alerts={visibleAlerts}
            onBack={() => { setSelectedServiceId(null); setCurrentPage('connectivity'); }}
          />
        );
      }
    }
    
    switch (currentPage) {
      case 'connectivity':
        return (
          <ConnectivityOverviewPage 
            services={visibleServices}
            antennas={visibleAntennas}
            alerts={visibleAlerts}
            usageData={mockUsageSamples}
          />
        );
      case 'services':
        return (
          <ConnectivityOverviewPage 
            services={visibleServices}
            antennas={visibleAntennas}
            alerts={visibleAlerts}
            usageData={mockUsageSamples}
          />
        );
      case 'fleet-map':
        return <FleetMapPage services={visibleServices} antennas={visibleAntennas} />;
      case 'data-pools':
        return <DataPoolPage services={visibleServices} antennas={visibleAntennas} />;
      case 'billing':
        return (
          <BillingPage 
            services={visibleServices}
            accounts={mockAccounts}
            invoices={mockInvoices}
            transactions={mockTransactions}
            billingByService={mockBillingByService}
            billingByLocation={mockBillingByLocation}
            billingHistory={mockBillingHistory}
          />
        );
      case 'alerts':
        return <AlertsPage alerts={visibleAlerts} services={visibleServices} antennas={visibleAntennas} />;
      case 'microsites':
        return (
          <PlatformAdminHub 
            retailerSites={v4RetailerMicrosites}
            retailerUsers={v4RetailerUsers}
            platformActivity={v4PlatformActivity}
            platformStats={v4PlatformStats}
            roles={v4SiteRolesDetailed}
            templates={siteTemplates}
            brandingPresets={v4BrandingPresets}
            emailTemplates={v4EmailTemplates}
          />
        );
      case 'site-admin-portal':
        return (
          <ACMESiteAdminPortal 
            site={v4RetailerMicrosites.find(s => s.slug === 'acme')}
            users={v4RetailerUsers}
            roles={v4SiteRolesDetailed}
            activity={v4PlatformActivity}
            permissionCategories={sitePermissionCategories}
          />
        );
      case 'user-management':
        return (
          <UserManagementPage 
            users={allManagedUsers}
            roles={userManagementRoles}
            auditLog={userAuditLog}
          />
        );
      case 'account-management':
        return (
          <AccountManagementPage 
            accounts={managedAccounts}
            users={allManagedUsers}
            stats={accountStats}
          />
        );
      case 'site-users':
        return (
          <SiteUsersAdminPage 
            microsites={mockMicrosites}
            siteUsers={mockSiteUsers}
            siteRoles={mockSiteRoles}
            activityLog={mockSiteActivityLog}
          />
        );
      case 'support':
        return <SupportPage />;
      default:
        return (
          <ConnectivityOverviewPage 
            services={visibleServices}
            antennas={visibleAntennas}
            alerts={visibleAlerts}
            usageData={mockUsageSamples}
          />
        );
    }
  };
  
  return (
    <AppContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/20">
        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .animate-slide-up { animation: slide-up 0.3s ease-out; }
        `}</style>
        
        <div className="flex min-h-screen">
          <Sidebar 
            currentPage={currentPage} 
            onNavigate={handleNavigate}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            role={role}
          />
          
          <div className="flex-1 flex flex-col min-w-0">
            <Header 
              role={role}
              onRoleChange={setRole}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            
            <main className="flex-1 overflow-y-auto">
              {renderPage()}
            </main>
          </div>
        </div>
        
        {toast && (
          <Toast 
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AppContext.Provider>
  );
}